/**
 * Typed access to the onesphere API (WP-03.2). Every response is validated with zod so a server change fails
 * loudly in development instead of rendering garbage. Query keys follow ["<resource>", ...params].
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { frappe } from "./frappe";

const nullableStr = z.string().nullable().optional().transform((v) => v ?? "");
const nullableNum = z.number().nullable().optional().transform((v) => (v == null ? null : v));

export const WhoAmISchema = z.object({
  user: z.string(),
  full_name: z.string().nullable().optional(),
  onesphere_roles: z.array(z.string()),
  scope: z.record(z.string(), z.array(z.string())).default({}),
});
export type WhoAmI = z.infer<typeof WhoAmISchema>;

export const StatusSchema = z.enum(["Green", "Amber", "Red", "Grey"]);

export const SnapshotSchema = z.object({
  name: z.string(),
  kpi: z.string(),
  company: nullableStr,
  plant: nullableStr,
  department: nullableStr,
  period: z.string(),
  value: nullableNum,
  target: nullableNum,
  status: StatusSchema.nullable().optional().transform((v) => v ?? "Grey"),
  source: nullableStr,
  last_updated: z.string(),
  refresh_class: nullableStr,
});
export type Snapshot = z.infer<typeof SnapshotSchema>;

export const KpiBoardSchema = z.object({ tiles: z.array(SnapshotSchema), count: z.number(), message: z.string().optional() });
export type KpiBoard = z.infer<typeof KpiBoardSchema>;

export const ExceptionSchema = z.object({
  name: z.string(),
  kpi: z.string(),
  company: nullableStr,
  plant: nullableStr,
  department: nullableStr,
  period: z.string(),
  deviation: nullableNum,
  materiality_score: nullableNum,
  severity: z.enum(["Amber", "Red"]),
  status: z.string(),
  owner_user: nullableStr,
  insight: nullableStr,
});
export type KpiException = z.infer<typeof ExceptionSchema>;

export const ExceptionsSchema = z.object({ exceptions: z.array(ExceptionSchema), count: z.number() });

export const KpiDefinitionSchema = z.object({
  name: z.string(),
  kpi_name: z.string(),
  unit: nullableStr,
  direction: nullableStr,
  domain: nullableStr,
});
export type KpiDefinition = z.infer<typeof KpiDefinitionSchema>;

export const PlantSchema = z.object({
  name: z.string(),
  plant_name: z.string(),
  plant_type: nullableStr,
  company: nullableStr,
  city: nullableStr,
});
export type Plant = z.infer<typeof PlantSchema>;

export const ActionCreatedSchema = z.object({ name: z.string() }).passthrough();

export function useWhoAmI() {
  return useQuery({
    queryKey: ["whoami"],
    queryFn: async () => WhoAmISchema.parse(await frappe.call("onesphere.api.whoami")),
    staleTime: 5 * 60_000,
  });
}

/** My dashboard (onesphere.api.my.dashboard): identity, scope and permission-aware work blocks by role. */
export const MyDashboardSchema = z.object({
  user: z.string(),
  full_name: z.string().nullable().optional(),
  roles: z.array(z.string()),
  scope: z.record(z.string(), z.array(z.string())).default({}),
  blocks: z.array(z.object({
    key: z.string(), doctype: z.string(), count: z.number(),
    items: z.array(z.object({ name: z.string(), title: z.string().nullable().optional(), subtitle: z.string().nullable().optional(), date: z.string().nullable().optional() })),
  })),
  generated_at: z.string(),
});
export type MyDashboard = z.infer<typeof MyDashboardSchema>;
export function useMyDashboard() {
  return useQuery({ queryKey: ["my_dashboard"], queryFn: async () => MyDashboardSchema.parse(await frappe.call("onesphere.api.my.dashboard")) });
}

// ---------------------------------------------------------------- employee self-service (WP-15.2): leave, attendance, payslips

export type EssLeaveBalance = { leave_type: string; total_leaves: number; expired_leaves: number; leaves_taken: number; leaves_pending_approval: number; remaining_leaves: number; is_lwp: number };
export type EssLeaveApplication = { name: string; leave_type: string; from_date: string; to_date: string; total_leave_days: number; status: string; docstatus: number; description: string | null; posting_date: string };
export type EssPayslip = { name: string; start_date: string; end_date: string; gross_pay: number; net_pay: number; total_deduction: number; currency: string };
export type EssOverview = {
  employee: string; employee_name: string; designation: string | null; department: string | null; company: string;
  date_of_joining: string | null; image: string | null; leave_approver: string | null;
  leave_balances: EssLeaveBalance[]; leave_applications: EssLeaveApplication[];
  attendance: { since: string; total: number; by_status: Record<string, number>; present_rate: number | null };
  payslips: EssPayslip[];
};

export function useEssOverview(enabled = true) {
  return useQuery({ queryKey: ["ess_overview"], queryFn: () => frappe.call<EssOverview>("onesphere.api.my.ess_overview"), enabled });
}

export function useApplyLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leave_type: string; from_date: string; to_date: string; description?: string }) =>
      frappe.post<{ name: string; status: string; total_leave_days: number }>("onesphere.api.my.apply_leave", { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ess_overview"] }),
  });
}

export function useWithdrawLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) => frappe.post<{ name: string; withdrawn: boolean }>("onesphere.api.my.withdraw_leave", { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ess_overview"] }),
  });
}

/** AI module health (open endpoint) and data-lake status (Executive, Finance, administrators) for the Administration page. */
export const AiHealthSchema = z.object({ status: z.string(), version: z.string().optional().default(""), gateway: z.string().optional().default(""), primary_model: z.string().optional().default(""), cloud_enabled: z.boolean().optional().default(false) });
export function useAiHealth(enabled = true) {
  return useQuery({ queryKey: ["ai_health"], enabled, retry: false, queryFn: async () => { const res = await fetch("/ai/health"); if (!res.ok) throw new Error(`AI API ${res.status}`); return AiHealthSchema.parse(await res.json()); } });
}
export function useLakeStatus(enabled = true) {
  return useQuery({ queryKey: ["lake_status"], enabled, retry: false, queryFn: async () => { const res = await fetch("/ai/v1/lake/status", { headers: await aiHeaders() }); if (!res.ok) throw new Error(`lake status ${res.status}`); return (await res.json()) as Record<string, unknown>; } });
}

/** All snapshots the caller may see (server applies roles and User Permissions); shaped client-side in board.ts. */
export function useKpiBoard() {
  return useQuery({
    queryKey: ["kpi_board"],
    queryFn: async () => KpiBoardSchema.parse(await frappe.call("onesphere.api.kpi_board")),
  });
}

export function useExceptions(status = "Open", limit = 100) {
  return useQuery({
    queryKey: ["exceptions", status, limit],
    queryFn: async () => ExceptionsSchema.parse(await frappe.call("onesphere.api.exceptions", { status, limit })),
  });
}

export function useKpiDefinitions() {
  return useQuery({
    queryKey: ["kpi_definitions"],
    queryFn: async () =>
      z.array(KpiDefinitionSchema).parse(
        await frappe.list("KPI Definition", { fields: ["name", "kpi_name", "unit", "direction", "domain"], limit: 200 }),
      ),
    staleTime: 10 * 60_000,
  });
}

/** Sites; a caller without read access on Plant simply gets codes instead of names. */
export function usePlants() {
  return useQuery({
    queryKey: ["plants"],
    queryFn: async () => {
      try {
        return z.array(PlantSchema).parse(
          await frappe.list("Plant", { fields: ["name", "plant_name", "plant_type", "company", "city"], limit: 200 }),
        );
      } catch {
        return [] as Plant[];
      }
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export type CreateActionInput = {
  problem: string;
  owner_user: string;
  due_date: string;
  priority?: "Low" | "Medium" | "High" | "Critical";
  source_doctype?: string;
  source_name?: string;
};

// ---------------------------------------------------------------- projects (WP-09 preview)

export const ProjectSchema = z.object({
  name: z.string(),
  project_name: z.string(),
  company: nullableStr,
  status: z.string(),
  priority: nullableStr,
  percent_complete: nullableNum,
  expected_start_date: nullableStr,
  expected_end_date: nullableStr,
  actual_start_date: nullableStr,
  estimated_costing: nullableNum,
  total_costing_amount: nullableNum,
  notes: nullableStr,
  modified: z.string(),
  schedule_expected: nullableNum,
  schedule_gap: nullableNum,
  schedule_status: StatusSchema,
  days_left: z.number().nullable().optional(),
});
export type Project = z.infer<typeof ProjectSchema>;
export const ProjectsSchema = z.object({ projects: z.array(ProjectSchema), count: z.number(), as_of: z.string() });

/** Projects in the caller's scope with the deterministic schedule health computed server-side. */
export function useProjects(company?: string, status: string | null = "Open") {
  return useQuery({
    queryKey: ["projects", company ?? "", status ?? ""],
    queryFn: async () =>
      ProjectsSchema.parse(await frappe.call("onesphere.api.projects", { ...(company ? { company } : {}), status: status ?? "" })),
  });
}

// ---------------------------------------------------------------- companies (logos, tree)

export const CompanySchema = z.object({
  name: z.string(),
  abbr: nullableStr,
  company_logo: nullableStr,
  parent_company: nullableStr,
  is_group: z.number().nullable().optional(),
});
export type Company = z.infer<typeof CompanySchema>;

/** Companies the caller may see (User Permissions apply) with the logo attached to the Company record. */
export function useCompanies() {
  return useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      try {
        return z.array(CompanySchema).parse(
          await frappe.list("Company", { fields: ["name", "abbr", "company_logo", "parent_company", "is_group"], limit: 100, orderBy: "name asc" }),
        );
      } catch {
        return [] as Company[];
      }
    },
    staleTime: 10 * 60_000,
    retry: false,
  });
}

// ---------------------------------------------------------------- generated ERP screens (WP-03.3)

export type MetaBundle = { docs?: unknown[] };

export function useMeta(doctype: string) {
  return useQuery({
    queryKey: ["meta", doctype],
    queryFn: () => frappe.meta(doctype) as Promise<MetaBundle>,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

export type DocListArgs = { fields: string[]; filters?: unknown[][]; orFilters?: unknown[][]; orderBy?: string; page: number; pageSize: number };

/** One page of records; asks for pageSize + 1 rows so the UI knows whether a next page exists. */
export function useDocList(doctype: string, args: DocListArgs) {
  return useQuery({
    queryKey: ["list", doctype, args],
    queryFn: () =>
      frappe.list<Record<string, unknown>>(doctype, {
        fields: args.fields,
        filters: args.filters,
        orFilters: args.orFilters,
        orderBy: args.orderBy,
        limit: args.pageSize + 1,
        start: (args.page - 1) * args.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useDoc(doctype: string, name: string) {
  return useQuery({
    queryKey: ["doc", doctype, name],
    queryFn: () => frappe.get<Record<string, unknown>>(doctype, name),
  });
}

// ---------------------------------------------------------------- operations (WP-10 preview)

export const ReadingStatusSchema = z.enum(["Normal", "Warning", "Alarm"]);
export type ReadingStatus = z.infer<typeof ReadingStatusSchema>;

export const ParameterSchema = z.object({
  name: z.string(),
  line: z.string(),
  parameter_name: z.string(),
  unit: nullableStr,
  min_ok: nullableNum,
  max_ok: nullableNum,
  warning_band_pct: nullableNum,
  latest: z.object({ value: z.number(), status: ReadingStatusSchema, reading_time: z.string() }).nullable(),
  series: z.array(z.object({ t: z.string(), v: z.number(), s: ReadingStatusSchema })),
  abnormal_count: z.number(),
});
export type PlantParameter = z.infer<typeof ParameterSchema>;

export const PlantAlertSchema = z.object({
  name: z.string(),
  parameter: z.string(),
  line: nullableStr,
  parameter_name: nullableStr,
  severity: z.enum(["Warning", "Alarm"]),
  status: z.string(),
  last_value: nullableNum,
  last_reading_time: nullableStr,
  reading_count: z.number().nullable().optional(),
  message: nullableStr,
  acknowledged_by: nullableStr,
  action: nullableStr,
  source: nullableStr,
  insight: nullableStr,
});
export type PlantAlert = z.infer<typeof PlantAlertSchema>;

export const SpareSchema = z.object({
  item_code: z.string(),
  item_name: z.string(),
  lead_time_days: nullableNum,
  warehouse: z.string(),
  reorder_level: nullableNum,
  reorder_qty: nullableNum,
  actual_qty: nullableNum,
  status: z.enum(["Below", "OK"]),
});
export type Spare = z.infer<typeof SpareSchema>;

export const PlantOverviewSchema = z.object({
  plant: z.object({
    name: z.string(),
    plant_name: z.string(),
    plant_type: nullableStr,
    company: nullableStr,
    city: nullableStr,
    commune: nullableStr,
    province: nullableStr,
    floor_area_sqm: nullableNum,
    notes: nullableStr,
    gm_user: nullableStr,
  }),
  lines: z.array(z.object({ line: z.string(), status: ReadingStatusSchema, parameters: z.array(ParameterSchema) })),
  alerts: z.array(PlantAlertSchema),
  spares: z.array(SpareSchema),
  kpis: z.array(z.object({
    kpi: z.string(),
    period: z.string(),
    value: nullableNum,
    target: nullableNum,
    status: StatusSchema.nullable().optional().transform((v) => v ?? "Grey"),
    last_updated: z.string(),
    refresh_class: nullableStr,
  })),
  hours: z.number(),
  as_of: z.string(),
});
export type PlantOverview = z.infer<typeof PlantOverviewSchema>;

export function usePlantOverview(plant: string | undefined, hours = 48) {
  return useQuery({
    queryKey: ["plant_overview", plant, hours],
    queryFn: async () => PlantOverviewSchema.parse(await frappe.call("onesphere.api.plant_overview", { plant: plant!, hours })),
    enabled: !!plant,
    refetchInterval: 60_000,
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { alert: string; resolve?: boolean; note?: string }) =>
      frappe.post<{ name: string; status: string }>("onesphere.api.acknowledge_alert", { alert: input.alert, resolve: input.resolve ? 1 : 0, note: input.note ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plant_overview"] });
    },
  });
}

// ---------------------------------------------------------------- assistant (AI API through the same origin, /ai)

export const AssistantResponseSchema = z.object({
  interaction_id: z.string(),
  source: z.string(),
  model: nullableStr,
  text: z.string(),
  tools: z.array(z.object({ tool: z.string(), status: z.number(), latency_ms: z.number(), params: z.record(z.string(), z.unknown()).optional() })).default([]),
  proposals: z.array(z.object({
    problem: z.string(),
    due_in_days: z.number(),
    priority: z.string(),
    source_doctype: z.string().nullable().optional(),
    source_name: z.string().nullable().optional(),
  })).default([]),
  usage: z.record(z.string(), z.unknown()).optional(),
  latency_ms: z.number(),
});
export type AssistantResponse = z.infer<typeof AssistantResponseSchema>;
export type ChatTurn = { role: "user" | "assistant"; content: string };

/** A module-scoped assistant (OneFleet AD-04): the same call against another endpoint, with the module's headers;
 * the answer is OneSphere's envelope plus `evidence[]`, `tables[]`, `warnings[]` (OneFleet's superset). */
export const ScopedAssistantResponseSchema = AssistantResponseSchema.extend({
  tools: z.array(z.object({ tool: z.string().optional(), name: z.string().optional(), status: z.number().optional(), latency_ms: z.number().optional(), ms: z.number().optional(), params: z.record(z.string(), z.unknown()).optional() })).default([]),
  proposals: z.array(z.record(z.string(), z.unknown())).default([]),
  evidence: z.array(z.object({ kind: z.string(), ref: z.string(), deep_link: nullableStr, snippet: nullableStr })).default([]),
  tables: z.array(z.object({ title: z.string().optional(), columns: z.array(z.string()), rows: z.array(z.array(z.unknown())) })).default([]),
  warnings: z.array(z.string()).default([]),
  track: z.string().optional(),
  cloud_used: z.boolean().optional(),
});
export type ScopedAssistantResponse = z.infer<typeof ScopedAssistantResponseSchema>;

export async function askScopedAssistant(
  endpoint: string,
  messages: ChatTurn[],
  language: string,
  context: Record<string, unknown>,
  headers: Record<string, string> = {},
): Promise<ScopedAssistantResponse> {
  const { getAccessToken } = await import("./frappe");
  const token = getAccessToken();
  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...headers },
    body: JSON.stringify({ messages, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr", context }),
  });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return ScopedAssistantResponseSchema.parse(await res.json());
}

export async function askAssistant(messages: ChatTurn[], language: string, context: Record<string, unknown>): Promise<AssistantResponse> {
  const { getAccessToken } = await import("./frappe");
  const token = getAccessToken();
  const res = await fetch("/ai/v1/assistant/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ messages, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr", context }),
  });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return AssistantResponseSchema.parse(await res.json());
}

// ---------------------------------------------------------------- people (WP-11 preview)

export const PeopleOverviewSchema = z.object({
  as_of: z.string(),
  employees_total: z.number(),
  headcount: z.array(z.object({
    company: z.string(),
    total: z.number(),
    women: z.number(),
    departments: z.array(z.object({ department: z.string(), n: z.number() })),
  })),
  skills: z.array(z.object({ skill: z.string(), employees: z.number(), proficient: z.number(), avg: z.number() })),
  matrix: z.array(z.object({ company: nullableStr, skill: z.string(), avg: z.number(), n: z.number() })),
  certifications: z.object({
    counts: z.record(z.string(), z.number()),
    attention: z.array(z.object({
      name: z.string(),
      employee: z.string(),
      employee_name: nullableStr,
      company: nullableStr,
      designation: nullableStr,
      certification_type: z.string(),
      category: nullableStr,
      issued_on: nullableStr,
      expires_on: nullableStr,
      status: z.string(),
      days_to_expiry: z.number().nullable().optional(),
    })),
    total: z.number(),
    warn_days: z.number(),
  }),
  roadmap: z.array(z.object({
    name: z.string(),
    phase_no: z.number(),
    title: z.string(),
    company: nullableStr,
    planned_start: nullableStr,
    planned_end: nullableStr,
    status: nullableStr,
    deliverables: nullableStr,
    programs: z.array(z.object({ name: z.string(), status: nullableStr })),
    progress: z.number(),
    derived_status: z.string(),
  })),
  programs: z.array(z.object({ name: z.string(), training_program: z.string(), status: nullableStr, company: nullableStr, trainer_name: nullableStr })),
  events: z.array(z.object({
    name: z.string(),
    event_name: z.string(),
    training_program: nullableStr,
    event_status: z.string(),
    type: nullableStr,
    company: nullableStr,
    location: nullableStr,
    start_time: z.string(),
    end_time: nullableStr,
    invited: z.number(),
    present: z.number(),
  })),
});
export type PeopleOverview = z.infer<typeof PeopleOverviewSchema>;

export function usePeopleOverview(company?: string) {
  return useQuery({
    queryKey: ["people_overview", company ?? ""],
    queryFn: async () => PeopleOverviewSchema.parse(await frappe.call("onesphere.api.people_overview", company ? { company } : {})),
  });
}

// ---------------------------------------------------------------- HR lifecycle (Frappe HR through onesphere.api.hr)

const dateStr = z.string().nullable().optional().transform((v) => v ?? "");
const num0 = z.number().nullable().optional().transform((v) => v ?? 0);

// One schema and one hook per lifecycle screen (WP-23.1, OS-0614). The former HrOverview carried twelve
// sections in a single payload, so opening the approval queue also fetched the org chart, six months of
// payroll and four weeks of timesheet lines.

const LeaveRowSchema = z.object({ employee: z.string(), employee_name: nullableStr, leave_type: z.string(), from_date: dateStr, to_date: dateStr, company: nullableStr });
const PendingLeaveSchema = z.object({ name: z.string(), employee: z.string(), employee_name: nullableStr, leave_type: z.string(), from_date: dateStr, to_date: dateStr, total_leave_days: num0, leave_approver: nullableStr, description: nullableStr, posting_date: dateStr, company: nullableStr });
const PendingClaimSchema = z.object({ name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr, posting_date: dateStr, total_claimed_amount: num0, expense_approver: nullableStr, remark: nullableStr });
const TaskSchema = z.object({ subject: z.string(), status: z.string() });
const MeSchema = z.object({ name: z.string(), employee_name: nullableStr, company: nullableStr, designation: nullableStr }).nullable().optional();

export const LifecycleSummarySchema = z.object({
  as_of: z.string(),
  me: MeSchema,
  is_hr: z.boolean(),
  can_see_payroll: z.boolean(),
  employees: z.object({
    active: z.number(), left: z.number(), with_manager: z.number(), with_user: z.number(),
    by_employment_type: z.record(z.string(), z.number()), by_grade: z.record(z.string(), z.number()),
  }),
  onboarding: z.array(z.object({ name: z.string(), employee_name: nullableStr, employee: nullableStr, company: nullableStr, designation: nullableStr, date_of_joining: dateStr, boarding_status: nullableStr, project: nullableStr, tasks: z.array(TaskSchema), tasks_done: z.number() })),
  separations: z.array(z.object({ name: z.string(), employee_name: nullableStr, employee: nullableStr, company: nullableStr, designation: nullableStr, resignation_letter_date: dateStr, relieving_date: dateStr, boarding_status: nullableStr, project: nullableStr, tasks: z.array(TaskSchema), tasks_done: z.number() })),
  timesheets: z.object({ count_180d: num0, hours_180d: num0 }),
  expense_claims: z.object({ summary: z.array(z.object({ approval_status: nullableStr, n: z.number(), amount: num0 })) }),
  waiting: z.object({ leaves: z.number(), expense_claims: z.number(), mine_only: z.boolean() }),
  on_leave_today: z.number(),
  open_positions: z.number(),
});
export type LifecycleSummary = z.infer<typeof LifecycleSummarySchema>;

export const ApprovalsSchema = z.object({
  as_of: z.string(), is_hr: z.boolean(), mine_only: z.boolean(),
  leaves: z.array(PendingLeaveSchema),
  expense_claims: z.array(PendingClaimSchema),
});
export type ApprovalsQueue = z.infer<typeof ApprovalsSchema>;

export const AttendanceOverviewSchema = z.object({
  as_of: z.string(),
  leave: z.object({
    on_leave_today: z.array(LeaveRowSchema),
    upcoming: z.array(LeaveRowSchema),
    by_type_180d: z.array(z.object({ leave_type: z.string(), n: z.number(), days: num0 })),
  }),
  attendance: z.array(z.object({ company: z.string(), Present: num0, Absent: num0, "On Leave": num0, "Half Day": num0, total: z.number(), present_rate: z.number().nullable() })),
});
export type AttendanceOverview = z.infer<typeof AttendanceOverviewSchema>;

export const HiringSummarySchema = z.object({
  as_of: z.string(),
  openings: z.array(z.object({ name: z.string(), job_title: z.string(), company: nullableStr, designation: nullableStr, department: nullableStr, status: nullableStr, planned_vacancies: num0, posted_on: dateStr, closes_on: dateStr, lower_range: num0, upper_range: num0, applicants: z.number(), by_status: z.record(z.string(), z.number()) })),
  applicants_total: z.number(),
  applicants_by_status: z.record(z.string(), z.number()),
  interviews: z.array(z.object({ name: z.string(), job_applicant: z.string(), applicant_name: nullableStr, interview_type: nullableStr, status: z.string(), scheduled_on: dateStr, from_time: nullableStr, average_rating: z.number().nullable().optional() })),
  offers: z.array(z.object({ name: z.string(), applicant_name: z.string(), designation: nullableStr, company: nullableStr, status: nullableStr, offer_date: dateStr })),
  staffing_plans: z.array(z.object({ name: z.string(), company: nullableStr, from_date: dateStr, to_date: dateStr, total_estimated_budget: num0 })),
});
export type HiringSummary = z.infer<typeof HiringSummarySchema>;

export const OrgChartSchema = z.object({
  as_of: z.string(),
  tree: z.array(z.object({ id: z.string(), name: z.string(), company: nullableStr, designation: nullableStr, department: nullableStr, reports_to: nullableStr, status: z.string(), is_me: z.boolean() })),
});
export type OrgChart = z.infer<typeof OrgChartSchema>;

export const PayrollSummarySchema = z.object({
  as_of: z.string(),
  permitted: z.boolean(),
  runs: z.array(z.object({ company: z.string(), start_date: dateStr, period: z.string(), slips: z.number(), gross: num0, net: num0, deductions: num0, employer_contribution: num0, total_cost: num0 })),
  months: z.array(z.string()),
  latest: nullableStr,
  components: z.array(z.object({ name: z.string(), salary_component_abbr: nullableStr, type: nullableStr, formula: nullableStr, description: nullableStr })),
});
export type PayrollSummary = z.infer<typeof PayrollSummarySchema>;

const hrScreen = <T,>(key: string, method: string, schema: { parse: (v: unknown) => T }) =>
  (company?: string, enabled = true) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery({
      queryKey: [key, company ?? ""], enabled,
      queryFn: async () => schema.parse(await frappe.call(`onesphere.api.hr.${method}`, company ? { company } : {})),
    });

export const useLifecycleSummary = hrScreen("hr_lifecycle_summary", "lifecycle_summary", LifecycleSummarySchema);
export const useApprovalsQueue = hrScreen("hr_approvals", "approvals", ApprovalsSchema);
export const useAttendanceOverview = hrScreen("hr_attendance", "attendance_overview", AttendanceOverviewSchema);
export const useHiringSummary = hrScreen("hr_hiring", "hiring_summary", HiringSummarySchema);
export const useOrgChart = hrScreen("hr_org_chart", "org_chart", OrgChartSchema);
export const usePayrollSummary = hrScreen("hr_payroll", "payroll_summary", PayrollSummarySchema);

/** Every lifecycle screen that an approval can change. */
export const HR_SCREEN_KEYS = ["hr_lifecycle_summary", "hr_approvals", "hr_attendance", "hr_org_chart"] as const;

export function useHrAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { kind: "leave" | "expense"; name: string; action: "approve" | "reject"; note?: string }) =>
      frappe.post<{ name: string; status?: string; approval_status?: string }>(
        input.kind === "leave" ? "onesphere.api.hr.act_on_leave" : "onesphere.api.hr.act_on_expense_claim",
        { name: input.name, action: input.action, note: input.note ?? null },
      ),
    onSuccess: () => {
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
      void qc.invalidateQueries({ queryKey: ["people_overview"] });
    },
  });
}

// ---------------------------------------------------------------- employee ↔ user account link (Administration, People)

export function useUnlinkedUsers(search?: string) {
  return useQuery({
    queryKey: ["unlinked_users", search ?? ""],
    queryFn: () => frappe.call<{ name: string; full_name: string }[]>("onesphere.api.hr.unlinked_users", search ? { search } : {}),
  });
}

export function useLinkEmployeeUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; mode: "existing" | "create"; user?: string; email?: string }) =>
      frappe.post<{ employee: string; employee_name: string; user_id: string; created: boolean }>("onesphere.api.hr.link_employee_user", { ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
      void qc.invalidateQueries({ queryKey: ["unlinked_users"] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useUnlinkEmployeeUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (employee: string) => frappe.post<{ employee: string; user_id: null }>("onesphere.api.hr.unlink_employee_user", { employee }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
      void qc.invalidateQueries({ queryKey: ["unlinked_users"] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

// ---------------------------------------------------------------- manager assignment and approvers (Administration, People)

export type EmployeeCandidate = { name: string; employee_name: string; designation: string | null; company: string; user_id: string | null };

export function useSearchEmployees(query: string, exclude?: string, enabled = true) {
  return useQuery({
    queryKey: ["search_employees", query, exclude ?? ""],
    queryFn: () => frappe.call<EmployeeCandidate[]>("onesphere.api.hr.search_employees", { query, ...(exclude ? { exclude } : {}) }),
    enabled,
  });
}

export function useAssignManager() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; manager: string }) =>
      frappe.post<{ employee: string; reports_to: string; synced_approvers: string[]; leave_approver: string | null; expense_approver: string | null; timesheet_approver: string | null }>(
        "onesphere.api.hr.assign_manager", { ...input },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useSetApprover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; kind: "leave" | "expense" | "timesheet"; approver_employee: string }) =>
      frappe.post<{ employee: string; kind: string; approver_employee: string; approver_user: string }>("onesphere.api.hr.set_approver", { ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export type AccountCandidate = { name: string; account_name: string; account_type: string | null };

export function useSearchAccounts(employee: string, query: string, enabled = true) {
  return useQuery({
    queryKey: ["search_accounts", employee, query],
    queryFn: () => frappe.call<AccountCandidate[]>("onesphere.api.hr.search_accounts", { employee, query }),
    enabled,
  });
}

export function useSetEmployeeAdvanceAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; account: string | null }) =>
      frappe.post<{ employee: string; employee_advance_account: string | null }>("onesphere.api.hr.set_employee_advance_account", { ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export type CostCenterCandidate = { name: string; cost_center_name: string };

export function useSearchCostCenters(employee: string, query: string, enabled = true) {
  return useQuery({
    queryKey: ["search_cost_centers", employee, query],
    queryFn: () => frappe.call<CostCenterCandidate[]>("onesphere.api.hr.search_cost_centers", { employee, query }),
    enabled,
  });
}

export function useCreateEmployeeAdvanceAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; cost_center?: string }) =>
      frappe.post<{ employee: string; employee_advance_account: string; cost_center: string }>("onesphere.api.hr.create_employee_advance_account", { ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

// ---------------------------------------------------------------- skill catalogue and employee skill levels (Administration, People)

export type SkillCandidate = { name: string; skill_name: string; category: string | null; description: string | null; is_active?: number };

export function useSearchSkills(query: string, category: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["search_skills", query, category ?? ""],
    queryFn: () => frappe.call<SkillCandidate[]>("onesphere.api.hr.search_skills", { query, ...(category ? { category } : {}) }),
    enabled,
  });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { skill_name: string; category: string; description?: string }) =>
      frappe.post<SkillCandidate>("onesphere.api.hr.create_skill", { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["search_skills"] }),
  });
}

export type EmployeeSkillRow = { skill: string; proficiency: number; evaluation_date: string | null; category: string | null };

export function useEmployeeSkills(employee: string, enabled = true) {
  return useQuery({
    queryKey: ["employee_skills", employee],
    queryFn: () => frappe.call<EmployeeSkillRow[]>("onesphere.api.hr.employee_skills", { employee }),
    enabled,
  });
}

export function useSetEmployeeSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; skill: string; proficiency: number; evaluation_date?: string }) =>
      frappe.post<{ employee: string; skill: string; proficiency: number; evaluation_date: string }>("onesphere.api.hr.set_employee_skill", { ...input }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["employee_skills", vars.employee] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useRemoveEmployeeSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; skill: string }) =>
      frappe.post<{ employee: string; skill: string; removed: boolean }>("onesphere.api.hr.remove_employee_skill", { ...input }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["employee_skills", vars.employee] });
      for (const key of HR_SCREEN_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

// ---------------------------------------------------------------- workforce search and redeployment (WP-11.2, HR-05, HR-06)

export type WorkforceSpec = { skills: string[]; min_proficiency: number; category: string | null; designation: string | null; company: string | null };
export type WorkforceCatalogue = { skills: { skill_name: string; category: string | null }[]; designations: string[]; companies: string[] };

export function useWorkforceCatalogue(enabled = true) {
  return useQuery({ queryKey: ["workforce_catalogue"], queryFn: () => frappe.call<WorkforceCatalogue>("onesphere.api.workforce.catalogue"), enabled });
}

export type WorkforceDraft = { source: "rule" | "ai"; model: string | null; spec: WorkforceSpec | null; interaction_id: string | null };

export async function draftWorkforceRule(query: string): Promise<WorkforceDraft> {
  const d = await frappe.call<{ ok: boolean; spec: WorkforceSpec }>("onesphere.api.workforce.parse_query", { query });
  return { source: "rule", model: null, spec: d.spec ?? null, interaction_id: null };
}

export async function draftWorkforceAi(query: string, language: string): Promise<WorkforceDraft> {
  const res = await fetch("/ai/v1/workforce/draft", {
    method: "POST", headers: await aiHeaders(),
    body: JSON.stringify({ instruction: query, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }),
  });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  const d = await res.json();
  return { source: d.source, model: d.model ?? null, spec: d.spec ?? null, interaction_id: d.interaction_id ?? null };
}

export type WorkforceResult = {
  name: string; employee_name: string; designation: string | null; department: string | null; company: string;
  matched_skills: { skill: string; proficiency: number }[]; avg_proficiency: number;
};

export async function searchWorkforce(spec: Partial<WorkforceSpec> & { query?: string }): Promise<WorkforceResult[]> {
  return frappe.call<WorkforceResult[]>("onesphere.api.workforce.search", {
    skills: spec.skills ?? [], min_proficiency: spec.min_proficiency ?? 0,
    ...(spec.category ? { category: spec.category } : {}), ...(spec.designation ? { designation: spec.designation } : {}),
    ...(spec.company ? { company: spec.company } : {}), ...(spec.query ? { query: spec.query } : {}),
  });
}

export function useExpectedSkills(designation: string, enabled = true) {
  return useQuery({
    queryKey: ["expected_skills", designation],
    queryFn: () => frappe.call<string[]>("onesphere.api.workforce.get_expected_skills", { designation }),
    enabled: enabled && !!designation,
  });
}

export function useSetExpectedSkills() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { designation: string; skills: string[] }) =>
      frappe.post<{ designation: string; skills: string[] }>("onesphere.api.workforce.set_expected_skills", { designation: input.designation, skills: input.skills }),
    onSuccess: (_data, vars) => void qc.invalidateQueries({ queryKey: ["expected_skills", vars.designation] }),
  });
}

export type RedeploymentCandidate = {
  name: string; employee_name: string; designation: string | null; department: string | null; company: string;
  gap: { matched: string[]; missing: string[]; coverage_pct: number; avg_proficiency: number };
};

export function useRedeploymentCandidates(designation: string, company: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["redeployment_candidates", designation, company ?? ""],
    queryFn: () => frappe.call<RedeploymentCandidate[]>("onesphere.api.workforce.redeployment_candidates", { designation, ...(company ? { company } : {}) }),
    enabled: enabled && !!designation,
  });
}

export type PendingRedeployment = { name: string; employee: string; employee_name: string; transfer_date: string; company: string; new_company: string };

export function usePendingRedeployments(company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["pending_redeployments", company ?? ""],
    queryFn: () => frappe.call<PendingRedeployment[]>("onesphere.api.workforce.pending_redeployments", company ? { company } : {}),
    enabled,
  });
}

export function useProposeRedeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; designation: string; company?: string; note?: string }) =>
      frappe.post<{ employee_transfer: string; coverage_pct: number }>("onesphere.api.workforce.propose_redeployment", { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["pending_redeployments"] }),
  });
}

// ---------------------------------------------------------------- compliance repository (WP-11.3, HR-12…15)

export type ComplianceDoc = {
  name: string; employee: string; employee_name: string; company: string; designation: string | null;
  document_type: string; issued_on: string | null; expires_on: string | null; status: string; days_to_expiry: number | null;
  reference_no?: string | null; issuing_authority?: string | null;
};

export type ComplianceMissing = { employee: string; employee_name: string; company: string; designation: string | null; missing: string[] };

export type ComplianceOverview = {
  as_of: string; employees_total: number; counts: Record<string, number>; total: number; warn_days: number;
  attention: ComplianceDoc[]; missing: ComplianceMissing[]; missing_total: number; mandatory_document_types: string[];
};

export function useComplianceOverview(company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["compliance_overview", company ?? ""],
    queryFn: () => frappe.call<ComplianceOverview>("onesphere.api.compliance.overview", company ? { company } : {}),
    enabled,
  });
}

export function useComplianceDocuments(filters: { company?: string; employee?: string; document_type?: string; status?: string }, enabled = true) {
  return useQuery({
    queryKey: ["compliance_documents", filters],
    queryFn: () => frappe.call<ComplianceDoc[]>("onesphere.api.compliance.list_documents", { ...filters }),
    enabled,
  });
}

export type ComplianceAccessLogEntry = { viewed_by: string; viewed_on: string };

export function useComplianceAccessLog(name: string, enabled: boolean) {
  return useQuery({
    queryKey: ["compliance_access_log", name],
    queryFn: () => frappe.call<ComplianceAccessLogEntry[]>("onesphere.api.compliance.access_log", { name }),
    enabled: enabled && !!name,
  });
}

export async function viewComplianceDocument(name: string): Promise<ComplianceDoc & { document: string | null; notes: string | null }> {
  return frappe.call("onesphere.api.compliance.document", { name });
}

export const ComplianceAuditPackSchema = z.object({ file_name: z.string(), mime: z.string(), size: z.number(), content_base64: z.string(), employees: z.number(), as_of: z.string() });

export async function complianceAuditPack(company?: string, employee?: string) {
  return ComplianceAuditPackSchema.parse(await frappe.call("onesphere.api.compliance.audit_pack", { ...(company ? { company } : {}), ...(employee ? { employee } : {}) }));
}

// ---------------------------------------------------------------- L&D roadmap depth (WP-11.4, PPL-01…08)

export type LdGap = { matched: string[]; missing: string[]; coverage_pct: number; avg_proficiency: number };
export type LdNeedingTraining = { name: string; employee_name: string; company: string; department: string | null; designation: string | null; gap: LdGap };
export type LdTrainingNeeds = { employees_scored: number; designations_with_targets: number; needing_training: LdNeedingTraining[]; priorities: { skill: string; employees: number }[] };

export function useTrainingNeeds(company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["ld_training_needs", company ?? ""],
    queryFn: () => frappe.call<LdTrainingNeeds>("onesphere.api.ld.training_needs", company ? { company } : {}),
    enabled,
  });
}

export type LdProgramPending = { name: string; employee_name: string; company: string; department: string | null; designation: string | null; attended_events: number; invited_events: number; complete: boolean };
export type LdProgramStatus = { program: string; events: number; employees_total: number; completed: number; pending: LdProgramPending[]; completion_pct: number };

export function useLdProgramStatus(program: string, company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["ld_program_status", program, company ?? ""],
    queryFn: () => frappe.call<LdProgramStatus>("onesphere.api.ld.program_status", { program, ...(company ? { company } : {}) }),
    enabled,
  });
}

export type LdCriticalRoleRow = { designation: string; employees: number; avg_coverage_pct: number };
export type LdCriticalRoleMatrix = { critical_designations: string[]; rows: LdCriticalRoleRow[] };

export function useCriticalRoleMatrix(company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["ld_critical_role_matrix", company ?? ""],
    queryFn: () => frappe.call<LdCriticalRoleMatrix>("onesphere.api.ld.critical_role_matrix", company ? { company } : {}),
    enabled,
  });
}

export type LdRotation = { department: string | null; designation: string | null; from_date: string | null; to_date: string | null };
export type LdManagementTrainee = {
  employee: string; employee_name: string; company: string; department: string | null; designation: string | null;
  attended_events: number; invited_events: number; complete: boolean; events_total: number; rotations: LdRotation[];
};

export function useManagementTrainees(company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["ld_management_trainees", company ?? ""],
    queryFn: () => frappe.call<LdManagementTrainee[]>("onesphere.api.ld.management_trainees", company ? { company } : {}),
    enabled,
  });
}

export type LdSuccessionCandidate = { name: string; employee_name: string; designation: string | null; department: string | null; company: string; gap: LdGap; band: string };
export type LdSuccessionRole = { designation: string; holders: { name: string; employee_name: string; company: string; department: string | null }[]; candidates: LdSuccessionCandidate[] };

export function useSuccessionMap(company: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["ld_succession_map", company ?? ""],
    queryFn: () => frappe.call<LdSuccessionRole[]>("onesphere.api.ld.succession_map", company ? { company } : {}),
    enabled,
  });
}

// ---------------------------------------------------------------- executive weekly pack (AI API)

export const PackSchema = z.object({
  interaction_id: z.string(),
  source: z.string(),
  model: nullableStr,
  markdown: z.string(),
  sections: z.record(z.string(), z.string()),
  evidence: z.array(z.object({ kind: z.string(), ref: z.string() })).default([]),
  insight: nullableStr,
  latency_ms: z.number(),
});
export type WeeklyPack = z.infer<typeof PackSchema>;

async function aiHeaders(): Promise<Record<string, string>> {
  const { getAccessToken } = await import("./frappe");
  const token = getAccessToken();
  return { "Content-Type": "application/json", Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export async function requestWeeklyPack(language: string, context: Record<string, unknown>): Promise<WeeklyPack> {
  const res = await fetch("/ai/v1/pack/weekly", {
    method: "POST",
    headers: await aiHeaders(),
    body: JSON.stringify({ language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr", context }),
  });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return PackSchema.parse(await res.json());
}

export async function sendAssistantFeedback(interactionId: string, feedback: "useful" | "not_useful" | "correct" | "needs_review"): Promise<boolean> {
  const res = await fetch("/ai/v1/assistant/feedback", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ interaction_id: interactionId, feedback }) });
  if (!res.ok) return false;
  return Boolean((await res.json()).ok);
}

export function useCreateAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateActionInput) =>
      ActionCreatedSchema.parse(await frappe.post("onesphere.api.create_action", input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["exceptions"] });
    },
  });
}


// ---------------------------------------------------------------- sourcing and quote comparison (SRC-01 … SRC-12)

const num = z.number().nullable().optional().transform((v) => v ?? 0);
export const SourcingQuoteSchema = z.object({
  supplier_quotation: z.string(), supplier: z.string(), supplier_name: nullableStr, supplier_country: nullableStr, currency: nullableStr,
  fx_rate: num, quoted_total: num, goods_total_cdf: num, freight_cdf: num, insurance_cdf: num, duties_cdf: num, other_cdf: num, landed_total_cdf: num,
  lead_time_days: num, payment_days: num, advance_pct: num, validity_days: z.number().nullable().optional(), warranty_months: num, has_history: num, missing_terms: num,
  price_score: num, lead_score: num, payment_score: num, quality_score: num, performance_score: num, warranty_score: num, risk_score: num, value_score: num,
  rank_price: num, rank_value: num, is_best_price: num, is_best_value: num,
  flags: z.array(z.string()).default([]),
  evidence: z.object({
    lines: z.array(z.object({ item: z.string(), qty: z.number(), uom: nullableStr, rate: z.number(), amount: z.number(), lead_time_days: z.number().nullable().optional() })).default([]),
    charges: z.array(z.object({ description: nullableStr, amount: z.number() })).default([]),
    payment_terms: nullableStr, valid_till: nullableStr, terms: nullableStr, purchase_orders: z.number().optional(),
  }).partial().default({}),
});
export type SourcingQuote = z.infer<typeof SourcingQuoteSchema>;

export const SourcingPermissionsSchema = z.object({ can_edit_weights: z.boolean(), can_recommend_award: z.boolean(), can_create_draft_po: z.boolean() });
export const SourcingEventRowSchema = z.object({
  name: z.string(), title: z.string(), company: z.string(), project: nullableStr, project_name: nullableStr, rfq: nullableStr, package_type: nullableStr, status: z.string(),
  target_date: nullableStr, best_price_supplier: nullableStr, best_price_total_cdf: num, best_value_supplier: nullableStr, best_value_score: num, award_supplier: nullableStr,
  evaluated_on: nullableStr, narrative_source: nullableStr, quotes: z.number(),
});
export const SourcingEventsSchema = z.object({ events: z.array(SourcingEventRowSchema), count: z.number(), permissions: SourcingPermissionsSchema, default_weights: z.record(z.string(), z.number()) });
export const SourcingEventSchema = z.object({
  name: z.string(), title: z.string(), company: z.string(), project: nullableStr, project_name: nullableStr, rfq: nullableStr, package_type: nullableStr, status: z.string(),
  target_date: nullableStr, delivery_location: nullableStr, notes: nullableStr, reference_price_source: nullableStr, last_po_total_cdf: num, budget_total_cdf: num,
  best_price_supplier: nullableStr, best_price_total_cdf: num, best_value_supplier: nullableStr, best_value_score: num, evaluated_on: nullableStr, evaluated_by: nullableStr,
  narrative: nullableStr, narrative_source: nullableStr, narrative_model: nullableStr, interaction_id: nullableStr,
  award_supplier: nullableStr, award_note: nullableStr, award_recommended_by: nullableStr, award_recommended_on: nullableStr, draft_purchase_order: nullableStr,
  weights: z.record(z.string(), z.number()), quotes: z.array(SourcingQuoteSchema), permissions: SourcingPermissionsSchema, as_of: z.string(), narrative_stored_language: nullableStr,
  rfq_detail: z.object({ name: z.string(), subject: nullableStr, transaction_date: nullableStr, schedule_date: nullableStr,
    suppliers: z.array(z.object({ supplier: z.string(), quote_status: nullableStr })), items: z.array(z.object({ item_code: z.string(), item_name: nullableStr, qty: z.number(), uom: nullableStr, schedule_date: nullableStr })),
    message: nullableStr }).nullable().optional(),
});
export type SourcingEvent = z.infer<typeof SourcingEventSchema>;

export function useSourcingEvents(company?: string) {
  return useQuery({ queryKey: ["sourcing_events", company ?? ""], queryFn: async () => SourcingEventsSchema.parse(await frappe.call("onesphere.api.sourcing.events", company ? { company } : {})) });
}
export function useSourcingEvent(name: string | undefined, language?: string) {
  const lang = language ? (language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr") : undefined;
  return useQuery({ queryKey: ["sourcing_event", name ?? "", lang ?? ""], enabled: Boolean(name), queryFn: async () => SourcingEventSchema.parse(await frappe.call("onesphere.api.sourcing.event", { name, ...(lang ? { language: lang } : {}) })) });
}
export function useSourcingEvaluate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; weights?: Record<string, number>; language: string }) =>
      SourcingEventSchema.parse(await frappe.post("onesphere.api.sourcing.evaluate", { name: input.name, weights: input.weights ? JSON.stringify(input.weights) : null, language: input.language })),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["sourcing_event", v.name] }); void qc.invalidateQueries({ queryKey: ["sourcing_events"] }); },
  });
}
export function useRecommendAward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; supplier: string; note?: string }) =>
      SourcingEventSchema.parse(await frappe.post("onesphere.api.sourcing.recommend_award", { name: input.name, supplier: input.supplier, note: input.note ?? null })),
    // the procure-to-pay trail under the award card reads the same event: without it the next step stays "recommend award"
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["sourcing_event", v.name] }); void qc.invalidateQueries({ queryKey: ["sourcing_events"] }); void qc.invalidateQueries({ queryKey: ["procure_chain", v.name] }); },
  });
}
export function useCreateDraftPo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) => frappe.post<{ purchase_order: string; docstatus: number }>("onesphere.api.sourcing.create_draft_po", { name: input.name }),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["sourcing_event", v.name] }); void qc.invalidateQueries({ queryKey: ["procure_chain", v.name] }); },
  });
}
export const SourcingNarrativeSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, text: z.string(), saved: z.boolean(), latency_ms: z.number() });
export async function narrateSourcing(event: string, language: string) {
  const res = await fetch("/ai/v1/sourcing/narrate", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ event, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return SourcingNarrativeSchema.parse(await res.json());
}


// ---------------------------------------------------------------- procure-to-pay (WP-09: requisition → AI RFQ → onboarding → award authority → order → receipt → 3-way invoice → payment)

export const ProcurePermissionsSchema = z.object({ can_request: z.boolean(), can_procure: z.boolean(), can_finance: z.boolean(), can_approve_award_levels: z.array(z.string()).default([]),
  can_open_supplier_file: z.boolean().optional(), user: nullableStr });
export const SourcingRequestRowSchema = z.object({
  name: z.string(), title: z.string(), company: z.string(), project: nullableStr, plant: nullableStr, package_type: nullableStr, status: z.string(), needed_by: nullableStr,
  budget_total_cdf: num, requested_by: nullableStr, requested_on: nullableStr, material_request: nullableStr, rfq: nullableStr, sourcing_event: nullableStr, draft_source: nullableStr, approval_status: nullableStr,
  lines: z.number().optional(), invited: z.number().optional(),
});
export const OnboardingRowSchema = z.object({
  name: z.string(), supplier_name: z.string(), supplier: nullableStr, company: nullableStr, supplier_group: nullableStr, country: nullableStr, status: z.string(), checklist_score: num,
  recommendation: nullableStr, assessment_source: nullableStr, preferred: num, created_supplier: nullableStr, sourcing_event: nullableStr,
});
export const QualificationRowSchema = z.object({
  name: z.string(), supplier: z.string(), supplier_name: nullableStr, item_group: z.string(), status: z.string(), score: num, on_time_pct: z.number().nullable().optional(),
  quality_pct: z.number().nullable().optional(), purchase_orders: num, awards: num, last_award_on: nullableStr, basis: nullableStr, qualified_on: nullableStr,
});
export const MatchSchema = z.object({
  ok: z.boolean(), issues: z.array(z.string()).default([]), tolerance_pct: z.number().optional(),
  lines: z.array(z.object({ item_code: z.string(), ordered_qty: z.number(), ordered_rate: z.number(), received_qty: z.number(), invoiced_qty: z.number(), invoiced_rate: z.number(), status: z.string(), price_variance_pct: z.number() })).default([]),
});
export const ProcureChainSchema = z.object({
  event: z.object({ name: z.string(), title: z.string(), company: z.string(), status: z.string(), best_price_supplier: nullableStr, best_value_supplier: nullableStr, award_supplier: nullableStr,
    award_supplier_name: nullableStr, award_recommended_by: nullableStr, award_recommended_on: nullableStr, approved_by: nullableStr, approved_on: nullableStr, quotes: z.number(), landed_total_cdf: num }),
  request: z.object({ name: z.string(), title: nullableStr, status: nullableStr, material_request: nullableStr, requested_by: nullableStr, draft_source: nullableStr }).nullable().optional(),
  material_request: z.object({ name: z.string(), status: nullableStr }).nullable().optional(),
  rfq: z.object({ name: z.string(), transaction_date: nullableStr, status: nullableStr, suppliers: z.number().optional(), quotations: z.number().optional() }).nullable().optional(),
  authority: z.object({ level: z.string(), ceiling_cdf: z.number().nullable().optional(), roles: z.array(z.string()), over_budget: z.boolean().optional(), budget_cdf: z.number().nullable().optional() }).nullable().optional(),
  purchase_order: z.object({ name: z.string(), docstatus: z.number(), status: nullableStr, transaction_date: nullableStr, supplier: nullableStr, currency: nullableStr, grand_total: num, base_grand_total: num, per_received: num, per_billed: num,
    items: z.array(z.object({ item_code: z.string(), item_name: nullableStr, qty: num, received_qty: num, billed_amt: num, rate: num, amount: num, uom: nullableStr })).default([]) }).nullable().optional(),
  ordered_qty: num, received_qty: num,
  receipts: z.array(z.object({ name: z.string(), posting_date: nullableStr, status: nullableStr, grand_total: num, currency: nullableStr, qty: num })).default([]),
  invoices: z.array(z.object({ name: z.string(), posting_date: nullableStr, status: nullableStr, grand_total: num, currency: nullableStr, outstanding_amount: num, bill_no: nullableStr, remarks: nullableStr, match: MatchSchema })).default([]),
  payments: z.array(z.object({ name: z.string(), posting_date: nullableStr, mode_of_payment: nullableStr, paid_amount: num, paid_from_account_currency: nullableStr, paid_to_account_currency: nullableStr, reference_no: nullableStr, allocated_amount: num, invoice: nullableStr })).default([]),
  qualification: z.array(z.object({ item_group: z.string(), status: z.string(), awards: num, on_time_pct: z.number().nullable().optional() })).default([]),
  next_step: z.string(), can_take_next_step: z.boolean(), blocked: nullableStr, permissions: ProcurePermissionsSchema, as_of: z.string(),
});
export type ProcureChain = z.infer<typeof ProcureChainSchema>;
export const ProcureOverviewSchema = z.object({
  requests: z.array(SourcingRequestRowSchema), onboardings: z.array(OnboardingRowSchema), qualifications: z.array(QualificationRowSchema), chains: z.array(ProcureChainSchema),
  stats: z.object({ requests: z.number(), rfq_sent: z.number(), onboardings_open: z.number(), preferred: z.number(), qualified: z.number(), chains: z.number() }),
  permissions: ProcurePermissionsSchema, as_of: z.string(),
});
export const SourcingCandidateSchema = z.object({
  supplier: z.string(), supplier_name: nullableStr, rank: num, score: num, qualification_status: nullableStr, purchase_orders: num, on_time_pct: z.number().nullable().optional(),
  quality_pct: z.number().nullable().optional(), currency: nullableStr, country: nullableStr, reasons: z.array(z.string()).default([]), selected: num,
});
export type SourcingCandidate = z.infer<typeof SourcingCandidateSchema>;
export const SourcingRequestSchema = SourcingRequestRowSchema.extend({
  delivery_location: nullableStr, justification: nullableStr, project_name: nullableStr, item_groups: z.array(z.string()).default([]),
  items: z.array(z.object({ item_code: z.string(), item_name: nullableStr, qty: z.number(), uom: nullableStr, specification: nullableStr, estimated_rate_cdf: num })),
  candidates: z.array(SourcingCandidateSchema).default([]),
  rfq_subject: nullableStr, rfq_message: nullableStr, rfq_terms: nullableStr, rfq_questions_list: z.array(z.string()).default([]), draft_model: nullableStr, interaction_id: nullableStr, draft_on: nullableStr,
  material_request_status: nullableStr, rfq_status: nullableStr, quotations: z.number().optional(), company_currency: nullableStr,
  rfq_suppliers: z.array(z.object({ supplier: z.string(), quote_status: nullableStr, supplier_name: nullableStr, currency: nullableStr, quotation: nullableStr,
    quoted_total: z.number().nullable().optional(), quoted_currency: nullableStr })).default([]),
  rfq_sent_on: nullableStr, rfq_sent_by: nullableStr, draft_stored_language: nullableStr,
  approval_status: nullableStr, purchase_order: nullableStr, direct_reason: nullableStr, repeat_of: nullableStr, package_types: z.array(z.string()).default([]),
  approval: z.object({ status: z.string(), level: z.string(), estimate_cdf: num, budget_cdf: num, decided_by: nullableStr, decided_on: nullableStr, note: nullableStr,
    can_decide: z.boolean(), blocked: nullableStr }).optional(),
  rfq_log: z.array(z.object({ supplier: z.string(), supplier_name: nullableStr, action: z.string(), channel: nullableStr, sent_to: nullableStr, logged_on: nullableStr,
    logged_by: nullableStr, note: nullableStr })).default([]),
  direct_order: z.object({ name: z.string(), docstatus: z.number(), status: nullableStr, supplier: nullableStr, grand_total: num, currency: nullableStr }).nullable().optional(),
  event: z.object({ status: nullableStr, best_price_supplier: nullableStr, best_value_supplier: nullableStr, award_supplier: nullableStr, approved_on: nullableStr }).nullable().optional(),
  permissions: ProcurePermissionsSchema, as_of: z.string(),
});
export type SourcingRequest = z.infer<typeof SourcingRequestSchema>;
export const OnboardingSchema = OnboardingRowSchema.extend({
  supplier_type: nullableStr, currency: nullableStr, item_groups: nullableStr, item_group_list: z.array(z.string()).default([]), is_local: z.boolean().optional(), required_documents: z.array(z.string()).default([]),
  invited_on: nullableStr, documents_on: nullableStr, qualification_on: nullableStr, decided_on: nullableStr, decided_by: nullableStr, sourcing_request: nullableStr,
  legal_form: nullableStr, rccm: nullableStr, id_nat: nullableStr, tax_id: nullableStr, website: nullableStr, address: nullableStr, contact_name: nullableStr, contact_email: nullableStr, contact_phone: nullableStr,
  bank_name: nullableStr, bank_account_no: nullableStr, bank_swift: nullableStr, payment_terms: nullableStr, opened_by: nullableStr, decision_blocked: nullableStr,
  documents: z.array(z.object({ document_type: z.string(), required: num, status: z.string(), reference: nullableStr, issued_on: nullableStr, expires_on: nullableStr, note: nullableStr })).default([]),
  references: z.array(z.object({ customer: z.string(), contact: nullableStr, years: num, scope: nullableStr, verified: num, note: nullableStr })).default([]),
  checklist: z.object({ origin: z.string(), score: z.number(), recommendation: z.string(), flags: z.array(z.string()).default([]), blocking: z.array(z.string()).default([]), verified_references: z.number().optional(),
    items: z.array(z.object({ document_type: z.string(), required: z.boolean(), status: z.string(), points: z.number(), max: z.number() })).default([]) }).nullable().optional(),
  flags: z.array(z.string()).default([]), qualified_on: nullableStr, assessment: nullableStr, assessment_model: nullableStr, interaction_id: nullableStr, decision_note: nullableStr, assessment_stored_language: nullableStr,
  supplier_facts: z.object({ purchase_orders: num, on_time_pct: z.number().nullable().optional(), quality_pct: z.number().nullable().optional(), qualification_status: nullableStr }).nullable().optional(),
  qualifications: z.array(z.object({ name: z.string(), item_group: z.string(), status: z.string(), score: num, awards: num })).default([]),
  permissions: ProcurePermissionsSchema, as_of: z.string(),
});
export type Onboarding = z.infer<typeof OnboardingSchema>;

export const invalidateProcure = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ["procure_overview"] }); void qc.invalidateQueries({ queryKey: ["procure_chain"] });
  void qc.invalidateQueries({ queryKey: ["sourcing_request"] }); void qc.invalidateQueries({ queryKey: ["onboarding"] });
  void qc.invalidateQueries({ queryKey: ["sourcing_event"] }); void qc.invalidateQueries({ queryKey: ["sourcing_events"] });
  void qc.invalidateQueries({ queryKey: ["purchase_order"] }); void qc.invalidateQueries({ queryKey: ["purchase_orders"] }); void qc.invalidateQueries({ queryKey: ["material_requests"] });
};
export function useProcureOverview(company?: string) {
  return useQuery({ queryKey: ["procure_overview", company ?? ""], queryFn: async () => ProcureOverviewSchema.parse(await frappe.call("onesphere.api.procure.overview", company ? { company } : {})) });
}
const apiLang = (l?: string) => (l ? (l.startsWith("en") ? "en" : l === "ln" ? "ln" : "fr") : undefined);
export function useSourcingRequest(name: string | undefined, language?: string) {
  const lang = apiLang(language);
  return useQuery({ queryKey: ["sourcing_request", name ?? "", lang ?? ""], enabled: Boolean(name), queryFn: async () => SourcingRequestSchema.parse(await frappe.call("onesphere.api.procure.request", { name, ...(lang ? { language: lang } : {}) })) });
}
export function useProcureChain(event: string | undefined) {
  return useQuery({ queryKey: ["procure_chain", event ?? ""], enabled: Boolean(event), queryFn: async () => ProcureChainSchema.parse(await frappe.call("onesphere.api.procure.chain", { event })) });
}
export function useOnboarding(name: string | undefined, language?: string) {
  const lang = apiLang(language);
  return useQuery({ queryKey: ["onboarding", name ?? "", lang ?? ""], enabled: Boolean(name), queryFn: async () => OnboardingSchema.parse(await frappe.call("onesphere.api.procure.onboarding", { name, ...(lang ? { language: lang } : {}) })) });
}
function procureMutation<TIn, TOut>(method: string, parse: (raw: unknown) => TOut, body: (input: TIn) => Record<string, unknown>) {
  return function useProcureMutation() {
    const qc = useQueryClient();
    return useMutation({ mutationFn: async (input: TIn) => parse(await frappe.post(`onesphere.api.procure.${method}`, body(input))), onSuccess: () => invalidateProcure(qc) });
  };
}
export const useCreateSourcingRequest = procureMutation("create_request", (r) => SourcingRequestSchema.parse(r), (payload: Record<string, unknown>) => ({ payload: JSON.stringify(payload) }));
export const useRequestCandidates = procureMutation("candidates", (r) => SourcingRequestSchema.parse(r), (i: { name: string }) => ({ name: i.name }));
export const useDraftRfq = procureMutation("draft_rfq", (r) => SourcingRequestSchema.parse(r), (i: { name: string; language: string }) => ({ name: i.name, language: i.language }));
export const useSaveRfqDraft = procureMutation("save_rfq_draft", (r) => SourcingRequestSchema.parse(r),
  (i: { name: string; subject: string; message: string; terms: string; questions: string[]; candidates: string[]; source: string }) =>
    ({ name: i.name, subject: i.subject, message: i.message, terms: i.terms, questions: JSON.stringify(i.questions), candidates_selected: JSON.stringify(i.candidates), source: i.source }));
export const useSendRfq = procureMutation("send_rfq", (r) => SourcingRequestSchema.parse(r), (i: { name: string; suppliers: string[] }) => ({ name: i.name, suppliers: JSON.stringify(i.suppliers) }));
export const useRecordQuotation = procureMutation("record_quotation", (r) => SourcingRequestSchema.parse(r),
  (i: { name: string; supplier: string; rates: Record<string, number>; lead_time_days: number; valid_days: number; terms: string; currency: string }) =>
    ({ name: i.name, supplier: i.supplier, rates: JSON.stringify(i.rates), lead_time_days: i.lead_time_days, valid_days: i.valid_days, terms: i.terms, currency: i.currency }));
export const useOpenEvent = procureMutation("open_event", (r) => SourcingRequestSchema.parse(r), (i: { name: string; language: string }) => ({ name: i.name, language: i.language }));
export const useCreateOnboarding = procureMutation("create_onboarding", (r) => OnboardingSchema.parse(r), (payload: Record<string, unknown>) => ({ payload: JSON.stringify(payload) }));
export const useUpdateOnboarding = procureMutation("update_onboarding", (r) => OnboardingSchema.parse(r), (i: { name: string; payload: Record<string, unknown> }) => ({ name: i.name, payload: JSON.stringify(i.payload) }));
export const useQualifySupplier = procureMutation("qualify", (r) => OnboardingSchema.parse(r), (i: { name: string; language: string }) => ({ name: i.name, language: i.language }));
export const useDecideOnboarding = procureMutation("decide", (r) => OnboardingSchema.parse(r),
  (i: { name: string; decision: string; note?: string; preferred?: boolean; item_groups?: string[] }) => ({ name: i.name, decision: i.decision, note: i.note ?? null, preferred: i.preferred ? 1 : 0, item_groups: JSON.stringify(i.item_groups ?? []) }));
export const useOnboardFromEvent = procureMutation("onboard_from_event", (r) => OnboardingSchema.parse(r), (i: { event: string; supplier: string }) => ({ event: i.event, supplier: i.supplier }));
export function useChainAction() {
  const qc = useQueryClient();
  return useMutation({
    // `fields` carries the invoice's and the payment's own facts (bill_no, bill_date, amount, account, mode_of_payment, reference_no, …)
    mutationFn: async (input: { action: "approve_award" | "submit_po" | "receive" | "invoice" | "pay"; event: string; lines?: Record<string, number>; note?: string; override?: boolean; fields?: Record<string, string | number> }) =>
      ProcureChainSchema.parse(await frappe.post(`onesphere.api.procure.${input.action}`, {
        event: input.event, ...(input.lines ? { lines: JSON.stringify(input.lines) } : {}), ...(input.note !== undefined ? { note: input.note } : {}), ...(input.override ? { override: 1 } : {}),
        ...(input.fields ?? {}),
      })),
    onSuccess: () => invalidateProcure(qc),
  });
}
export const RfqDraftAiSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, draft: z.object({ subject: nullableStr, message: nullableStr, terms: nullableStr, questions: z.array(z.string()).default([]) }), saved: z.boolean(), errors: z.array(z.string()).default([]), latency_ms: z.number() });
export async function aiDraftRfq(request: string, language: string) {
  const res = await fetch("/ai/v1/procure/rfq_draft", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ request, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return RfqDraftAiSchema.parse(await res.json());
}
export const SupplierAssessmentAiSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, text: z.string(), saved: z.boolean(), latency_ms: z.number() });
export async function aiAssessSupplier(onboarding: string, language: string) {
  const res = await fetch("/ai/v1/procure/supplier_assessment", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ onboarding, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return SupplierAssessmentAiSchema.parse(await res.json());
}


// ---------------------------------------------------------------- order to cash (quotation → order with credit check → delivery → invoice → collection; incentives)

export const OrderPermissionsSchema = z.object({ can_sell: z.boolean(), can_deliver: z.boolean(), can_invoice: z.boolean(), can_collect: z.boolean(), can_compute: z.boolean(), can_approve: z.boolean(), can_pay: z.boolean(),
  can_release: z.boolean().default(false), can_reject: z.boolean().default(false) });
export const CreditSchema = z.object({ ok: z.boolean(), limit: num, outstanding: num, exposure: num, headroom: z.number().nullable().optional(), over_by: num, hold: z.boolean(), dunning_hold: z.boolean().optional(), payment_terms: nullableStr });
export const OrderChainSchema = z.object({
  order: z.object({ name: z.string(), docstatus: z.number(), status: nullableStr, customer: z.string(), customer_name: nullableStr, company: z.string(), transaction_date: nullableStr, delivery_date: nullableStr,
    grand_total: num, net_total: num, per_delivered: num, per_billed: num, po_no: nullableStr, stock_items: z.boolean().optional(),
    rejected: z.object({ by: nullableStr, on: nullableStr, reason: nullableStr }).nullable().optional(),
    items: z.array(z.object({ item_code: z.string(), item_name: nullableStr, qty: num, delivered_qty: num, rate: num, amount: num, uom: nullableStr })).default([]),
    sales_team: z.array(z.object({ sales_person: z.string(), sales_person_name: nullableStr, allocated_percentage: num })).default([]) }),
  quotation: z.object({ name: z.string(), transaction_date: nullableStr, status: nullableStr, grand_total: num }).nullable().optional(),
  credit: CreditSchema,
  deliveries: z.array(z.object({ name: z.string(), posting_date: nullableStr, status: nullableStr, grand_total: num, per_billed: num, qty: num })).default([]),
  invoices: z.array(z.object({ name: z.string(), posting_date: nullableStr, due_date: nullableStr, status: nullableStr, grand_total: num, base_net_total: num, outstanding_amount: num, overdue_days: num, level: num })).default([]),
  payments: z.array(z.object({ name: z.string(), posting_date: nullableStr, mode_of_payment: nullableStr, paid_amount: num, paid_from_account_currency: nullableStr, reference_no: nullableStr, allocated_amount: num, invoice: nullableStr })).default([]),
  ordered_qty: num, delivered_qty: num, billed: num, outstanding: num, next_step: z.string(), can_take_next_step: z.boolean(), permissions: OrderPermissionsSchema, as_of: z.string(),
});
export type OrderChain = z.infer<typeof OrderChainSchema>;
export const CollectionRowSchema = z.object({ name: z.string(), customer: z.string(), customer_name: nullableStr, company: z.string(), posting_date: nullableStr, due_date: nullableStr, outstanding: num, overdue_days: num, level: num, priority: num, sales_person: nullableStr });
export const CollectionCustomerSchema = z.object({ customer: z.string(), customer_name: nullableStr, company: z.string(), outstanding: num, overdue: num, level: num, invoices: num, oldest_days: num, sales_person: nullableStr, last_reminder: nullableStr });
export const CollectionsSchema = z.object({ rows: z.array(CollectionRowSchema), customers: z.array(CollectionCustomerSchema), total_overdue: num, as_of: z.string() });
export const IncentiveBoardRowSchema = z.object({
  sales_person: z.string(), sales_person_name: nullableStr, employee: nullableStr, employee_name: nullableStr, company: nullableStr, team: nullableStr, invoiced_net: num, collected_net: num, target: num,
  achievement_pct: z.number().nullable().optional(), multiplier: num, earned: num, payable: num, held: num, invoices: num, statement: nullableStr, status: nullableStr, additional_salary: nullableStr,
});
export const IncentivesSchema = z.object({
  period: z.string(), board: z.array(IncentiveBoardRowSchema), plan: z.record(z.string(), z.unknown()).optional(), plan_name: nullableStr, permissions: OrderPermissionsSchema,
  statements: z.array(z.object({ name: z.string(), sales_person: z.string(), sales_person_name: nullableStr, status: z.string(), payable: num, held: num, earned: num, achievement_pct: z.number().nullable().optional(), additional_salary: nullableStr, paid_on: nullableStr })).default([]),
});
export const OrdersOverviewSchema = z.object({
  period: z.string(), stats: z.object({ open_quotations: z.number(), to_deliver: z.number(), to_bill: z.number(), overdue_count: z.number(), overdue_amount: num, collected_period: num, incentives_payable: num }),
  chains: z.array(OrderChainSchema), collections: z.array(CollectionRowSchema), incentives: IncentivesSchema, permissions: OrderPermissionsSchema, as_of: z.string(),
});
export const IncentiveStatementSchema = z.object({
  name: z.string(), sales_person: z.string(), sales_person_name: nullableStr, employee: nullableStr, employee_name: nullableStr, company: nullableStr, period: z.string(), plan: nullableStr, status: z.string(),
  computed_on: nullableStr, approved_by: nullableStr, approved_on: nullableStr, paid_on: nullableStr, additional_salary: nullableStr, target: num, invoiced_net: num, collected_net: num,
  achievement_pct: z.number().nullable().optional(), multiplier: num, base_commission: num, earned: num, payable: num, held: num, deductions: num,
  lines: z.array(z.object({ sales_invoice: z.string(), customer: nullableStr, posting_date: nullableStr, due_date: nullableStr, net_amount: num, allocated_pct: num, share_amount: num, collected_amount: num, collected_on: nullableStr,
    days_late: num, margin_pct: z.number().nullable().optional(), base_commission: num, multiplier: num, payable: num, held: num, flags: z.array(z.string()).default([]) })).default([]),
  brief: nullableStr, brief_source: nullableStr, brief_model: nullableStr, interaction_id: nullableStr, decision_note: nullableStr, plan_rule: z.record(z.string(), z.unknown()).optional(), brief_stored_language: nullableStr,
  permissions: OrderPermissionsSchema, as_of: z.string(),
});
export type IncentiveStatement = z.infer<typeof IncentiveStatementSchema>;
export const DunningSchema = z.object({ customer: z.string(), company: z.string(), rows: z.array(CollectionRowSchema), note: z.object({ subject: z.string(), body: z.string(), level: num, total: num }),
  contact: z.object({ customer_name: nullableStr, customer_group: nullableStr, payment_terms: nullableStr }).partial().default({}), credit: CreditSchema });

const invalidateOrders = (qc: ReturnType<typeof useQueryClient>) => {
  for (const k of ["orders_overview", "order_chain", "collections", "incentives", "incentive_statement", "dunning", "order_queue", "receipt_options", "my_dashboard"]) void qc.invalidateQueries({ queryKey: [k] });
};
export function useOrdersOverview(company?: string, period?: string) {
  return useQuery({ queryKey: ["orders_overview", company ?? "", period ?? ""], queryFn: async () => OrdersOverviewSchema.parse(await frappe.call("onesphere.api.orders.overview", { ...(company ? { company } : {}), ...(period ? { period } : {}) })) });
}
export function useOrderChain(salesOrder: string | undefined) {
  return useQuery({ queryKey: ["order_chain", salesOrder ?? ""], enabled: Boolean(salesOrder), queryFn: async () => OrderChainSchema.parse(await frappe.call("onesphere.api.orders.chain", { sales_order: salesOrder })) });
}
export function useCollections(company?: string) {
  return useQuery({ queryKey: ["collections", company ?? ""], queryFn: async () => CollectionsSchema.parse(await frappe.call("onesphere.api.orders.collections_list", company ? { company } : {})) });
}
export function useIncentives(period?: string, company?: string) {
  return useQuery({ queryKey: ["incentives", period ?? "", company ?? ""], queryFn: async () => IncentivesSchema.parse(await frappe.call("onesphere.api.orders.incentives", { ...(period ? { period } : {}), ...(company ? { company } : {}) })) });
}
export function useIncentiveStatement(name: string | undefined, language?: string) {
  const lang = language ? (language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr") : undefined;
  return useQuery({ queryKey: ["incentive_statement", name ?? "", lang ?? ""], enabled: Boolean(name), queryFn: async () => IncentiveStatementSchema.parse(await frappe.call("onesphere.api.orders.statement", { name, ...(lang ? { language: lang } : {}) })) });
}
export function useDunning(customer: string | undefined, company: string | undefined, language: string) {
  return useQuery({ queryKey: ["dunning", customer ?? "", company ?? "", language], enabled: Boolean(customer && company),
    queryFn: async () => DunningSchema.parse(await frappe.call("onesphere.api.orders.dunning", { customer, company, language: language.startsWith("en") ? "en" : "fr" })) });
}
function ordersMutation<TIn, TOut>(method: string, parse: (raw: unknown) => TOut, body: (input: TIn) => Record<string, unknown>) {
  return function useOrdersMutation() {
    const qc = useQueryClient();
    return useMutation({ mutationFn: async (input: TIn) => parse(await frappe.post(`onesphere.api.orders.${method}`, body(input))), onSuccess: () => invalidateOrders(qc) });
  };
}
export const useCreateOrder = ordersMutation("create_order", (r) => OrderChainSchema.parse(r), (payload: Record<string, unknown>) => ({ payload: JSON.stringify(payload) }));
export function useOrderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: "deliver" | "invoice" | "collect" | "release_hold" | "reject_hold"; sales_order: string; lines?: Record<string, number>; amount?: number; mode_of_payment?: string; note?: string;
      reason?: string; paid_to?: string; reference_no?: string; reference_date?: string; posting_date?: string }) =>
      OrderChainSchema.parse(await frappe.post(`onesphere.api.orders.${input.action}`, {
        sales_order: input.sales_order, ...(input.lines ? { lines: JSON.stringify(input.lines) } : {}), ...(input.amount ? { amount: input.amount } : {}),
        ...(input.mode_of_payment ? { mode_of_payment: input.mode_of_payment } : {}), ...(input.note !== undefined ? { note: input.note } : {}),
        ...(input.reason !== undefined ? { reason: input.reason } : {}), ...(input.paid_to ? { paid_to: input.paid_to } : {}), ...(input.reference_no ? { reference_no: input.reference_no } : {}),
        ...(input.reference_date ? { reference_date: input.reference_date } : {}), ...(input.posting_date ? { posting_date: input.posting_date } : {}),
      })),
    onSuccess: () => invalidateOrders(qc),
  });
}
export const useRecordReminder = ordersMutation("record_reminder", (r) => r as { customer: string; invoices: number; recorded_on: string },
  (i: { customer: string; company: string; subject: string; body: string; level: number; source: string; model?: string | null; interaction_id?: string | null }) =>
    ({ customer: i.customer, company: i.company, subject: i.subject, body: i.body, level: i.level, source: i.source, model: i.model ?? null, interaction_id: i.interaction_id ?? null }));
export const useComputeStatement = ordersMutation("compute_statement", (r) => IncentiveStatementSchema.parse(r), (i: { sales_person: string; period: string; language: string }) => ({ sales_person: i.sales_person, period: i.period, language: i.language }));
export const useApproveStatement = ordersMutation("approve_statement", (r) => IncentiveStatementSchema.parse(r), (i: { name: string; note?: string }) => ({ name: i.name, note: i.note ?? null }));
export const usePayStatement = ordersMutation("pay_statement", (r) => IncentiveStatementSchema.parse(r), (i: { name: string }) => ({ name: i.name }));
export const IncentiveBriefAiSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, text: z.string(), saved: z.boolean(), latency_ms: z.number() });
export async function aiIncentiveBrief(statement: string, language: string) {
  const res = await fetch("/ai/v1/orders/incentive_brief", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ statement, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return IncentiveBriefAiSchema.parse(await res.json());
}
export const CollectionNoteAiSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, subject: z.string(), body: z.string(), level: z.number().nullable().optional(), total: z.number().nullable().optional(), invoices: z.number(), errors: z.array(z.string()).default([]), latency_ms: z.number() });
export async function aiCollectionNote(customer: string, company: string, language: string) {
  const res = await fetch("/ai/v1/orders/collection_note", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ customer, company, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return CollectionNoteAiSchema.parse(await res.json());
}

// order to cash, the work queue and the receipt (OS-0672, OS-0673): held / to deliver / to invoice / to collect / rejected
export const QUEUE_BUCKETS = ["held", "to_deliver", "to_invoice", "to_collect", "rejected"] as const;
export type QueueBucket = (typeof QUEUE_BUCKETS)[number];
export const OrderQueueSchema = z.object({
  bucket: z.string(), search: nullableStr, counts: z.record(z.string(), z.number()).default({}), permissions: OrderPermissionsSchema, as_of: z.string(),
  rows: z.array(z.object({ name: z.string(), customer: z.string(), customer_name: nullableStr, company: z.string(), transaction_date: nullableStr, delivery_date: nullableStr, grand_total: num,
    currency: nullableStr, per_delivered: num, per_billed: num, status: nullableStr, docstatus: z.number(), outstanding: num, po_no: nullableStr, hold_reason: nullableStr,
    os_rejection_reason: nullableStr })).default([]),
});
export type OrderQueue = z.infer<typeof OrderQueueSchema>;
export function useOrderQueue(bucket: QueueBucket, search: string) {
  return useQuery({ queryKey: ["order_queue", bucket, search], queryFn: async () => OrderQueueSchema.parse(await frappe.call("onesphere.api.orders.queue", { bucket, ...(search ? { search } : {}) })) });
}
export const ReceiptOptionsSchema = z.object({
  company: z.string(), invoice: nullableStr, outstanding: num, today: z.string(),
  accounts: z.array(z.object({ name: z.string(), account_name: nullableStr, account_type: z.string(), account_currency: nullableStr })).default([]),
  modes: z.array(z.object({ name: z.string(), type: nullableStr, default_account: nullableStr })).default([]),
});
export type ReceiptOptions = z.infer<typeof ReceiptOptionsSchema>;
export function useReceiptOptions(salesOrder: string | undefined, enabled: boolean) {
  return useQuery({ queryKey: ["receipt_options", salesOrder ?? ""], enabled: Boolean(salesOrder) && enabled, staleTime: 60_000,
    queryFn: async () => ReceiptOptionsSchema.parse(await frappe.call("onesphere.api.orders.receipt_options", { sales_order: salesOrder })) });
}
export const CreditProposalsSchema = z.object({
  may_set_credit: z.boolean(), as_of: z.string(),
  rows: z.array(z.object({ name: z.string(), customer_name: nullableStr, company: nullableStr, proposed: num, current: z.number().nullable().optional(), proposed_by: nullableStr, proposed_on: nullableStr })).default([]),
});
export function useCreditProposals() {
  return useQuery({ queryKey: ["credit_proposals"], queryFn: async () => CreditProposalsSchema.parse(await frappe.call("onesphere.api.master.credit_proposals", {})) });
}
export function useSetCreditLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (i: { customer: string; company: string; credit_limit: number; note?: string }) =>
      (await frappe.post("onesphere.api.master.set_credit_limit", { customer: i.customer, company: i.company, credit_limit: i.credit_limit, note: i.note ?? null })) as { customer: string; credit_limit: number },
    onSuccess: () => { for (const k of ["credit_proposals", "order_chain", "order_queue"]) void qc.invalidateQueries({ queryKey: [k] }); },
  });
}

// CRM in Order management (OS-0670): leads, opportunities, quotations and the conversions between them
export const CrmLeadSchema = z.object({ name: z.string(), lead_name: nullableStr, company_name: nullableStr, email_id: nullableStr, mobile_no: nullableStr, source: nullableStr, status: nullableStr, company: nullableStr, actions: z.array(z.string()).default([]) });
export const CrmOpportunitySchema = z.object({ name: z.string(), opportunity_from: nullableStr, party_name: nullableStr, customer_name: nullableStr, title: nullableStr, status: nullableStr, opportunity_amount: num,
  currency: nullableStr, expected_closing: nullableStr, transaction_date: nullableStr, company: nullableStr, actions: z.array(z.string()).default([]) });
export const CrmQuotationSchema = z.object({ name: z.string(), quotation_to: nullableStr, party_name: nullableStr, customer_name: nullableStr, status: nullableStr, docstatus: z.number(), grand_total: num, currency: nullableStr,
  transaction_date: nullableStr, valid_till: nullableStr, company: nullableStr, opportunity: nullableStr, sales_order: nullableStr, actions: z.array(z.string()).default([]) });
export type CrmLead = z.infer<typeof CrmLeadSchema>;
export type CrmOpportunity = z.infer<typeof CrmOpportunitySchema>;
export type CrmQuotation = z.infer<typeof CrmQuotationSchema>;
export const CrmOverviewSchema = z.object({
  leads: z.array(CrmLeadSchema).nullable(), opportunities: z.array(CrmOpportunitySchema).nullable(), quotations: z.array(CrmQuotationSchema).nullable(),
  stats: z.object({ open_leads: z.number(), open_opportunities: z.number(), pipeline_value: num, open_quotations: z.number() }), search: nullableStr, permissions: OrderPermissionsSchema, as_of: z.string(),
});
export function useCrmOverview(search: string) {
  return useQuery({ queryKey: ["crm_overview", search], queryFn: async () => CrmOverviewSchema.parse(await frappe.call("onesphere.api.crm.overview", search ? { search } : {})) });
}
export const CrmOptionsSchema = z.object({
  companies: z.array(z.string()).default([]), customers: z.array(z.object({ name: z.string(), customer_name: nullableStr, default_price_list: nullableStr })).default([]),
  leads: z.array(z.object({ name: z.string(), lead_name: nullableStr, company_name: nullableStr })).default([]),
  items: z.array(z.object({ name: z.string(), item_name: nullableStr, stock_uom: nullableStr })).default([]),
  price_lists: z.array(z.string()).default([]), default_price_list: nullableStr, prices: z.record(z.string(), z.record(z.string(), z.number())).default({}), sources: z.array(z.string()).default([]), today: z.string(),
});
export type CrmOptions = z.infer<typeof CrmOptionsSchema>;
export function useCrmOptions(enabled: boolean) {
  return useQuery({ queryKey: ["crm_options"], enabled, staleTime: 5 * 60_000, queryFn: async () => CrmOptionsSchema.parse(await frappe.call("onesphere.api.crm.form_options", {})) });
}
const CrmMadeSchema = z.object({ doctype: z.string().optional(), name: z.string(), label: nullableStr, status: nullableStr }).loose();
export function useCrmAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: "create_lead" | "create_opportunity" | "create_quotation" | "submit_quotation" | "declare_lost"; body: Record<string, unknown> }) =>
      CrmMadeSchema.parse(await frappe.post(`onesphere.api.crm.${input.method}`, input.body)),
    onSuccess: () => { for (const k of ["crm_overview", "crm_options", "orders_overview"]) void qc.invalidateQueries({ queryKey: [k] }); },
  });
}
export function useMakeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (i: { quotation: string; delivery_date?: string }) =>
      OrderChainSchema.parse(await frappe.post("onesphere.api.crm.make_order", { quotation: i.quotation, ...(i.delivery_date ? { delivery_date: i.delivery_date } : {}) })),
    onSuccess: () => { invalidateOrders(qc); void qc.invalidateQueries({ queryKey: ["crm_overview"] }); },
  });
}


// ---------------------------------------------------------------- recruitment assistance (HR-07 … HR-11, REC-01 … REC-15)

const anyRec = z.record(z.string(), z.unknown());
export const JobTemplateSchema = z.object({
  name: z.string(), template_name: z.string(), designation: z.string(), department: nullableStr, company: nullableStr, employment_type: nullableStr, grade: nullableStr,
  approved: num, language: nullableStr, openings: num,
  criteria: z.array(z.object({ label: z.string(), kind: z.string(), weight: num, min_years: z.number().nullable().optional(), keywords: nullableStr })).default([]),
});
export const JobTemplatesSchema = z.object({ templates: z.array(JobTemplateSchema), count: z.number() });
export const JobPreviewSchema = z.object({ template: z.string(), designation: z.string(), company: z.string(), description: z.string(),
  criteria: z.array(z.object({ label: z.string(), kind: z.string(), weight: num, min_years: z.number().nullable().optional() })) });
export const JobPostingSchema = z.object({ name: z.string(), channel: z.string(), status: z.string(), deep_link: nullableStr, external_reference: nullableStr, posted_on: nullableStr });
export const PostJobResultSchema = z.object({ job_requisition: z.string(), job_opening: z.string(), postings: z.array(JobPostingSchema), description: z.string() });
export const OpeningSchema = z.object({
  name: z.string(), job_title: z.string(), company: z.string(), designation: nullableStr, department: nullableStr, status: z.string(), planned_vacancies: num, vacancies: num,
  posted_on: nullableStr, closes_on: nullableStr, applicants: num, applicants_by_status: z.record(z.string(), z.number()).default({}), screened: num, shortlisted: num,
  best_score: z.number().nullable().optional(), template: nullableStr, postings: z.array(JobPostingSchema).default([]), time_to_fill_days: z.number().nullable().optional(),
});
export const RecruitmentOpeningsSchema = z.object({ openings: z.array(OpeningSchema), count: z.number(), is_hr: z.boolean() });
export const EvalCriterionSchema = z.object({ criterion: z.string(), kind: nullableStr, status: nullableStr, evidence: nullableStr, semantic_note: nullableStr, semantic_score: z.number().nullable().optional() });
export const EvaluationRowSchema = z.object({
  name: z.string(), job_applicant: z.string(), applicant_name: nullableStr, candidate_profile: nullableStr, job_template: nullableStr, score: num, rank: num,
  mandatory_met: num, mandatory_total: num, preferred_met: num, preferred_total: num, status: z.string(), pack_source: nullableStr, screened_on: nullableStr, rating: z.number().nullable().optional(),
  decided_by: nullableStr, criteria: z.array(EvalCriterionSchema).default([]), applicant_status: nullableStr, questions: num,
  profile: z.object({ years_experience: num, location: nullableStr, consent_status: nullableStr, source: nullableStr, current_title: nullableStr }).nullable().optional(),
});
export const ShortlistSchema = z.object({
  job_opening: z.string(), opening: z.object({ job_title: z.string(), company: z.string(), designation: nullableStr, status: z.string(), planned_vacancies: num }).nullable(),
  template: nullableStr, criteria: z.array(z.object({ label: z.string(), kind: z.string(), weight: num, min_years: z.number().nullable().optional(), keywords: nullableStr })).default([]),
  evaluations: z.array(EvaluationRowSchema), unscreened: z.array(z.object({ name: z.string(), applicant_name: z.string(), status: nullableStr, source: nullableStr })).default([]), is_hr: z.boolean(),
});
export const InterviewQuestionSchema = z.object({ question: z.string(), basis: nullableStr, source: nullableStr, ignored: num, edited: num });
export const CandidateCardSchema = z.object({
  name: z.string(), job_opening: z.string(), job_applicant: z.string(), applicant_name: nullableStr, score: num, rank: num, mandatory_met: num, mandatory_total: num, preferred_met: num, preferred_total: num,
  status: z.string(), interviewer_notes: nullableStr, rating: z.number().nullable().optional(), decided_by: nullableStr, decided_on: nullableStr, fairness_note: nullableStr,
  pack_source: nullableStr, pack_model: nullableStr, interaction_id: nullableStr, pack_generated_on: nullableStr,
  criteria: z.array(EvalCriterionSchema).default([]), interview_pack: z.array(InterviewQuestionSchema).default([]),
  profile: z.object({
    name: z.string(), full_name: nullableStr, current_title: nullableStr, email: nullableStr, phone: nullableStr, location: nullableStr, years_experience: num, languages: nullableStr, source: nullableStr,
    parse_source: nullableStr, summary: nullableStr, professional_indicator: nullableStr, indicator_evidence: nullableStr, education: nullableStr, certifications: nullableStr,
    profile_link: nullableStr, profile_link_summary: nullableStr, consent_status: nullableStr, consent_on: nullableStr, retention_until: nullableStr, dob_note: nullableStr,
    skills: z.array(z.object({ skill: z.string(), level: nullableStr })).default([]),
    experience: z.array(z.object({ title: nullableStr, employer: nullableStr, from_year: z.number().nullable().optional(), to_year: z.number().nullable().optional(), highlights: nullableStr })).default([]),
  }).nullable().optional(),
  applicant: z.object({ applicant_name: nullableStr, status: nullableStr, source: nullableStr, email_id: nullableStr, job_title: nullableStr }).nullable().optional(),
  template: z.object({ template_name: nullableStr, designation: nullableStr, interview_focus: nullableStr }).nullable().optional(),
  is_hr: z.boolean(),
}).passthrough();
export type CandidateCard = z.infer<typeof CandidateCardSchema>;
export const FairnessSchema = z.object({
  evaluations: num, by_status: z.record(z.string(), z.number()).default({}), pack_sources: z.record(z.string(), z.number()).default({}), questions: num, questions_ignored: num, questions_edited: num,
  criteria_in_use: z.array(z.object({ template: z.string(), criterion: z.string(), kind: nullableStr })).default([]), human_decisions: num, protected_attributes_used: z.array(z.string()).default([]), statement: z.string(),
});
void anyRec;

export function useJobTemplates() { return useQuery({ queryKey: ["job_templates"], queryFn: async () => JobTemplatesSchema.parse(await frappe.call("onesphere.api.recruitment.templates", {})) }); }
export function useRecruitmentOpenings(company?: string) { return useQuery({ queryKey: ["rec_openings", company ?? ""], queryFn: async () => RecruitmentOpeningsSchema.parse(await frappe.call("onesphere.api.recruitment.openings", company ? { company } : {})) }); }
export function useShortlist(opening: string | undefined) { return useQuery({ queryKey: ["rec_shortlist", opening ?? ""], enabled: Boolean(opening), queryFn: async () => ShortlistSchema.parse(await frappe.call("onesphere.api.recruitment.shortlist", { job_opening: opening })) }); }
export function useCandidate(evaluation: string | undefined, language?: string) { const lang = language ? (language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr") : undefined; return useQuery({ queryKey: ["rec_candidate", evaluation ?? "", lang ?? ""], enabled: Boolean(evaluation), queryFn: async () => CandidateCardSchema.parse(await frappe.call("onesphere.api.recruitment.candidate", { evaluation, ...(lang ? { language: lang } : {}) })) }); }
export function useFairness(opening?: string) { return useQuery({ queryKey: ["rec_fairness", opening ?? ""], queryFn: async () => FairnessSchema.parse(await frappe.call("onesphere.api.recruitment.fairness", opening ? { job_opening: opening } : {})) }); }
export async function previewJob(template: string, company: string, vacancies: number, closesOn: string | null, language: string) {
  return JobPreviewSchema.parse(await frappe.call("onesphere.api.recruitment.preview_job", { template, company, vacancies, closes_on: closesOn, language: language.startsWith("en") ? "en" : "fr" }));
}
export function useRecruitmentMutation<TInput extends Record<string, unknown>>(method: string, invalidate: string[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => frappe.post<unknown>(method, input),
    onSuccess: () => { for (const k of invalidate) void qc.invalidateQueries({ queryKey: [k] }); },
  });
}
export const CandidatePackAiSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, summary: nullableStr, questions: z.array(z.object({ question: z.string(), basis: nullableStr, source: nullableStr })), dropped_by_guardrail: z.number(), saved: z.boolean(), latency_ms: z.number() });
export async function requestCandidatePack(evaluation: string, language: string) {
  const res = await fetch("/ai/v1/recruitment/candidate_pack", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ evaluation, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return CandidatePackAiSchema.parse(await res.json());
}
export const SemanticMatchAiSchema = z.object({
  interaction_id: z.string(), source: z.string(), model: nullableStr,
  notes: z.array(z.object({ criterion: z.string(), note: z.string() })).default([]), saved: z.boolean(), latency_ms: z.number(),
});
export async function requestSemanticMatch(evaluation: string, language: string) {
  const res = await fetch("/ai/v1/recruitment/semantic_match", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ evaluation, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return SemanticMatchAiSchema.parse(await res.json());
}
export const JdDraftAiSchema = z.object({ interaction_id: z.string(), source: z.string(), model: nullableStr, description: z.string(), latency_ms: z.number() });
export async function requestJdDraft(template: string, company: string, vacancies: number, closesOn: string | null, language: string) {
  const res = await fetch("/ai/v1/recruitment/jd_draft", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ template, company, vacancies, closes_on: closesOn, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return JdDraftAiSchema.parse(await res.json());
}


// ---------------------------------------------------------------- finance on the real ledgers (FIN-02 … FIN-14)

const monthMap = z.record(z.string(), z.number());
const monthMapN = z.record(z.string(), z.number().nullable());
const nnum = z.number().nullable().optional().transform((v) => (v == null ? null : v));
export const PnlLineSchema = z.object({
  line: z.string(), label_fr: z.string(), kind: z.string(), values: monthMap, gross: monthMap.optional(), elimination: monthMap.optional(), in_inventory: monthMap.optional(),
  groups: z.array(z.string()).optional(),
});
export type PnlLine = z.infer<typeof PnlLineSchema>;
export const PnlGroupSchema = z.object({
  group_account: z.string(), group_name: z.string(), line: z.string(), values: monthMap,
  accounts: z.array(z.object({ account: z.string(), company: z.string(), values: monthMap })),
});
export const PnlCompanySchema = z.object({
  company: z.string(), revenue: monthMap, cost_of_sales: monthMap, opex: monthMap, depreciation: monthMap, gross_margin: monthMap, gross_margin_pct: monthMapN,
  operating_result: monthMap, unmapped: monthMap,
});
const AgeingRowSchema = z.object({ company: z.string(), total: num, overdue: num, count: num, not_due: num, d1_30: num, d31_60: num, d61_90: num, d90_plus: num, overdue_pct: num });
export type AgeingRow = z.infer<typeof AgeingRowSchema>;
export const AgeingSchema = z.object({
  as_of: z.string(), companies: z.array(AgeingRowSchema),
  total: z.object({ total: num, overdue: num, not_due: num, d1_30: num, d31_60: num, d61_90: num, d90_plus: num, overdue_pct: num }),
  top_parties: z.array(z.object({ company: z.string(), party: nullableStr, total: num, overdue: num, oldest_days: num, invoices: num })),
});
export type Ageing = z.infer<typeof AgeingSchema>;
export const VarianceFactSchema = z.object({
  kind: z.string(), label: z.string(), current: z.number(), reference: z.number(), delta: z.number(), delta_pct: nnum, unit: z.string(),
  company: z.string().optional(), group_account: z.string().optional(), line: z.string().optional(), reference_month: z.string().nullable().optional(), reference_months: z.number().optional(),
});
export type VarianceFact = z.infer<typeof VarianceFactSchema>;
export const DuplicateSchema = z.object({
  invoice: z.string(), duplicate_of: z.string(), company: z.string(), supplier: z.string(), amount: num, currency: nullableStr, bill_no: nullableStr, other_bill_no: nullableStr,
  posting_date: nullableStr, other_date: nullableStr, days_apart: nnum, purchase_order: nullableStr, reasons: z.array(z.string()), confidence: num, paid_twice: z.boolean(), still_payable: z.boolean(),
});
export const FinExceptionSchema = z.object({
  name: z.string(), title: z.string(), exception_type: z.string(), company: z.string(), account: nullableStr, party: nullableStr, amount: num, difference: num, currency: nullableStr,
  period: nullableStr, status: z.string(), owner_user: nullableStr, due_date: nullableStr, reason: nullableStr, resolution: nullableStr, decided_by: nullableStr, decided_on: nullableStr,
  reference_doctype: nullableStr, reference_name: nullableStr, related_doctype: nullableStr, related_name: nullableStr, evidence: nullableStr, source: nullableStr, modified: z.string(),
});
export type FinException = z.infer<typeof FinExceptionSchema>;
export const CloseTaskSchema = z.object({
  name: z.string(), task: z.string(), period: z.string(), company: nullableStr, area: nullableStr, status: z.string(), owner_user: nullableStr, due_date: nullableStr, depends_on: nullableStr,
  sequence: num, completed_on: nullableStr, signed_by: nullableStr, signed_on: nullableStr, note: nullableStr, evidence_doctype: nullableStr, evidence_name: nullableStr,
});
export type CloseTask = z.infer<typeof CloseTaskSchema>;
export const CommentarySchema = z.object({
  name: z.string(), title: z.string(), source: z.string(), status: z.string(), summary: nullableStr, model: nullableStr, ai_interaction_id: nullableStr, language: nullableStr, reviewed_by: nullableStr,
  reviewed_on: nullableStr, feedback: nullableStr, modified: z.string(), owner: z.string(),
  alternatives: z.array(z.object({ name: z.string(), source: z.string(), status: z.string(), model: nullableStr })).default([]),
});
export const FinanceOverviewSchema = z.object({
  period: z.string(), months: z.array(z.string()), as_of: z.string(), companies: z.array(z.string()),
  permissions: z.object({ can_map: z.boolean(), can_decide: z.boolean(), can_sign_off: z.boolean(), can_draft_commentary: z.boolean(), can_review_commentary: z.boolean(), can_read_ledger: z.boolean() }),
  pnl: z.object({
    months: z.array(z.string()), lines: z.array(PnlLineSchema), subtotals: z.array(PnlLineSchema), unmapped: PnlLineSchema, elimination: PnlLineSchema,
    groups: z.array(PnlGroupSchema), companies: z.array(PnlCompanySchema),
    unmapped_accounts: z.array(z.object({ account: z.string(), company: z.string(), account_name: nullableStr, balance: num, proposed: nullableStr })),
  }),
  eliminations: z.object({ pairs: num, revenue: monthMap, in_inventory: monthMap }),
  variance: z.object({
    month: z.string(), previous_month: nullableStr, threshold_pct: num, materiality: num, gross_margin_pct: nnum, facts: z.array(VarianceFactSchema), significant: z.array(VarianceFactSchema),
    company_movers: z.array(VarianceFactSchema), expense_drivers: z.array(VarianceFactSchema), unmapped_balance: num, elimination: num,
  }),
  rule_commentary: z.string(), rule_commentary_en: z.string(), commentary: CommentarySchema.nullable(),
  ageing: z.object({ receivables: AgeingSchema, payables: AgeingSchema }),
  working_capital: z.object({ receivables: num, payables: num, inventory: num, cash_and_bank: num, net_working_capital: num, dso_days: nnum, dpo_days: nnum, dio_days: nnum, cash_conversion_days: nnum }),
  cash: z.array(z.object({ company: z.string(), cash_and_bank: num, inventory: num })),
  duplicates: z.array(DuplicateSchema), duplicates_count: num, duplicates_payable: num,
  recurring_vendors: z.array(z.object({ company: z.string(), supplier: z.string(), months: num, invoices: num, typical_amount: num, annualised: num, cv_pct: num, last_invoice: nullableStr, currency: nullableStr, expense_account: nullableStr })),
  exceptions: z.array(FinExceptionSchema), exception_counts: z.record(z.string(), z.number()), exceptions_open_amount: num,
  coa: z.object({
    unmapped: z.array(z.object({ account: z.string(), account_name: nullableStr, company: z.string(), account_type: nullableStr, root_type: nullableStr, balance: num, proposed: nullableStr, map_name: nullableStr, status: nullableStr })),
    unmapped_with_balance: num, unmapped_balance: num,
    coverage: z.object({ accounts: num, with_balance: num, mapped_with_balance: num, by_rule: num, rate_pct: num, automation_pct: num }).nullable(),
    group_accounts: z.array(z.object({ name: z.string(), account_name: z.string(), account_name_fr: nullableStr, root_type: z.string(), statement: z.string(), line: z.string(), sort_order: num })),
    rules: nnum,
  }),
  close: z.object({ tasks: z.array(CloseTaskSchema), progress: z.object({ total: num, done: num, signed_off: num, overdue: num, blocked: num, pct: num, overdue_tasks: z.array(z.string()), blocked_tasks: z.array(z.string()) }) }),
  freshness: z.object({ source: z.string(), last_updated: nullableStr, class: z.string() }),
});
export type FinanceOverview = z.infer<typeof FinanceOverviewSchema>;
export const LedgerSchema = z.object({
  account: z.string(), month: z.string(), count: num, debit: num, credit: num,
  rows: z.array(z.object({ name: z.string(), posting_date: z.string(), voucher_type: z.string(), voucher_no: z.string(), party_type: nullableStr, party: nullableStr, debit: num, credit: num, remarks: nullableStr, against: nullableStr })),
});

export function useFinanceOverview(period?: string) {
  return useQuery({ queryKey: ["finance_overview", period ?? ""], staleTime: 60_000, queryFn: async () => FinanceOverviewSchema.parse(await frappe.call("onesphere.api.finance.overview", period ? { period } : {})) });
}
export const BalanceSheetSchema = z.object({
  as_of: z.string(), companies: z.array(z.string()),
  lines: z.array(z.object({ line: z.string(), line_fr: z.string(), side: z.enum(["asset", "liability", "equity"]), amount: num })),
  assets: num, liabilities_equity: num, difference: num,
  result_not_closed: nullableNum, unexplained: nullableNum, balanced: z.boolean(),
  unmapped: num,
  unmapped_accounts: z.array(z.object({ account: z.string(), company: nullableStr, account_name: nullableStr, balance: num, proposed: nullableStr })),
  groups: z.array(z.object({ group_account: z.string(), group_name: z.string(), line: z.string(), balance: num,
    accounts: z.array(z.object({ account: z.string(), company: nullableStr, account_name: nullableStr, balance: num })) })),
  by_company: z.record(z.string(), z.record(z.string(), num)),
});
export type BalanceSheet = z.infer<typeof BalanceSheetSchema>;
export function useBalanceSheet(period?: string) {
  return useQuery({ queryKey: ["finance_balance_sheet", period ?? ""], staleTime: 60_000,
    queryFn: async () => BalanceSheetSchema.parse(await frappe.call("onesphere.api.finance.balance_sheet", period ? { period } : {})) });
}
export function useFinanceLedger(account: string | null, month: string) {
  return useQuery({ queryKey: ["finance_ledger", account ?? "", month], enabled: Boolean(account), queryFn: async () => LedgerSchema.parse(await frappe.call("onesphere.api.finance.ledger", { account, month })) });
}
/** One mutation hook for every Finance decision (mapping, exception, close task, commentary review): re-reads the board afterwards. */
export function useFinanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: string; params: Record<string, unknown> }) => frappe.post(`onesphere.api.finance.${input.method}`, input.params),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["finance_overview"] }); },
  });
}
export const FinanceCommentaryResponseSchema = z.object({ interaction_id: z.string(), period: nullableStr, source: z.enum(["ai", "rule"]), model: nullableStr, text: z.string(), insight: nullableStr, latency_ms: z.number() });
export type FinanceCommentaryResponse = z.infer<typeof FinanceCommentaryResponseSchema>;
export async function requestVarianceCommentary(period: string, language: string) {
  const res = await fetch("/ai/v1/finance/commentary", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ period, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return FinanceCommentaryResponseSchema.parse(await res.json());
}


// ---------------------------------------------------------------- factory management (OPS-07 … OPS-15, requirement 7.4 … 7.7)

export const ProductionLineSchema = z.object({
  name: z.string(), line_code: z.string(), line_name: z.string(), plant: z.string(), company: nullableStr, workstation: nullableStr, asset: nullableStr, status: nullableStr,
  hours_per_day: num, working_days_per_week: num, shifts: nullableStr, skus: z.array(z.object({ item: z.string(), item_name: nullableStr, rate_per_hour: num, uom: nullableStr })),
});
export type ProductionLine = z.infer<typeof ProductionLineSchema>;
export const CapacityRowSchema = z.object({
  line_code: z.string(), line_name: z.string(), workstation: nullableStr, line_status: nullableStr, horizon_days: num, working_days: num, gross_hours: num, downtime_hours: num, available_hours: num,
  planned_hours: num, free_hours: num, utilisation_pct: nnum, status: z.string(),
  orders: z.array(z.object({ name: z.string(), item: z.string(), remaining: num, hours: num, planned_start: nullableStr })), unrated_orders: z.array(z.string()).default([]),
});
export type CapacityRow = z.infer<typeof CapacityRowSchema>;
export const FactoryAlertSchema = z.object({
  name: z.string(), parameter: nullableStr, line: nullableStr, parameter_name: nullableStr, severity: z.string(), status: z.string(), alert_kind: nullableStr, recommendation: nullableStr,
  projected_days: nnum, last_value: nnum, last_reading_time: nullableStr, reading_count: nnum, message: nullableStr, acknowledged_by: nullableStr, action: nullableStr, source: nullableStr,
});
export type FactoryAlert = z.infer<typeof FactoryAlertSchema>;
export const SpareRiskSchema = z.object({
  item_code: z.string(), item_name: nullableStr, lead_time_days: nnum, warehouse: nullableStr, reorder_level: nnum, reorder_qty: nnum, actual_qty: nnum, on_hand: num, required_qty: num,
  needed_by: nullableStr, days_left: nnum, requirements: z.array(z.object({ item_code: z.string(), qty: num, needed_by: nullableStr, reason: nullableStr, alert: nullableStr })),
  inbound_qty: num, inbound: z.array(z.object({ item_code: z.string(), qty: num, expected: nullableStr, reference: nullableStr, supplier: nullableStr })), projected: num, risk: z.string(),
  recommendation: nullableStr, suggested_qty: num, material_requests: z.array(z.object({ name: z.string(), qty: num, draft: z.boolean() })).default([]),
});
export type SpareRisk = z.infer<typeof SpareRiskSchema>;
export const OrderReadinessSchema = z.object({
  sales_order: z.string(), customer: nullableStr, item: z.string(), item_name: nullableStr, open_qty: num, delivery_date: nullableStr, from_stock: num, from_production: num, uncovered: num,
  coverage_pct: num, production_due: nullableStr, work_orders: z.array(z.string()), status: z.string(), days_late: num, projected_delay_days: nnum,
});
export type OrderReadiness = z.infer<typeof OrderReadinessSchema>;
export const DowntimeEntrySchema = z.object({ name: z.string(), workstation: z.string(), from_time: z.string(), to_time: nullableStr, downtime: num, stop_reason: nullableStr, operator: nullableStr, remarks: nullableStr });
export const DowntimeSummarySchema = z.object({
  start: z.string(), end: z.string(), count: num, lost_hours: num,
  by_reason: z.array(z.object({ reason: z.string(), reason_fr: z.string(), count: num, hours: num, share_pct: num })),
  by_workstation: z.array(z.object({ workstation: z.string(), count: num, hours: num })),
  longest: z.object({ name: nullableStr, workstation: nullableStr, hours: num, reason: nullableStr, from_time: nullableStr }).nullable(),
});
export const LabelScanSchema = z.object({
  name: z.string(), production_line: nullableStr, item: nullableStr, item_name: nullableStr, batch: nullableStr, label_code: z.string(), result: z.string(), reasons: nullableStr, expected_item: nullableStr,
  operator: nullableStr, scan_time: z.string(), status: nullableStr, resolution: nullableStr, resolved_by: nullableStr,
});
export type LabelScan = z.infer<typeof LabelScanSchema>;
export const FactorySchema = z.object({
  plant: z.object({ name: z.string(), plant_name: z.string(), plant_type: nullableStr, company: nullableStr, default_warehouse: nullableStr }),
  period: z.string(), months: z.array(z.string()), as_of: z.string(), horizon_days: num,
  permissions: z.object({ can_operate: z.boolean(), can_manage: z.boolean(), can_request_spares: z.boolean(), can_log_downtime: z.boolean() }),
  lines: z.array(ProductionLineSchema),
  production: z.object({
    by_month: z.array(z.object({ month: z.string(), planned: num, produced: num, orders: num, completed: num, late: num, completion_pct: nnum })),
    by_sku: z.array(z.object({ item: z.string(), item_name: nullableStr, line: nullableStr, planned: num, produced: num, orders: num, open_orders: z.array(z.string()), completion_pct: nnum })),
    today: z.object({ date: z.string(), produced: num, orders_completed: num }),
  }),
  capacity: z.array(CapacityRowSchema),
  oee: z.array(z.object({ line_code: z.string(), line_name: z.string(), availability_pct: nnum, performance_pct: nnum, quality_pct: nnum, oee_pct: nnum, reliable: z.boolean(), scheduled_hours: num, run_hours: num, produced_qty: nnum, downtime_hours: num })),
  downtime: z.object({ period: DowntimeSummarySchema, by_month: z.array(z.object({ month: z.string(), lost_hours: num, count: num })), recent: z.array(DowntimeEntrySchema), reasons: z.array(z.string()) }),
  alerts: z.array(FactoryAlertSchema),
  maintenance: z.array(z.object({ name: z.string(), parent: z.string(), asset_name: nullableStr, asset_label: nullableStr, maintenance_task: z.string(), maintenance_type: nullableStr, maintenance_status: nullableStr, periodicity: nullableStr, next_due_date: nullableStr, last_completion_date: nullableStr, assign_to: nullableStr, overdue: z.boolean() })),
  spares: z.array(SpareRiskSchema),
  orders: z.object({ rows: z.array(OrderReadinessSchema), counts: z.record(z.string(), z.number()), stock: z.record(z.string(), z.number()).optional() }),
  quality: z.object({ rejected: z.array(z.object({ name: z.string(), inspection_type: nullableStr, reference_type: nullableStr, reference_name: nullableStr, item_code: nullableStr, item_name: nullableStr, status: nullableStr, sample_size: nnum, report_date: nullableStr, inspected_by: nullableStr })), accepted: num, rejected_count: num, inspections: num.optional() }),
  labels: z.object({ recent: z.array(LabelScanSchema), open_mismatches: z.array(LabelScanSchema), scans: num, mismatches: num, today: num, error_rate_pct: num.optional() }),
  summary: z.object({ alerts_open: num, trend_alerts: num, maintenance_overdue: num, spares_critical: num, spares_reorder: num, orders_at_risk: num, lines_constrained: num, label_mismatches_open: num, quality_rejected: num, lost_hours_period: num }),
});
export type Factory = z.infer<typeof FactorySchema>;
export const LabelContextSchema = z.object({
  plant: z.string(), lines: z.array(z.object({ line_code: z.string(), line_name: z.string(), skus: z.array(z.object({ item: z.string(), item_name: nullableStr, rate_per_hour: num, uom: nullableStr })) })),
  items: z.array(z.object({ item: z.string(), item_name: nullableStr })),
  rules: z.array(z.object({ name: z.string(), item: z.string(), item_name: nullableStr, label_code: z.string(), packaging: nullableStr, requires_batch: num, requires_expiry_check: num, status: nullableStr, production_line: nullableStr })),
  batches: z.array(z.object({ name: z.string(), item: z.string(), expiry_date: nullableStr, manufacturing_date: nullableStr })),
  permissions: z.object({ can_operate: z.boolean(), can_manage: z.boolean(), can_request_spares: z.boolean(), can_log_downtime: z.boolean() }),
});
export type LabelContext = z.infer<typeof LabelContextSchema>;
export const LabelVerifySchema = z.object({ result: z.string(), reasons: z.array(z.string()), rule: nullableStr, expected_item: nullableStr, log: z.string(), scan_time: z.string() });
export type LabelVerify = z.infer<typeof LabelVerifySchema>;

export function useFactory(plant: string | undefined, period?: string) {
  return useQuery({ queryKey: ["factory", plant ?? "", period ?? ""], enabled: Boolean(plant), staleTime: 30_000,
    queryFn: async () => FactorySchema.parse(await frappe.call("onesphere.api.operations.factory", { plant, ...(period ? { period } : {}) })) });
}
export function useLabelContext(plant: string | undefined) {
  return useQuery({ queryKey: ["label_context", plant ?? ""], enabled: Boolean(plant), queryFn: async () => LabelContextSchema.parse(await frappe.call("onesphere.api.operations.label_context", { plant })) });
}
/** One mutation hook for the factory decisions (label verification, downtime, spare request, trend scan, exception resolution). */
export function useOpsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: string; params: Record<string, unknown> }) => frappe.post<Record<string, unknown>>(`onesphere.api.operations.${input.method}`, input.params),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["factory"] }); void qc.invalidateQueries({ queryKey: ["plant_overview"] }); },
  });
}
export async function verifyLabel(plant: string, labelCode: string, item: string | null, batch: string | null, line: string | null) {
  return LabelVerifySchema.parse(await frappe.post("onesphere.api.operations.verify_label", { plant, label_code: labelCode, item, batch, production_line: line }));
}


// ---------------------------------------------------------------- recruitment channels (REC-02, REC-03)

export const ChannelSchema = z.object({
  name: z.string(), channel_name: z.string(),
  kind: z.enum(["Job board", "Careers site", "Internal portal", "Referral", "Agency", "Government"]),
  active: z.number(), deep_link: nullableStr, supplier: nullableStr, contact_person: nullableStr,
  contact_email: nullableStr, phone: nullableStr,
  fee_percent: nullableNum, guarantee_days: nullableNum, exclusive: z.number().nullable().optional(),
  terms: nullableStr, postings: z.number().optional().default(0), posted: num0,
});
export type RecruitmentChannel = z.infer<typeof ChannelSchema>;

export const ChannelsSchema = z.object({ channels: z.array(ChannelSchema), is_hr: z.boolean() });

export const ChannelEffectivenessSchema = z.object({
  as_of: z.string(), company: nullableStr, is_hr: z.boolean(),
  rows: z.array(z.object({
    name: nullableStr, channel_name: nullableStr, kind: nullableStr, unattributed: z.boolean().optional(),
    supplier: nullableStr.optional(), fee_percent: nullableNum.optional(),
    applicants: z.number(), screened: z.number(), shortlisted: z.number(), hired: z.number(), rejected: z.number(),
    conversion_pct: z.number().nullable(), cost: num0, cost_per_hire: z.number().nullable(),
  })),
  attribution: z.object({ total: z.number(), unattributed: z.number(), pct: z.number().nullable() }),
  unattributed_examples: z.array(z.object({
    applicant: z.string(), applicant_name: nullableStr, status: nullableStr, source: nullableStr,
    candidate_profile: nullableStr,
  })),
});
export type ChannelEffectiveness = z.infer<typeof ChannelEffectivenessSchema>;

export function useChannels(includeInactive = false) {
  return useQuery({
    queryKey: ["rec_channels", includeInactive],
    queryFn: async () => ChannelsSchema.parse(
      await frappe.call("onesphere.api.recruitment.channels", includeInactive ? { include_inactive: 1 } : {})),
  });
}

export function useChannelEffectiveness(company?: string) {
  return useQuery({
    queryKey: ["rec_channel_effectiveness", company ?? ""],
    queryFn: async () => ChannelEffectivenessSchema.parse(
      await frappe.call("onesphere.api.recruitment.channel_effectiveness", company ? { company } : {})),
  });
}

/** Saving a channel, or attributing a candidate to one; both re-read the register and the funnel. */
export function useChannelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: "save_channel" | "attribute_candidate"; params: Record<string, unknown> }) =>
      frappe.post<Record<string, unknown>>(`onesphere.api.recruitment.${input.method}`, input.params),
    onSuccess: () => {
      for (const key of ["rec_channels", "rec_channel_effectiveness", "rec_openings"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

// ---------------------------------------------------------------- agency correspondence (REC-02, 9.2)

const MailReadinessSchema = z.object({
  can_send: z.boolean(), can_receive: z.boolean(),
  outgoing_account: nullableStr, incoming_account: nullableStr,
  muted: z.boolean(), blockers: z.array(z.string()),
});
export type MailReadiness = z.infer<typeof MailReadinessSchema>;

export const MailStatusSchema = MailReadinessSchema.extend({
  queued_not_sent: z.number(), would_be: z.string(), communications: z.number(),
});

const MessageSchema = z.object({
  name: z.string(), subject: nullableStr, content: nullableStr, sender: nullableStr, recipients: nullableStr,
  sent_or_received: z.enum(["Sent", "Received"]), communication_date: nullableStr,
  reference_doctype: nullableStr, reference_name: nullableStr, state: z.string(),
});

const ThreadSchema = z.object({
  reference_doctype: z.string(), reference_name: z.string(), subject: nullableStr,
  messages: z.array(MessageSchema), sent: z.number(), received: z.number(),
  last_at: nullableStr, awaiting_reply: z.boolean(),
  job_opening: nullableStr, job_title: nullableStr, company: nullableStr,
  days_quiet: z.number().optional(),
});

export const AgencyCorrespondenceSchema = z.object({
  as_of: z.string(), chase_after_days: z.number(), is_hr: z.boolean(),
  readiness: MailReadinessSchema,
  agencies: z.array(z.object({
    name: z.string(), contact_person: nullableStr, contact_email: nullableStr, supplier: nullableStr,
    fee_percent: nullableNum,
    threads: z.array(ThreadSchema),
    chase: z.array(ThreadSchema),
    postings: z.array(z.object({ name: z.string(), channel: nullableStr, job_opening: nullableStr,
                                 status: nullableStr, posted_on: nullableStr, job_title: nullableStr })),
  })),
});
export type AgencyCorrespondence = z.infer<typeof AgencyCorrespondenceSchema>;

export function useMailStatus() {
  return useQuery({
    queryKey: ["mail_status"],
    queryFn: async () => MailStatusSchema.parse(await frappe.call("onesphere.api.correspondence.mail_status")),
  });
}

export function useAgencyCorrespondence(channel?: string) {
  return useQuery({
    queryKey: ["agency_correspondence", channel ?? ""],
    queryFn: async () => AgencyCorrespondenceSchema.parse(
      await frappe.call("onesphere.api.correspondence.agency_correspondence", channel ? { channel } : {})),
  });
}

/** Sending a job description to an agency, or following up on a thread. */
export function useCorrespondenceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: "send_posting" | "reply_on_thread"; params: Record<string, unknown> }) =>
      frappe.post<{ communication: string; to: string; state: string }>(
        `onesphere.api.correspondence.${input.method}`, input.params),
    onSuccess: () => {
      for (const key of ["agency_correspondence", "mail_status", "rec_channels"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

// ---------------------------------------------------------------- notification settings (9.2)

export const NotificationSettingsSchema = z.object({
  as_of: z.string(),
  tracks: z.array(z.string()),
  routes: z.array(z.string()),
  readiness: z.object({
    can_send: z.boolean(), can_receive: z.boolean(), muted: z.boolean(),
    outgoing_account: nullableStr, incoming_account: nullableStr,
    blockers: z.array(z.string()), mobile_channel: nullableStr,
  }),
  features: z.array(z.object({
    code: z.string(), track: z.string(), label: z.string(), audience: z.string(),
    default_routes: z.array(z.string()), has_severity: z.boolean(),
    rule: nullableStr, configured: z.boolean(), is_active: z.boolean(),
    routes: z.object({ in_app: z.boolean(), email: z.boolean(), mobile: z.boolean() }),
    deliverable: z.array(z.string()),
    blocked: z.record(z.string(), z.array(z.string())),
    min_severity: nullableStr, recipients_role: nullableStr,
  })),
  summary: z.object({
    features: z.number(), configured: z.number(), on: z.number(), blocked: z.number(),
    blocked_features: z.array(z.string()),
  }),
});
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

export function useNotificationSettings() {
  return useQuery({
    queryKey: ["notification_settings"],
    queryFn: async () => NotificationSettingsSchema.parse(
      await frappe.call("onesphere.api.notifications.settings")),
  });
}

/** Switching a feature's routes, or sending one test through them. */
export function useNotificationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: "set_feature" | "send_test"; params: Record<string, unknown> }) =>
      frappe.post<Record<string, unknown>>(`onesphere.api.notifications.${input.method}`, input.params),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["notification_settings"] }); },
  });
}

// ---------------------------------------------------------------- workforce assignments (WFA-01 … WFA-04)

const AssignmentPeriodSchema = z.object({
  name: z.string(), person: z.string(), person_name: nullableStr,
  assignment_type: z.enum(["Employee", "Contractor"]),
  employee: nullableStr, contractor: nullableStr, company: nullableStr,
  from_date: z.string(), to_date: nullableStr,
  state: z.enum(["Draft", "Future", "Current", "Past"]),
  role_title: nullableStr, reason: nullableStr,
  days: z.number(), gap_days_before: z.number(),
});
export type AssignmentPeriod = z.infer<typeof AssignmentPeriodSchema>;

const WorkforcePersonSchema = z.object({
  name: z.string(), person_name: z.string(), national_id: nullableStr,
  current_type: nullableStr, current_since: nullableStr, status: nullableStr,
  assignments: z.number(), both_sides: z.boolean(),
  periods: z.array(AssignmentPeriodSchema),
});

export const AssignmentsOverviewSchema = z.object({
  as_of: z.string(), company: nullableStr,
  permissions: z.object({ can_view: z.boolean(), can_manage: z.boolean() }),
  counts: z.object({
    people: z.number(), employees_today: z.number(), contractors_today: z.number(),
    unengaged: z.number(), changed_side: z.number(),
  }),
  people: z.array(WorkforcePersonSchema),
  changed_side: z.array(WorkforcePersonSchema),
  unlinked: z.object({ contractors: z.array(z.string()) }),
});
export type AssignmentsOverview = z.infer<typeof AssignmentsOverviewSchema>;

export const PersonTimelineSchema = z.object({
  person: z.record(z.string(), z.unknown()),
  permissions: z.object({ can_view: z.boolean(), can_manage: z.boolean() }),
  periods: z.array(AssignmentPeriodSchema.extend({
    history: z.object({
      kind: z.string(), record: nullableStr,
      engagements: z.number().optional(), time_logs: z.number().optional(), documents: z.number().optional(),
      compliance_documents: z.number().optional(), certifications: z.number().optional(),
      timesheets: z.number().optional(),
    }),
  })),
});
export type PersonTimeline = z.infer<typeof PersonTimelineSchema>;

export function useAssignments(company?: string) {
  return useQuery({
    queryKey: ["assignments_overview", company ?? ""],
    queryFn: async () => AssignmentsOverviewSchema.parse(
      await frappe.call("onesphere.api.assignments.overview", company ? { company } : {})),
  });
}

export function usePersonTimeline(name: string | null) {
  return useQuery({
    queryKey: ["person_timeline", name ?? ""], enabled: !!name,
    queryFn: async () => PersonTimelineSchema.parse(
      await frappe.call("onesphere.api.assignments.person", { name })),
  });
}

/** Changing which side of the payroll somebody is on. */
export function useAssignmentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: "hire_contractor" | "engage_employee"; params: Record<string, unknown> }) =>
      frappe.post<Record<string, unknown>>(`onesphere.api.assignments.${input.method}`, input.params),
    onSuccess: () => {
      for (const key of ["assignments_overview", "person_timeline", "contractor_overview", "contractor", "people_overview"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

// ---------------------------------------------------------------- approval integrity (HRX-01, 9.7)

const FindingsSchema = z.object({
  sound: z.number(), total: z.number(),
  counts: z.object({ self_approved: z.number(), approver_is_creator: z.number(), no_approver: z.number() }),
  self_approved: z.array(z.record(z.string(), z.unknown())),
  approver_is_creator: z.array(z.record(z.string(), z.unknown())),
  no_approver: z.array(z.record(z.string(), z.unknown())),
});

export const ApprovalIntegritySchema = z.object({
  as_of: z.string(), can_manage: z.boolean(), total_findings: z.number(),
  guards: z.array(z.object({
    key: z.string(), label: z.string(), control: z.string(),
    enforced: z.boolean(), configured: z.boolean(), bypassed_by_workflow: z.boolean(),
  })),
  areas: z.record(z.string(), FindingsSchema),
  workflows: z.array(z.object({ name: z.string(), document_type: nullableStr, is_active: z.number().nullable().optional() })),
  unread_hrms_feed: z.array(z.object({ doctype: nullableStr, n: z.number() })),
});
export type ApprovalIntegrity = z.infer<typeof ApprovalIntegritySchema>;

export function useApprovalIntegrity() {
  return useQuery({
    queryKey: ["approval_integrity"],
    queryFn: async () => ApprovalIntegritySchema.parse(await frappe.call("onesphere.api.approvals.integrity")),
  });
}

export function useApprovalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; on: number }) =>
      frappe.post<{ key: string; on: boolean }>("onesphere.api.approvals.set_guard", input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["approval_integrity"] }); },
  });
}

// ---------------------------------------------------------------- conduct (HRX-11)

export const EmployeeFilesSchema = z.object({
  as_of: z.string(),
  employees: z.array(z.object({
    name: z.string(), employee_name: nullableStr, company: nullableStr, designation: nullableStr,
    department: nullableStr, branch: nullableStr, grade: nullableStr, employment_type: nullableStr,
    completeness: z.object({
      sections: z.array(z.object({ section: z.string(), held: z.number(), of: z.number(),
                                   complete: z.boolean(), missing: z.array(z.string()) })),
      held: z.number(), of: z.number(), missing: z.array(z.string()),
      score: z.number(), complete: z.boolean() }),
    faults: z.array(z.string()),
    reach: z.object({ ways: z.array(z.string()), reachable: z.boolean(), by_phone: z.boolean() }),
    kin: z.object({ named: z.boolean(), contactable: z.boolean(), present: z.boolean(),
                    relation: nullableStr }),
    pay: z.object({ mode: nullableStr, payable: z.boolean(), reason: nullableStr }),
    terms: z.object({ employment_type: nullableStr, grade: nullableStr, known: z.boolean(),
                      missing: z.array(z.string()) }),
    history: z.object({ education: z.number(), prior_employers: z.number(), moves_here: z.number(),
                        arrived_knowing_nothing_recorded: z.boolean() }),
  })),
  summary: z.object({ employees: z.number(), complete: z.number(), without_faults: z.number(),
                      score: z.number().nullable(), unreachable: z.number(),
                      no_emergency_contact: z.number(), unpayable: z.number(),
                      bank_without_an_account: z.number(), terms_unknown: z.number(),
                      no_background: z.number(), with_education: z.number(),
                      with_prior_employers: z.number() }),
  sections: z.array(z.object({ section: z.string(), missing: z.number(), of: z.number() })),
  departments: z.object({ total: z.number(), empty: z.number(), single: z.number(), used: z.number(),
                          empty_share: z.number().nullable(), cluttered: z.boolean(),
                          names: z.array(z.string()) }),
  designations: z.object({ total: z.number(), empty: z.number(), single: z.number(), used: z.number(),
                           empty_share: z.number().nullable(), cluttered: z.boolean(),
                           names: z.array(z.string()) }),
  branches: z.number(), groups: z.number(),
  holiday_lists: z.array(z.object({ name: z.string(), from_date: nullableStr, to_date: nullableStr,
                                    total_holidays: num0 })),
  calendar: z.object({ lists: z.number(), years: z.array(z.number()), ends: nullableStr,
                       days_left: z.number().nullable(), expired: z.boolean(), running_out: z.boolean(),
                       covers_next_year: z.boolean(), horizon: z.number() }),
  calendar_by_company: z.object({ without_next_year: z.array(z.string()),
                                  default_still_this_year: z.array(z.string()), year: z.number() }),
  next_year: z.number(),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ manage: z.boolean() }),
});
export type EmployeeFiles = z.infer<typeof EmployeeFilesSchema>;

export function useEmployeeFiles(company?: string) {
  return useQuery({
    queryKey: ["employee_files", company ?? ""],
    queryFn: async () => EmployeeFilesSchema.parse(
      await frappe.call("onesphere.api.employee_file.board", company ? { company } : {})),
  });
}

/** The DRC national days are fixed dates, so next year's calendar is the one piece of content on this wave
 *  that can honestly be created without asking somebody. The customer's own shutdowns are not knowable from here. */
export function useBuildNextYearCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { year?: number }) => frappe.post<{
      year: number; created: string[]; attached: string[]; national_days: number; note: string;
    }>("onesphere.api.employee_file.build_next_year_calendar", input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["employee_files"] }); },
  });
}

/** Disabled, never deleted: every Attendance, Timesheet and Salary Slip already written against a department
 *  keeps its reference, and disabling only takes it out of the pickers. Asks before touching 172 records. */
export function useTidyDepartments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { confirm?: number }) => frappe.post<{
      would_disable?: number; disabled?: number; sample?: string[]; confirm_required: boolean;
    }>("onesphere.api.employee_file.tidy_departments", input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["employee_files"] }); },
  });
}

/* The ERPNext fleet board (`onesphere.api.fleet.board`, `post_readings`, `link_assets`) was read here until
 * 2026-09-28. The `/fleet` module's overview now shows **OneFleet's** register instead (its own vehicles and its
 * own thirty-day KPIs): the fleet's records live in OneFleet, the ERP holds the asset a vehicle links to, and the
 * ERPNext read was refused for the fleet's own roles anyway (owner's report). The endpoints and their tests stay
 * on the server; nothing in this application calls them. */

export const TravelBoardSchema = z.object({
  as_of: z.string(),
  requests: z.array(z.object({
    name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
    travel_type: nullableStr, travel_funding: nullableStr, purpose_of_travel: nullableStr,
    description: nullableStr, docstatus: z.number(),
    os_approver: nullableStr, os_decision: nullableStr, os_decision_note: nullableStr,
    os_decided_by: nullableStr, os_decided_on: nullableStr,
    os_departure: nullableStr, os_return: nullableStr,
    itinerary: z.array(z.object({
      travel_from: nullableStr, travel_to: nullableStr, mode_of_travel: nullableStr,
      departure_date: nullableStr, arrival_date: nullableStr,
      travel_advance_required: z.number().nullable().optional(), advance_amount: nullableStr,
    })),
    window: z.object({ legs: z.number(), departure: nullableStr, return: nullableStr,
                       days: z.number().nullable(), advance_requested: num0, lodging_nights: z.number() }),
    costing: z.object({ funded: num0, sponsored: num0, total: num0, declared_total: num0,
                        lines: z.number(), mismatch: z.boolean(),
                        by_type: z.array(z.object({ type: z.string(), amount: num0 })) }),
    notice: z.object({ days: z.number().nullable(), retrospective: z.boolean(), short_notice: z.boolean() }),
    faults: z.array(z.string()),
    advances: z.array(z.object({ name: z.string(), advance_amount: num0, paid_amount: num0 })),
    settlement: z.object({ authorised: num0, advanced: num0, claimed: num0, outstanding: num0,
                           settled: z.boolean(), variance: z.number().nullable(), over: z.boolean(),
                           variance_pct: z.number().nullable() }),
  })),
  summary: z.object({ requests: z.number(), awaiting: z.number(), approved: z.number(),
                      rejected: z.number(), with_faults: z.number(), self_approved: z.number(),
                      upcoming: z.number(), authorised: num0, advance_asked: num0 }),
  coverage: z.object({ total: num0, covered: num0, share: z.number().nullable(), claims: z.number() }),
  unrequested: z.object({ claims: z.number(), amount: num0, people: z.number(), oldest: nullableStr }),
  price_book: z.object({
    routes: z.array(z.object({ route: z.string(), trips: z.number(), low: num0, high: num0,
                               mean: num0, total: num0, spread: num0, wide: z.boolean() })),
    unparsed: z.number(), priced: z.number(), tolerance: num0,
    wide: z.array(z.object({ route: z.string() }).loose()),
  }),
  purposes: z.array(z.string()), modes: z.array(z.string()), notice_days: z.number(),
  advance_gap: num0,
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ manage: z.boolean() }),
});
export type TravelBoard = z.infer<typeof TravelBoardSchema>;

export function useTravelBoard(company?: string) {
  return useQuery({
    queryKey: ["travel_board", company ?? ""],
    queryFn: async () => TravelBoardSchema.parse(
      await frappe.call("onesphere.api.travel.board", company ? { company } : {})),
  });
}

/** Creating the travel purposes is what makes a request possible at all: `purpose_of_travel` is mandatory
 *  and `Purpose of Travel` held zero rows, so the doctype could not be saved by anybody. */
export function useOpenTravelChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => frappe.post<{ created: string[]; purposes: number }>(
      "onesphere.api.travel.open_travel", {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["travel_board"] }); },
  });
}

/** Ticking `travel_advance_required` on an itinerary reached nothing before this wave: no field anywhere
 *  pointed at a travel request, so the box was a wish. The advance is raised in draft — paying it is a
 *  finance action with its own approval (wave N). */
export function useDrawTravelAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { request: string; amount?: number }) =>
      frappe.post<{ advance: string; created: boolean; amount: number }>(
        "onesphere.api.travel.draw_advance", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["travel_board"] });
      void qc.invalidateQueries({ queryKey: ["expense_advances"] });
    },
  });
}

export const ConductBoardSchema = z.object({
  as_of: z.string(),
  grievances: z.array(z.object({
    name: z.string(), subject: nullableStr, raised_by: nullableStr, employee_name: nullableStr,
    date: nullableStr, status: nullableStr, grievance_type: nullableStr, description: nullableStr,
    days: z.number(), closed: z.boolean(), overdue: z.boolean(), against_self: z.boolean(),
  })),
  grievance_summary: z.object({ grievances: z.number(), open: z.number(), overdue: z.number(),
                                resolved: z.number(), invalid: z.number(),
                                average_days_to_resolve: num0, raisers: z.number() }),
  cases: z.array(z.object({
    name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
    category: nullableStr, stage: nullableStr, opened_on: nullableStr, opened_by: nullableStr,
    allegation: nullableStr, evidence_note: nullableStr, reply_invited_on: nullableStr,
    replied_on: nullableStr, outcome: nullableStr, decided_by: nullableStr, decided_on: nullableStr,
    sanction: nullableStr, decision_note: nullableStr, faults: z.array(z.string()),
  })),
  case_summary: z.object({ cases: z.number(), open: z.number(), upheld: z.number(),
                           not_upheld: z.number(), at_dismissal: z.number(), with_faults: z.number(),
                           faults: z.array(z.string()) }),
  types: z.array(z.object({ name: z.string(), description: nullableStr })),
  evidence: z.array(z.object({ key: z.string(), value: z.number() })),
  ladder: z.array(z.string()),
  retention_days: z.number(), sla_days: z.number(),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ manage: z.boolean() }),
});
export type ConductBoard = z.infer<typeof ConductBoardSchema>;

export function useConductBoard(company?: string) {
  return useQuery({
    queryKey: ["conduct_board", company ?? ""],
    queryFn: async () => ConductBoardSchema.parse(
      await frappe.call("onesphere.api.conduct.board", company ? { company } : {})),
  });
}

/** Creating the grievance categories is what makes the channel usable at all: `grievance_type` is mandatory. */
export function useOpenGrievanceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => frappe.post<{ created: string[]; types: number }>(
      "onesphere.api.conduct.open_channel", {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["conduct_board"] }); },
  });
}

// ---------------------------------------------------------------- hiring paperwork (HRX-10)

export const HiringBoardSchema = z.object({
  as_of: z.string(),
  offers: z.array(z.object({
    name: z.string(), applicant_name: nullableStr, job_applicant: nullableStr, status: nullableStr,
    offer_date: nullableStr, designation: nullableStr, company: nullableStr,
    terms: z.array(z.object({ offer_term: nullableStr, value: nullableStr })),
    coverage: z.object({ given: z.number(), required: z.number(), missing: z.array(z.string()),
                         blank: z.array(z.string()), complete: z.boolean() }),
    state: z.object({ status: z.string(), days: z.number(), stale: z.boolean(), closed: z.boolean() }),
  })),
  library: z.object({ offers: z.number(), terms_written: z.number(), distinct_terms: z.number(),
                      written_more_than_once: z.number(), repetition_pct: num0,
                      would_be_templated: z.array(z.string()) }),
  templates: z.object({ terms: z.number(), letters: z.number() }),
  incomplete_offers: z.array(z.record(z.string(), z.any())),
  stale_offers: z.array(z.record(z.string(), z.any())),
  letters: z.object({ hires: z.number(), with_letter: z.number(), without_letter: z.number(),
                      covered_pct: num0, written: z.number(),
                      missing: z.array(z.object({ name: nullableStr, employee_name: nullableStr,
                                                  designation: nullableStr, date_of_joining: nullableStr,
                                                  job_applicant: nullableStr })) }),
  interviews: z.object({ interviews: z.number(), decided: z.number(), with_feedback: z.number(),
                         rated: z.number(), undocumented: z.number(), pending: z.number(),
                         undocumented_rows: z.array(z.record(z.string(), z.any())) }),
  referrals: z.object({ referrals: z.number(), accepted: z.number(), in_process: z.number(),
                        rejected: z.number(), bonus_due: z.number(), conversion_pct: num0,
                        referrers: z.number() }),
  referral_rows: z.array(z.object({
    name: z.string(), full_name: nullableStr, status: nullableStr, for_designation: nullableStr,
    referrer: nullableStr, referrer_name: nullableStr, date: nullableStr,
    is_applicable_for_referral_bonus: z.number().nullable(), referral_payment_status: nullableStr,
  })),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ manage: z.boolean() }),
});
export type HiringBoard = z.infer<typeof HiringBoardSchema>;

export function useHiringBoard(company?: string) {
  return useQuery({
    queryKey: ["hiring_board", company ?? ""],
    queryFn: async () => HiringBoardSchema.parse(
      await frappe.call("onesphere.api.hiring.board", company ? { company } : {})),
  });
}

function hiringMutation<T>(method: string) {
  return () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (input: T) => frappe.post<Record<string, unknown>>(method, input ?? {}),
      onSuccess: () => { void qc.invalidateQueries({ queryKey: ["hiring_board"] }); },
    });
  };
}

/** Each of these writes a document, so each is a click rather than a page load. */
export const useBuildTermTemplate = hiringMutation<void>("onesphere.api.hiring.build_term_template");
export const useBuildLetterTemplate = hiringMutation<void>("onesphere.api.hiring.build_letter_template");
export const useIssueLetter = hiringMutation<{ job_applicant: string }>("onesphere.api.hiring.issue_letter");

// ---------------------------------------------------------------- training outcomes (HRX-09)

const AttendanceSchema = z.object({ invited: z.number(), present: z.number(), absent: z.number(),
                                    completed: z.number(), no_show_pct: num0, completion_pct: num0 });
const ResultsSchema = z.object({ graded: z.number(), passed: z.number(), failed: z.number(),
                                 ungraded: z.number(), average_score: num0, pass_rate: num0, hours: num0 });

export const TrainingBoardSchema = z.object({
  as_of: z.string(),
  events: z.array(z.object({
    name: z.string(), event_name: nullableStr, training_program: nullableStr, company: nullableStr,
    event_status: nullableStr, type: nullableStr, start_time: nullableStr, end_time: nullableStr,
    location: nullableStr, closed: z.boolean(),
    attendance: AttendanceSchema, results: ResultsSchema,
    feedback: z.object({ given: z.number(), not_asked: z.number(), response_pct: num0 }),
  })),
  summary: z.object({ programmes: z.number(), events: z.number(), completed: z.number(),
                      attended: z.number(), no_shows: z.number(), graded: z.number(),
                      ungraded: z.number(), feedback: z.number(), never_asked: z.number() }),
  matrix: z.object({ skills: z.number(), fresh: z.number(), ageing: z.number(), stale: z.number(),
                     never: z.number(), trustworthy_pct: num0,
                     oldest: z.object({ skill: nullableStr, employee_name: nullableStr,
                                        days: z.number().nullable() }).passthrough().nullable() }),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ record: z.boolean() }),
});
export type TrainingBoard = z.infer<typeof TrainingBoardSchema>;

export const TrainingEventSchema = z.object({
  event: z.record(z.string(), z.any()),
  rows: z.array(z.object({
    employee: nullableStr, employee_name: nullableStr, department: nullableStr,
    status: nullableStr, attendance: nullableStr, is_mandatory: z.number().nullable().optional(),
    score: num0,
    grade: z.object({ score: num0, grade: z.string(), passed: z.boolean() }).nullable(),
    skills: z.array(z.object({ skill: z.string(), after: num0.nullable(), days: z.number().nullable(),
                               state: z.string() }).passthrough()),
  })),
  attendance: AttendanceSchema, results: ResultsSchema,
  feedback_rows: z.array(z.object({ name: z.string(), employee_name: nullableStr, feedback: nullableStr })),
  feedback: z.object({ given: z.number(), not_asked: z.number(), response_pct: num0 }),
  skills: z.array(z.string()),
  effectiveness: z.object({ people: z.number(), measured: z.number(), unmeasured: z.number(),
                            improved: z.number(), unchanged: z.number(), average_uplift: num0 }),
  can_record: z.boolean(),
});

export function useTrainingBoard(company?: string) {
  return useQuery({
    queryKey: ["training_board", company ?? ""],
    queryFn: async () => TrainingBoardSchema.parse(
      await frappe.call("onesphere.api.training.board", company ? { company } : {})),
  });
}

export function useTrainingEvent(name?: string) {
  return useQuery({
    queryKey: ["training_event", name ?? ""], enabled: Boolean(name),
    queryFn: async () => TrainingEventSchema.parse(
      await frappe.call("onesphere.api.training.event", { name })),
  });
}

/** Moving the skill matrix is a real change to what the redeployment search reads, so it is always a click. */
export function useApplyUplift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { training_event: string }) =>
      frappe.post<{ moved: number; skills: string[] }>("onesphere.api.training.apply_uplift", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["training_board"] });
      void qc.invalidateQueries({ queryKey: ["training_event"] });
    },
  });
}

// ---------------------------------------------------------------- appraisals (HRX-08)

const BandSchema = z.object({ label: z.string(), merit_multiplier: num0, from_score: num0 });
const BlendSchema = z.object({ score: num0, used: z.record(z.string(), num0), missing: z.array(z.string()),
                               reweighted: z.boolean() });
const GapSchema = z.object({ gap: num0, verdict: z.string(), measured: z.boolean(),
                             self: num0.optional(), manager: num0.optional() });

const AppraisalRowSchema = z.object({
  name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
  department: nullableStr, designation: nullableStr, appraisal_cycle: nullableStr,
  appraisal_template: nullableStr, remarks: nullableStr,
  final_score: num0, total_score: num0, self_score: num0, avg_feedback_score: num0,
  goal_score: num0, platform_score: num0,
  band: BandSchema, platform_band: BandSchema, blend: BlendSchema, gap: GapSchema,
});
export type AppraisalRow = z.infer<typeof AppraisalRowSchema>;

export const AppraisalBoardSchema = z.object({
  as_of: z.string(),
  cycles: z.array(z.object({ name: z.string(), cycle_name: nullableStr, company: nullableStr,
                             status: nullableStr, start_date: nullableStr, end_date: nullableStr,
                             kra_evaluation_method: nullableStr })),
  cycle: nullableStr,
  progress: z.object({ appraisees: z.number(), scored: z.number(), in_progress: z.number(),
                       not_started: z.number(), average_score: num0, with_feedback: z.number(),
                       complete_pct: num0 }),
  distribution: z.array(z.object({ label: z.string(), from_score: num0, merit_multiplier: num0, count: z.number() })),
  scoring: z.object({ reweighted: z.number(), platform_average: num0, blended_average: num0 }),
  gaps: z.object({ measured: z.number(), disagreeing: z.number(), overrated: z.number(),
                   underrated: z.number(), widest: AppraisalRowSchema.nullable() }),
  templates: z.array(z.object({ name: z.string(), template_title: nullableStr })),
  merit: z.object({ available: z.number(), consumed_by_incentive: z.boolean(), incentive_plans: z.number() }),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ run: z.boolean() }),
});
export type AppraisalBoard = z.infer<typeof AppraisalBoardSchema>;

export const AppraisalCycleSchema = z.object({
  cycle: z.record(z.string(), z.any()),
  rows: z.array(AppraisalRowSchema),
  progress: AppraisalBoardSchema.shape.progress,
  distribution: AppraisalBoardSchema.shape.distribution,
  merit: z.array(z.object({ employee: nullableStr, final_score: num0, band: z.string(),
                            merit_multiplier: num0, applied: z.boolean() })),
  can_run: z.boolean(),
});

export const AppraisalPersonSchema = z.object({
  appraisal: z.record(z.string(), z.any()),
  kras: z.array(z.object({ kra: nullableStr, per_weightage: num0, score: num0 })),
  kra_score: z.object({ score: num0, weight_used: num0, unweighted_lines: z.number(),
                        detail: z.array(z.record(z.string(), z.any())) }),
  self_ratings: z.array(z.object({ criteria: nullableStr, per_weightage: num0, score: num0 })),
  self_computed: z.object({ score: num0, weight_used: num0, unweighted_lines: z.number(),
                            detail: z.array(z.record(z.string(), z.any())) }),
  feedback: z.array(z.object({ name: z.string(), reviewer_name: nullableStr,
                               reviewer_designation: nullableStr, total_score: num0,
                               feedback: nullableStr, added_on: nullableStr })),
  blend: BlendSchema, band: BandSchema, gap: GapSchema,
  evidence: z.array(z.object({ key: z.string(), value: num0, detail: z.record(z.string(), z.any()).optional() })),
});

export function useAppraisalBoard(cycle?: string, company?: string) {
  return useQuery({
    queryKey: ["appraisal_board", cycle ?? "", company ?? ""],
    queryFn: async () => AppraisalBoardSchema.parse(
      await frappe.call("onesphere.api.appraisal.board",
        { ...(cycle ? { cycle } : {}), ...(company ? { company } : {}) })),
  });
}

export function useAppraisalCycle(name?: string) {
  return useQuery({
    queryKey: ["appraisal_cycle", name ?? ""], enabled: Boolean(name),
    queryFn: async () => AppraisalCycleSchema.parse(
      await frappe.call("onesphere.api.appraisal.cycle", { name })),
  });
}

export function useAppraisalPerson(appraisal?: string) {
  return useQuery({
    queryKey: ["appraisal_person", appraisal ?? ""], enabled: Boolean(appraisal),
    queryFn: async () => AppraisalPersonSchema.parse(
      await frappe.call("onesphere.api.appraisal.person", { appraisal })),
  });
}

// ---------------------------------------------------------------- payroll depth (HRX-07)

const DepthSchema = z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) }));

export const PayrollBoardSchema = z.object({
  as_of: z.string(),
  period: z.object({ from: z.string(), to: z.string(), n: z.number(), emps: z.number(), gross: num0, net: num0 }),
  all_time: z.object({ n: z.number(), emps: z.number(), gross: num0, deductions: num0, net: num0 }),
  coverage: z.object({ active: z.number(), assigned: z.number(), paid: z.number(), unassigned: z.number(),
                       assigned_not_paid: z.number(), covered_pct: num0 }),
  companies_without_anybody_assigned: z.array(z.string()),
  gratuity: z.object({ people: z.number(), qualifying: z.number(), not_yet_qualifying: z.number(),
                       months: num0, amount: num0, average_years: num0,
                       rule: z.record(z.string(), z.any()) }),
  depth: DepthSchema,
  basis: z.object({ payroll_based_on: nullableStr, unmarked_attendance_as: nullableStr,
                    attendance_days_recorded: z.number() }),
  permissions: z.object({ configure: z.boolean() }),
});
export type PayrollBoard = z.infer<typeof PayrollBoardSchema>;

export const GratuitySchema = z.object({
  rows: z.array(z.object({
    name: z.string(), employee_name: nullableStr, company: nullableStr, designation: nullableStr,
    grade: nullableStr, status: nullableStr, date_of_joining: nullableStr, relieving_date: nullableStr,
    monthly_pay: num0, years: num0, months: num0, amount: num0, qualifies: z.boolean(),
    bands: z.array(z.object({ from_year: num0, to_year: num0, years: num0, fraction_per_year: num0, months: num0 })),
    raised: z.object({ name: z.string(), status: nullableStr, amount: num0 }).nullable().optional(),
  })),
  summary: z.object({ people: z.number(), qualifying: z.number(), not_yet_qualifying: z.number(),
                      months: num0, amount: num0, average_years: num0 }),
  rule: z.record(z.string(), z.any()),
  can_configure: z.boolean(),
  indicative: z.record(z.string(), z.any()),
});

export const AdjustmentsSchema = z.object({
  rows: z.array(z.object({
    name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
    salary_component: z.string(), component_type: z.string(), amount: num0,
    payroll_date: nullableStr, type: nullableStr, reason: z.string().optional().default(""),
  })),
  count: z.number(), by_type: z.record(z.string(), num0), total: num0, can_post: z.boolean(),
  components: z.array(z.object({ name: z.string(), type: nullableStr })),
});

export const PayrollTaxSchema = z.object({
  slab: z.object({ name: z.string(), allow_tax_exemption: z.number(), standard_tax_exemption_amount: num0 }).nullable(),
  bands: z.array(z.object({ from_amount: num0, to_amount: num0, percent_deduction: num0 })),
  exemption: num0,
  basis: z.object({ bands_are: z.string(), pay_is: z.string(), taxable_base: z.string(), months_per_year: z.number() }).optional(),
  rows: z.array(z.object({
    name: z.string(), employee_name: nullableStr, gross_pay: num0, inss: num0.nullable(),
    ipr: num0.nullable(), taxable: num0, computed: num0, residual: num0,
  })),
  summary: z.object({ people: z.number(), charged: num0, computed: num0, residual: num0,
                      residual_pct: num0, relief_applied: z.boolean() }),
});

export function usePayrollBoard(company?: string) {
  return useQuery({
    queryKey: ["payroll_board", company ?? ""],
    queryFn: async () => PayrollBoardSchema.parse(
      await frappe.call("onesphere.api.payroll.board", company ? { company } : {})),
  });
}

export function useGratuity(company?: string) {
  return useQuery({
    queryKey: ["gratuity", company ?? ""],
    queryFn: async () => GratuitySchema.parse(
      await frappe.call("onesphere.api.payroll.gratuity", company ? { company } : {})),
  });
}

export function usePayrollAdjustments(company?: string) {
  return useQuery({
    queryKey: ["payroll_adjustments", company ?? ""],
    queryFn: async () => AdjustmentsSchema.parse(
      await frappe.call("onesphere.api.payroll.adjustments", company ? { company } : {})),
  });
}

export function usePayrollTax(company?: string) {
  return useQuery({
    queryKey: ["payroll_tax", company ?? ""],
    queryFn: async () => PayrollTaxSchema.parse(
      await frappe.call("onesphere.api.payroll.tax", company ? { company } : {})),
  });
}

/** Creating the end-of-service rule, and raising one leaver's indemnity. Both write; both are explicit. */
export function useConfigureGratuity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => frappe.post<{ rule: string; created: boolean }>(
      "onesphere.api.payroll.configure_gratuity_rule", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["gratuity"] });
      void qc.invalidateQueries({ queryKey: ["payroll_board"] });
    },
  });
}

export function useRaiseGratuity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string }) =>
      frappe.post<{ gratuity: string; amount: number }>("onesphere.api.payroll.raise_gratuity", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["gratuity"] });
      void qc.invalidateQueries({ queryKey: ["payroll_board"] });
    },
  });
}

// ---------------------------------------------------------------- lifecycle events (HRX-06)

const ServiceSchema = z.object({ years: z.number(), months: z.number(), days: z.number(), total_days: z.number() });
const BlockSchema = z.object({ key: z.string(), amount: num0.optional(), count: z.number().optional() });
const ExitReadinessSchema = z.object({ ready: z.boolean(), blocks: z.array(BlockSchema), count: z.number() });

export const LifecycleBoardSchema = z.object({
  as_of: z.string(),
  exits: z.array(z.object({
    name: z.string(), employee_name: nullableStr, company: nullableStr, designation: nullableStr,
    grade: nullableStr, status: nullableStr, department: nullableStr,
    date_of_joining: nullableStr, relieving_date: nullableStr, reason_for_leaving: nullableStr,
    service: ServiceSchema, readiness: ExitReadinessSchema, mid_exit: z.boolean(),
    balances: z.object({
      advance: z.object({ amount: num0, count: z.number() }),
      claims: z.object({ amount: num0, count: z.number() }),
      leave: z.array(z.object({ leave_type: z.string(), days: num0 })),
      assets: z.array(z.object({ name: z.string(), asset_name: nullableStr, asset_category: nullableStr,
                                 purchase_amount: num0, value_after_depreciation: num0 })),
    }),
    settlement: z.object({ name: z.string(), status: nullableStr,
                           total_payable_amount: num0, total_receivable_amount: num0 }).nullable(),
  })),
  summary: z.object({
    leavers: z.number(), mid_exit: z.number(), not_ready: z.number(), settlements: z.number(),
    advance_at_risk: num0, owed_to_leavers: num0,
  }),
  movements: z.array(z.object({
    name: z.string(), kind: z.string(), employee: z.string(), employee_name: nullableStr,
    company: nullableStr, date: nullableStr, summary: z.string(),
    changes: z.array(z.object({ field: nullableStr, from: nullableStr, to: nullableStr })),
  })),
  movement_summary: z.object({ total: z.number(), promotions: z.number(), transfers: z.number(), employees: z.number() }),
  assets: z.object({ total: z.number(), with_custodian: z.number() }),
  notice_days: z.number(),
  permissions: z.object({ record: z.boolean() }),
});
export type LifecycleBoard = z.infer<typeof LifecycleBoardSchema>;

export const ExitPreviewSchema = z.object({
  employee: z.record(z.string(), z.any()),
  service: ServiceSchema,
  notice: z.object({ state: z.string(), served_days: z.number().nullable(), required_days: z.number(),
                     shortfall: z.number(), days_left: z.number().nullable() }),
  payables: z.array(z.object({ key: z.string(), amount: num0, detail: nullableStr })),
  receivables: z.array(z.object({ key: z.string(), amount: num0, detail: nullableStr })),
  assets: z.object({ count: z.number(), value: num0,
                     items: z.array(z.object({ name: nullableStr, asset_name: nullableStr,
                                               category: nullableStr, value: num0 })) }),
  leave: z.array(z.object({ leave_type: z.string(), days: num0 })),
  settlement: z.object({ payable: num0, receivable: num0, net: num0, direction: z.string(), asset_recovery: num0 }),
  readiness: ExitReadinessSchema,
  existing: z.object({ name: z.string(), status: nullableStr }).passthrough().nullable(),
  excluded: z.array(z.string()),
  can_record: z.boolean(),
});

export function useLifecycleBoard(company?: string) {
  return useQuery({
    queryKey: ["lifecycle_board", company ?? ""],
    queryFn: async () => LifecycleBoardSchema.parse(
      await frappe.call("onesphere.api.lifecycle.board", company ? { company } : {})),
  });
}

export function useExitPreview(employee?: string) {
  return useQuery({
    queryKey: ["exit_preview", employee ?? ""], enabled: Boolean(employee),
    queryFn: async () => ExitPreviewSchema.parse(
      await frappe.call("onesphere.api.lifecycle.exit_preview", { employee })),
  });
}

export function useDraftSettlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string }) =>
      frappe.post<{ statement: string; created: boolean }>("onesphere.api.lifecycle.draft_settlement", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lifecycle_board"] });
      void qc.invalidateQueries({ queryKey: ["exit_preview"] });
    },
  });
}

// ---------------------------------------------------------------- expenses and advances (HRX-05)

const AgeBucketSchema = z.object({ bucket: z.string(), count: z.number(), amount: num0 });

export const ExpenseBoardSchema = z.object({
  as_of: z.string(),
  owed: z.object({
    claims: z.number(), amount: num0, buckets: z.array(AgeBucketSchema),
    oldest: z.object({ days: z.number(), amount: num0, who: nullableStr, claim: nullableStr }).nullable(),
  }),
  claims: z.object({
    total: z.number(), draft: z.number(), approved: z.number(), rejected: z.number(), paid: z.number(),
    claimed_amount: num0, sanctioned_amount: num0, reimbursed_amount: num0,
  }),
  advances: z.object({ count: z.number(), unaccounted: num0, awaiting_payment: num0, open: z.number() }),
  reach: z.object({
    lines: z.number(), amount: num0, with_project: z.number(), project_amount: num0,
    with_cost_center: z.number(), unattributed: z.number(), unattributed_amount: num0,
  }),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  permissions: z.object({ pay: z.boolean(), configure: z.boolean() }),
});
export type ExpenseBoard = z.infer<typeof ExpenseBoardSchema>;

const ClaimRowSchema = z.object({
  name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
  department: nullableStr, posting_date: nullableStr, approval_status: nullableStr, status: nullableStr,
  expense_approver: nullableStr, project: nullableStr, remark: nullableStr,
  total_claimed_amount: num0, total_sanctioned_amount: num0, total_amount_reimbursed: num0,
  outstanding: num0, payment_state: z.string(), age_days: z.number(),
  gap: z.object({ claimed: num0, sanctioned: num0, cut: num0, cut_pct: num0 }),
});

export const ExpenseQueueSchema = z.object({
  rows: z.array(ClaimRowSchema), count: z.number(), amount: num0, can_pay: z.boolean(),
  accounts: z.array(z.object({ name: z.string(), company: nullableStr, account_type: nullableStr })),
});

export const AdvancesSchema = z.object({
  rows: z.array(z.object({
    name: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
    posting_date: nullableStr, purpose: nullableStr, status: nullableStr,
    advance_amount: num0, paid: num0, claimed: num0, returned: num0,
    unaccounted: num0, awaiting_payment: num0, state: z.string(),
  })),
  count: z.number(), unaccounted: num0, awaiting_payment: num0, can_pay: z.boolean(),
});

export const SettlementQueueSchema = z.object({
  rows: z.array(z.object({
    claim: z.string(), employee: z.string(), employee_name: nullableStr, company: nullableStr,
    posting_date: nullableStr, amount: num0, covered: num0, cash_due: num0,
    candidates: z.array(z.object({
      advance: z.string(), unclaimed: num0, would_settle: num0, advance_left: num0,
      advance_account: nullableStr, posting_date: nullableStr,
    })),
  })),
  count: z.number(), drafts: z.number(), advance_holders: z.number(), covered: num0,
  can_settle: z.boolean(),
});

export const RosterSchema = z.object({
  on: z.string(), people: z.number(), rostered: z.number(),
  shifts: z.record(z.string(), z.array(z.object({
    employee: z.string(), employee_name: nullableStr, assignment: nullableStr,
    from: nullableStr, to: nullableStr,
  }))),
  on_no_shift: z.array(z.object({
    employee: z.string(), employee_name: nullableStr, company: nullableStr, default_shift: nullableStr,
  })),
  on_no_shift_count: z.number(),
  drafts: z.array(z.object({
    assignment: z.string(), employee: z.string(), employee_name: nullableStr, shift_type: nullableStr,
    from: nullableStr, to: nullableStr,
  })),
  draft_count: z.number(),
  shift_types: z.array(z.object({ name: z.string(), start_time: nullableStr, end_time: nullableStr })),
  can_edit: z.boolean(),
});

export const ShiftRequestsSchema = z.object({
  rows: z.array(z.object({
    name: z.string(), employee: z.string(), employee_name: nullableStr, shift_type: nullableStr,
    from_date: nullableStr, to_date: nullableStr, status: nullableStr, approver: nullableStr,
    docstatus: z.number(), company: nullableStr,
  })),
  count: z.number(), people: z.number(), with_own_approver: z.number(),
  departments_with_an_approver: z.number(), departments: z.number(), raisable: z.number(),
  blocked_reason: nullableStr, shift_types: z.array(z.string()), can_decide: z.boolean(),
});

export const AttendanceGapsSchema = z.object({
  to_mark: z.array(z.object({
    employee: z.string(), employee_name: nullableStr, day: z.string(), company: nullableStr,
  })),
  would_mark: z.number(), would_skip: z.number(), days: z.number(), people: z.number(),
  skipped_by_reason: z.record(z.string(), z.number()),
  from_date: z.string(), to_date: z.string(), holidays: z.array(z.string()), can_mark: z.boolean(),
});

export const RequisitionCandidatesSchema = z.object({
  rows: z.array(z.object({
    opening: z.string(), job_title: nullableStr, designation: nullableStr, company: nullableStr,
    status: nullableStr, posted_on: nullableStr, requisition: nullableStr, currency: nullableStr,
    ok: z.boolean(), blocked: nullableStr, reason: nullableStr,
    fields: z.object({
      no_of_positions: z.number().optional(), expected_compensation: z.number().optional(),
      compensation_basis: z.string().optional(),
    }).partial(),
  })),
  count: z.number(), without_a_request: z.number(), raisable: z.number(), requisitions: z.number(),
  can_raise: z.boolean(),
});

export const SmigSchema = z.object({
  floor: z.object({ daily: num0, days: z.number(), monthly: num0, indicative: z.boolean() }),
  basis: z.string(), checked: z.number(), below_count: z.number(), at_or_above_count: z.number(),
  no_base_count: z.number(), shortfall: num0, currency: nullableStr,
  below: z.array(z.object({
    employee: z.string(), employee_name: nullableStr, company: nullableStr, designation: nullableStr,
    base: num0.nullable().optional(), short_by: num0,
  })),
  no_base: z.array(z.object({
    employee: z.string(), employee_name: nullableStr, company: nullableStr, designation: nullableStr,
  })),
  by_company: z.record(z.string(), z.object({ below: z.number(), no_base: z.number(), at_or_above: z.number() })),
});

export const FinanceDocumentsSchema = z.object({
  kind: z.string(), doctype: z.string(), label: z.string(), columns: z.array(z.string()),
  rows: z.array(z.object({
    name: z.string(), date: nullableStr, party: nullableStr, party_name: nullableStr,
    status: nullableStr, company: nullableStr, currency: nullableStr, docstatus: z.number(),
    total: num0.optional(), outstanding: num0.optional(), total_base: num0.optional(), conversion_rate: num0.optional(),
    per_received: num0.optional(), per_billed: num0.optional(), per_delivered: num0.optional(),
  })),
  totals: z.object({ count: z.number(), total: num0, outstanding: num0, settled: num0, open: z.number() }),
  currencies: z.array(z.string()),
  base_currency: z.string(),
  registers: z.array(z.object({ kind: z.string(), label: z.string() })),
});

export function useExpenseBoard(company?: string) {
  return useQuery({
    queryKey: ["expense_board", company ?? ""],
    queryFn: async () => ExpenseBoardSchema.parse(
      await frappe.call("onesphere.api.expenses.board", company ? { company } : {})),
  });
}

export function useExpenseQueue(company?: string) {
  return useQuery({
    queryKey: ["expense_queue", company ?? ""],
    queryFn: async () => ExpenseQueueSchema.parse(
      await frappe.call("onesphere.api.expenses.queue", company ? { company } : {})),
  });
}

export function useAdvances(company?: string) {
  return useQuery({
    queryKey: ["advances", company ?? ""],
    queryFn: async () => AdvancesSchema.parse(
      await frappe.call("onesphere.api.expenses.advances", company ? { company } : {})),
  });
}

/** Reimbursement writes to the ledger, so it is always an explicit click and never a page load. */
export function useReimburse() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["expense_queue"] });
    void qc.invalidateQueries({ queryKey: ["expense_board"] });
    void qc.invalidateQueries({ queryKey: ["advances"] });
  };
  return useMutation({
    mutationFn: async (input: { claim: string; paid_from?: string }) =>
      frappe.post<{ claim: string; payment_entry: string; amount: number; status: string }>(
        "onesphere.api.expenses.reimburse", input),
    onSuccess: invalidate,
  });
}

export function usePayAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { advance: string; paid_from?: string }) =>
      frappe.post<{ advance: string; payment_entry: string; amount: number }>(
        "onesphere.api.expenses.pay_advance", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["advances"] });
      void qc.invalidateQueries({ queryKey: ["expense_board"] });
    },
  });
}

/** Draft claims a float could close (OS-0627). Read-only; settling is the explicit click below. */
export function useSettlementQueue(company?: string) {
  return useQuery({
    queryKey: ["settlement_queue", company ?? ""],
    queryFn: async () => SettlementQueueSchema.parse(
      await frappe.call("onesphere.api.expenses.settlement_queue", company ? { company } : {})),
  });
}

/** Setting an advance against a claim changes what the company owes, so it is never a page load. */
export function useSettleAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { claim: string; advance: string; amount?: number }) =>
      frappe.post<{ claim: string; advance: string; allocated: number; total_advance_amount: number;
        grand_total: number; status: string; left_on_advance: number }>(
        "onesphere.api.expenses.settle", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["settlement_queue"] });
      void qc.invalidateQueries({ queryKey: ["advances"] });
      void qc.invalidateQueries({ queryKey: ["expense_board"] });
      void qc.invalidateQueries({ queryKey: ["expense_queue"] });
    },
  });
}

/** The roster of one day (OS-0624). Read-only; every roster change below is an explicit click. */
export function useRoster(company?: string, on?: string) {
  return useQuery({
    queryKey: ["roster", company ?? "", on ?? ""],
    queryFn: async () => RosterSchema.parse(await frappe.call(
      "onesphere.api.attendance.roster", { ...(company ? { company } : {}), ...(on ? { on } : {}) })),
  });
}

export function useShiftRequests(company?: string) {
  return useQuery({
    queryKey: ["shift_requests", company ?? ""],
    queryFn: async () => ShiftRequestsSchema.parse(
      await frappe.call("onesphere.api.attendance.shift_requests", company ? { company } : {})),
  });
}

export function useAttendanceGaps(from_date?: string, to_date?: string, company?: string) {
  return useQuery({
    queryKey: ["attendance_gaps", from_date ?? "", to_date ?? "", company ?? ""],
    enabled: Boolean(from_date && to_date),
    queryFn: async () => AttendanceGapsSchema.parse(await frappe.call(
      "onesphere.api.attendance.attendance_gaps",
      { from_date, to_date, ...(company ? { company } : {}) })),
  });
}

function rosterWrite<I, O>(method: string, qc: ReturnType<typeof useQueryClient>) {
  return {
    mutationFn: async (input: I) => frappe.post<O>(method, input as Record<string, unknown>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["roster"] });
      void qc.invalidateQueries({ queryKey: ["shift_requests"] });
      void qc.invalidateQueries({ queryKey: ["attendance_gaps"] });
    },
  };
}

export function useAssignShift() {
  const qc = useQueryClient();
  return useMutation(rosterWrite<{ employee: string; shift_type: string; from_date: string; to_date?: string },
    { assignment: string; employee: string; shift_type: string }>("onesphere.api.attendance.assign_shift", qc));
}

export function useEndAssignment() {
  const qc = useQueryClient();
  return useMutation(rosterWrite<{ assignment: string; end_date: string },
    { assignment: string; to: string }>("onesphere.api.attendance.end_assignment", qc));
}

export function usePublishAssignment() {
  const qc = useQueryClient();
  return useMutation(rosterWrite<{ assignment: string },
    { assignment: string; docstatus: number }>("onesphere.api.attendance.publish_assignment", qc));
}

export function useDecideShiftRequest() {
  const qc = useQueryClient();
  return useMutation(rosterWrite<{ request: string; approve: boolean },
    { request: string; status: string; assignments: string[] }>("onesphere.api.attendance.decide_shift_request", qc));
}

/** Marking a range writes submitted Attendance rows, so it is never a page load. */
export function useMarkAttendance() {
  const qc = useQueryClient();
  return useMutation(rosterWrite<{ from_date: string; to_date: string; company?: string; status?: string },
    { marked: number; failures: number; skipped_by_reason: Record<string, number> }>(
      "onesphere.api.attendance.mark_attendance", qc));
}

/** Openings with no request behind them (OS-0632). Filing one is the explicit click below. */
export function useRequisitionCandidates(company?: string) {
  return useQuery({
    queryKey: ["requisition_candidates", company ?? ""],
    queryFn: async () => RequisitionCandidatesSchema.parse(
      await frappe.call("onesphere.api.recruitment.requisition_candidates", company ? { company } : {})),
  });
}

export function useRaiseRequisition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { opening: string; requested_by?: string; expected_by?: string }) =>
      frappe.post<{ requisition: string; opening: string; no_of_positions: number;
        expected_compensation: number; compensation_basis: string }>(
        "onesphere.api.recruitment.raise_requisition", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requisition_candidates"] });
      void qc.invalidateQueries({ queryKey: ["hiring_board"] });
    },
  });
}

/** The wage floor (OS-0505). Indicative figures — the answer carries the arithmetic behind them. */
export function useSmig(company?: string) {
  return useQuery({
    queryKey: ["smig", company ?? ""],
    queryFn: async () => SmigSchema.parse(
      await frappe.call("onesphere.api.payroll.smig", company ? { company } : {})),
  });
}

/** One ERP document register, read-only, inside OneSphere's own finance screens.
 *
 * This is what replaced the ERPNext desk in the application (2026-09-28). Six curated registers, not a
 * passthrough that could list any doctype — a screen that can list anything is a second ERP desk in OneSphere's
 * colours, which is the thing the rule exists to prevent. */
export function useFinanceDocuments(kind: string, search?: string, company?: string) {
  return useQuery({
    queryKey: ["finance_documents", kind, search ?? "", company ?? ""],
    queryFn: async () => FinanceDocumentsSchema.parse(await frappe.call(
      "onesphere.api.finance.documents", { kind, ...(search ? { search } : {}), ...(company ? { company } : {}) })),
  });
}

// ---------------------------------------------------------------- leave policy and accrual (HRX-04)

const LeaveTypeSchema = z.object({
  name: z.string(), max_leaves_allowed: num0, is_earned_leave: z.number(),
  earned_leave_frequency: nullableStr, allocate_on_day: nullableStr, rounding: nullableStr,
  is_carry_forward: z.number(), maximum_carry_forwarded_leaves: num0,
  expire_carry_forwarded_leaves_after_days: num0,
  allow_encashment: z.number(), max_encashable_leaves: num0, non_encashable_leaves: num0,
  earning_component: nullableStr, is_compensatory: z.number(), is_lwp: z.number(),
  include_holiday: z.number(), max_continuous_days_allowed: num0, applicable_after: num0,
});

export const LeaveBoardSchema = z.object({
  as_of: z.string(),
  period: z.object({ name: z.string(), from_date: z.string(), to_date: z.string(), company: nullableStr }).nullable(),
  state: z.array(z.object({ key: z.string(), ok: z.boolean(), facts: z.record(z.string(), z.any()) })),
  types: z.array(LeaveTypeSchema),
  misleading_frequency: z.array(z.string()),
  allocations: z.object({ total: z.number(), people: z.number(), from_policy: z.number(), hand_written: z.number() }),
  policies: z.array(z.object({ name: z.string(), title: z.string() })),
  grades: z.array(z.string()),
  indicative: z.record(z.string(), z.any()),
  permissions: z.object({ configure: z.boolean() }),
});
export type LeaveBoard = z.infer<typeof LeaveBoardSchema>;

export const AccrualSchema = z.object({
  leave_type: z.string(),
  period: z.object({ name: z.string(), from_date: z.string(), to_date: z.string() }).nullable(),
  accrues: z.boolean().optional().default(false),
  rows: z.array(z.object({
    name: z.string(), employee_name: nullableStr, company: nullableStr, grade: nullableStr,
    designation: nullableStr, date_of_joining: nullableStr,
    entitlement: z.object({ base: num0, seniority: num0, annual: num0, grade: nullableStr, graded: z.boolean() }),
    allocated: num0, earned: num0, gap: num0, verdict: z.string(),
  })),
  summary: z.object({
    people: z.number(), over_allocated: z.number(), under_allocated: z.number(), level: z.number(),
    unearned_days: num0, allocated_days: num0, earned_days: num0,
  }),
  indicative: z.record(z.string(), z.any()).optional(),
});

export const CompOffSchema = z.object({
  rows: z.array(z.object({
    employee: z.string(), employee_name: nullableStr, company: nullableStr,
    attendance_date: z.string(), working_hours: num0, shift: nullableStr,
    days: num0, eligible: z.boolean(), claimed: z.boolean(),
  })),
  holidays: z.number(), from: z.string(), to: z.string().optional(),
  unclaimed: z.number().optional().default(0), days_due: num0.optional().default(0),
});

export function useLeaveBoard(company?: string) {
  return useQuery({
    queryKey: ["leave_board", company ?? ""],
    queryFn: async () => LeaveBoardSchema.parse(
      await frappe.call("onesphere.api.leave.board", company ? { company } : {})),
  });
}

export function useLeaveAccrual(company?: string, leaveType?: string) {
  return useQuery({
    queryKey: ["leave_accrual", company ?? "", leaveType ?? ""],
    queryFn: async () => AccrualSchema.parse(
      await frappe.call("onesphere.api.leave.accrual",
        { ...(company ? { company } : {}), ...(leaveType ? { leave_type: leaveType } : {}) })),
  });
}

export function useCompOff(company?: string, days = 60) {
  return useQuery({
    queryKey: ["comp_off", company ?? "", days],
    queryFn: async () => CompOffSchema.parse(
      await frappe.call("onesphere.api.leave.comp_off_candidates",
        { ...(company ? { company } : {}), days })),
  });
}

/** The configuration actions. Each is a deliberate act by HR, never a side effect of opening a screen. */
export function useLeaveConfigure() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["leave_board"] });
    void qc.invalidateQueries({ queryKey: ["leave_accrual"] });
    void qc.invalidateQueries({ queryKey: ["comp_off"] });
  };
  return useMutation({
    mutationFn: async (input: { action: string }) =>
      frappe.post<Record<string, unknown>>(`onesphere.api.leave.${input.action}`, {}),
    onSuccess: invalidate,
  });
}

export function useRaiseCompOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employee: string; work_date: string; half_day?: number }) =>
      frappe.post<{ request: string }>("onesphere.api.leave.raise_comp_off", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["comp_off"] });
      void qc.invalidateQueries({ queryKey: ["leave_board"] });
    },
  });
}

// ---------------------------------------------------------------- project labour costing (HRX-03)

const RollupRowSchema = z.object({
  key: z.string(), label: z.string(), hours: num0, cost: num0, billable_hours: num0, lines: z.number(), rate: num0,
});

const BillingSchema = z.object({
  billable_hours: num0, billable_amount: num0, billable_cost: num0, written_off_hours: num0,
  internal_hours: num0, internal_cost: num0, margin: num0, margin_pct: z.number().nullable(),
  customers: z.number().optional(),
});

const ApprovalSummarySchema = z.object({
  timesheets: z.number(), pending: z.number(), pending_hours: num0, pending_cost: num0,
  approved: z.number(), returned: z.number(), no_approver: z.number(), no_approver_cost: num0,
});

export const LabourBoardSchema = z.object({
  as_of: z.string(),
  companies: z.array(z.string()).optional().default([]),
  totals: z.object({ hours: num0, cost: num0, lines: z.number(), average_rate: num0, unrated_lines: z.number(), unrated_hours: num0 }),
  billing: BillingSchema,
  approval: ApprovalSummarySchema,
  contractor: z.object({ hours: num0, cost: num0, lines: z.number() }),
  by_project: z.array(RollupRowSchema),
  by_activity: z.array(RollupRowSchema),
  by_person: z.array(RollupRowSchema),
  permissions: z.object({ decide: z.boolean(), assign: z.boolean() }),
});
export type LabourBoard = z.infer<typeof LabourBoardSchema>;

const QueueRowSchema = z.object({
  name: z.string(), employee: nullableStr, employee_name: nullableStr, company: nullableStr,
  designation: nullableStr, created_by: nullableStr, project: nullableStr,
  start_date: nullableStr, end_date: nullableStr, hours: num0, cost: num0,
  assigned_approver: nullableStr, subject_user: nullableStr, state: z.string(),
  refusal: z.string().nullable().optional(),
  decision: z.object({ decision: z.string(), decided_by: nullableStr, decided_on: nullableStr, note: nullableStr }).nullable().optional(),
});
export type LabourQueueRow = z.infer<typeof QueueRowSchema>;

export const LabourQueueSchema = z.object({
  rows: z.array(QueueRowSchema), decided: z.array(QueueRowSchema),
  summary: ApprovalSummarySchema, is_hr: z.boolean(),
});

export const ReconciliationSchema = z.object({
  from: z.string(), to: z.string(), days_with_attendance: z.number().optional().default(0),
  on_site_but_never_book: z.number().optional().default(0),
  rows: z.array(z.object({
    employee: z.string(), employee_name: nullableStr, company: nullableStr, days_compared: z.number(),
    booked: num0, worked: num0, gap: num0, gap_pct: z.number().nullable(), verdict: z.string(),
  })),
  summary: z.object({ people: z.number(), no_attendance: z.number(), over_booked: z.number(), under_booked: z.number(), matched: z.number() }),
});

export function useLabourBoard(company?: string) {
  return useQuery({
    queryKey: ["labour_board", company ?? ""],
    queryFn: async () => LabourBoardSchema.parse(
      await frappe.call("onesphere.api.labour.board", company ? { company } : {})),
  });
}

export function useLabourQueue(company?: string, mine = 1) {
  return useQuery({
    queryKey: ["labour_queue", company ?? "", mine],
    queryFn: async () => LabourQueueSchema.parse(
      await frappe.call("onesphere.api.labour.queue", { ...(company ? { company } : {}), mine })),
  });
}

export function useLabourReconciliation(company?: string, days = 30) {
  return useQuery({
    queryKey: ["labour_reconciliation", company ?? "", days],
    queryFn: async () => ReconciliationSchema.parse(
      await frappe.call("onesphere.api.labour.booked_against_worked", { ...(company ? { company } : {}), days })),
  });
}

/** Accepting or returning somebody's booked hours. The four-eyes guard lives on the server. */
export function useDecideTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { timesheet: string; decision: string; note?: string }) =>
      frappe.post<{ approval: string; timesheet: string; decision: string; hours: number; cost: number }>(
        "onesphere.api.labour.decide", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["labour_queue"] });
      void qc.invalidateQueries({ queryKey: ["labour_board"] });
    },
  });
}

// ---------------------------------------------------------------- time capture (HRX-02)

const DayAssessmentSchema = z.object({
  date: z.string(), status: z.string(), hours: num0, scheduled_hours: num0,
  first_in: nullableStr, last_out: nullableStr, on_site: z.boolean(),
  late_entry: z.boolean(), early_exit: z.boolean(), late_by_minutes: z.number(),
  overtime_hours: num0, punches: z.number(),
});

export const MyDaySchema = z.object({
  employee: nullableStr, employee_name: nullableStr.optional(), company: nullableStr.optional(),
  reason: z.string().optional(), date: z.string().optional(),
  shift: z.object({ name: z.string(), start_time: z.string(), end_time: z.string() }).passthrough().nullable().optional(),
  punches: z.array(z.object({ name: z.string(), time: z.string(), log_type: nullableStr })).optional().default([]),
  next: z.string().optional(),
  day: DayAssessmentSchema.nullable().optional(),
  can_punch: z.boolean().optional().default(false),
});
export type MyDay = z.infer<typeof MyDaySchema>;

export const GateSchema = z.object({
  date: z.string(), shifts: z.array(z.string()),
  board: z.object({
    expected: z.number(), on_site: z.number(), clocked_out: z.number(), not_arrived: z.number(),
    late: z.number(), early_leavers: z.number(), overtime: z.number(), hours_today: num0,
  }),
  rows: z.array(DayAssessmentSchema.extend({
    name: z.string(), employee_name: z.string(), company: nullableStr,
    department: nullableStr, designation: nullableStr, shift: z.string(),
  })),
});
export type Gate = z.infer<typeof GateSchema>;

export function useMyDay(enabled = true) {
  return useQuery({
    queryKey: ["my_day"], enabled,
    queryFn: async () => MyDaySchema.parse(await frappe.call("onesphere.api.attendance.my_day")),
  });
}

export function useGate(company?: string, on?: string) {
  return useQuery({
    queryKey: ["attendance_gate", company ?? "", on ?? ""],
    queryFn: async () => GateSchema.parse(
      await frappe.call("onesphere.api.attendance.gate", { ...(company ? { company } : {}), ...(on ? { on } : {}) })),
  });
}

/** Clocking in or out. Only ever for oneself — the endpoint refuses anything else. */
export function usePunch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { log_type?: string }) =>
      frappe.post<{ punch: string; log_type: string; at: string; next: string }>(
        "onesphere.api.attendance.punch", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["my_day"] });
      void qc.invalidateQueries({ queryKey: ["attendance_gate"] });
    },
  });
}

// ---------------------------------------------------------------- contractor management (CON-01 … CON-08)

export const SiteAccessSchema = z.object({
  gate: z.enum(["Green", "Amber", "Red"]),
  missing: z.array(z.string()), expired: z.array(z.string()), expiring: z.array(z.string()),
  blocking: z.array(z.string()), required: z.array(z.string()), on_file: z.array(z.string()),
});
export const ContractorRowSchema = z.object({
  name: z.string(), contractor_name: z.string(), contractor_type: z.enum(["Firm", "Individual"]),
  trade: nullableStr, company: nullableStr, status: z.string(),
  supplier: nullableStr, employee: nullableStr, parent_contractor: nullableStr,
});
export const ContractorMilestoneSchema = z.object({
  name: z.string(), parent: nullableStr, title: z.string(), due_date: nullableStr, amount: num0,
  status: z.enum(["Pending", "Certified", "Invoiced", "Rejected"]),
  certified_on: nullableStr, certified_by: nullableStr, purchase_invoice: nullableStr, note: nullableStr,
  idx: z.number().optional(),
});
export type ContractorMilestone = z.infer<typeof ContractorMilestoneSchema>;

export const PostingStateSchema = z.object({
  to_timesheet: z.object({ logs: z.number(), days: num0, hours: num0, amount: num0 }),
  to_invoice: z.object({ lines: z.number(), amount: num0 }),
  supplier: nullableStr, project: nullableStr, expense_account: nullableStr,
  posted_timesheets: z.array(z.string()).optional().default([]),
});

export const ContractorEngagementSchema = z.object({
  billing_type: z.string().nullable().optional().transform((v) => v ?? "Time and material"),
  milestones: z.array(ContractorMilestoneSchema).optional().default([]),
  posting: PostingStateSchema.optional(),
  expense_account: nullableStr.optional(), cost_center: nullableStr.optional(),
  name: z.string(), contractor: z.string(), title: z.string(), company: nullableStr, plant: nullableStr, project: nullableStr,
  start_date: nullableStr, end_date: nullableStr, status: z.string(), rate: nullableNum, rate_basis: nullableStr,
  currency: nullableStr, budget_amount: nullableNum, owner_user: nullableStr, headcount: nullableNum,
});
export const ScheduleBalanceSchema = z.object({
  scheduled: num0, price: num0, certified: num0, invoiced: num0, certified_not_invoiced: num0,
  rejected: num0, remaining: num0, progress_pct: z.number().nullable(), reconciles: z.boolean(),
  difference: num0,
});

export const ContractorCostSchema = z.object({
  billing_type: z.string().optional().default("Time and material"),
  schedule: ScheduleBalanceSchema.nullable().optional(),
  approved_quantity: num, approved_amount: num, pending_quantity: num, pending_amount: num,
  budget: nullableNum, utilisation_pct: nullableNum, over_budget: z.boolean(), remaining: nullableNum,
});
export const ContractorOverviewSchema = z.object({
  as_of: z.string(), company: nullableStr,
  permissions: z.object({ can_manage: z.boolean(), can_view: z.boolean() }),
  counts: z.object({ contractors: z.number(), firms: z.number(), individuals: z.number(),
    active_engagements: z.number(), blocked: z.number(), expiring_soon: z.number() }),
  blocked: z.array(ContractorRowSchema.extend({ access: SiteAccessSchema })),
  attention: z.array(ContractorRowSchema.extend({ access: SiteAccessSchema })),
  renewals: z.array(ContractorEngagementSchema.extend({ days_to_end: z.number(), ended: z.boolean(), contractor_name: nullableStr })),
  engagements: z.array(ContractorEngagementSchema),
  contractors: z.array(ContractorRowSchema),
  cost: z.object({ this_month: ContractorCostSchema, all_time: ContractorCostSchema,
    by_contractor: z.array(z.object({ key: z.string(), amount: num, quantity: num, contractor_name: nullableStr })) }),
});
export type ContractorOverview = z.infer<typeof ContractorOverviewSchema>;
export type ContractorRow = z.infer<typeof ContractorRowSchema>;

export const ContractorDocumentSchema = z.object({
  name: z.string(), contractor: z.string(), document_type: z.string(), issued_on: nullableStr, expires_on: nullableStr,
  status: nullableStr, reference_no: nullableStr, issuing_authority: nullableStr, source: z.string().optional().default("contractor"),
});
export const ContractorDetailSchema = z.object({
  contractor: z.record(z.string(), z.unknown()),
  access: SiteAccessSchema,
  documents: z.array(ContractorDocumentSchema),
  engagements: z.array(ContractorEngagementSchema.extend({ scope: nullableStr, contract_ref: nullableStr, purchase_order: nullableStr })),
  cost_by_engagement: z.record(z.string(), ContractorCostSchema),
  milestones_due: z.array(ContractorMilestoneSchema.extend({
    engagement: nullableStr, engagement_title: nullableStr, contractor: nullableStr,
    days_to_due: z.number(), overdue: z.boolean(),
  })).optional().default([]),
  cost: ContractorCostSchema.extend({
    time_booked: num0.optional(),
    by_billing_type: z.record(z.string(), num0).optional(),
  }),
  logs: z.array(z.object({ name: z.string(), engagement: z.string(), contractor: nullableStr, log_date: nullableStr, quantity: num, rate: nullableNum, amount: num, status: z.string(), note: nullableStr, approved_by: nullableStr })),
  qualification: z.array(z.object({ name: z.string(), item_group: nullableStr, status: nullableStr, score: nullableNum, on_time_pct: nullableNum, quality_pct: nullableNum, purchase_orders: nullableNum, valid_until: nullableStr })),
  deliverables: z.array(z.object({ name: z.string(), project: nullableStr, title: nullableStr, due_date: nullableStr, status: nullableStr, received_on: nullableStr, accepted_on: nullableStr })),
  workers: z.array(z.object({ name: z.string(), contractor_name: z.string(), trade: nullableStr, status: z.string() })),
  permissions: z.object({ can_manage: z.boolean(), can_view: z.boolean() }),
});
export type ContractorDetail = z.infer<typeof ContractorDetailSchema>;

export function useContractorOverview(company?: string) {
  return useQuery({ queryKey: ["contractor_overview", company ?? ""], staleTime: 60_000,
    queryFn: async () => ContractorOverviewSchema.parse(await frappe.call("onesphere.api.contractors.overview", company ? { company } : {})) });
}
export function useContractor(name: string | null) {
  return useQuery({ queryKey: ["contractor", name ?? ""], enabled: !!name,
    queryFn: async () => ContractorDetailSchema.parse(await frappe.call("onesphere.api.contractors.contractor", { name })) });
}
/** One mutation for the contractor write actions (book time, approve or reject it); re-reads the board after. */
/** Posting cost lives in its own module, because it writes to the ledger and the project rather than to the
 * contractor record. Routed here so a caller names the action, not the module. */
const COST_ACTIONS = ["post_to_timesheet", "draft_invoice", "prepare_accounts"] as const;
type ContractorAction = "log_time" | "decide_time" | "certify_milestone" | (typeof COST_ACTIONS)[number];

export function useContractorMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: ContractorAction; params: Record<string, unknown> }) =>
      frappe.post<Record<string, unknown>>(
        (COST_ACTIONS as readonly string[]).includes(input.method)
          ? `onesphere.api.contractor_cost.${input.method}`
          : `onesphere.api.contractors.${input.method}`,
        input.params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contractor_overview"] });
      void qc.invalidateQueries({ queryKey: ["contractor"] });
      void qc.invalidateQueries({ queryKey: ["project"] });
    },
  });
}

// ---------------------------------------------------------------- project control (PRJ-03 … PRJ-15, requirement 6.4 … 6.8)

export const ScheduleTaskSchema = z.object({
  name: z.string(), subject: z.string(), planned_start: z.string(), planned_end: z.string(), start: z.string(), end: z.string(), duration_days: num, late_start: z.string(), late_finish: z.string(),
  float_days: num, critical: z.boolean(), near_critical: z.boolean(), is_milestone: z.boolean(), progress: num, status: nullableStr, depends_on: z.array(z.string()), successors: z.array(z.string()),
  shifted_days: num, supplier: nullableStr, owner: nullableStr, kind: nullableStr,
});
export type ScheduleTask = z.infer<typeof ScheduleTaskSchema>;
export const ScheduleSchema = z.object({
  tasks: z.array(ScheduleTaskSchema), project_end: nullableStr, critical_path: z.array(z.string()), near_critical: z.array(z.string()), shifted: z.array(z.string()),
  variance: z.object({ baseline_end: nullableStr, forecast_end: nullableStr, variance_days: num, status: z.string() }),
});
export const DelayChangeSchema = z.object({ name: z.string(), subject: nullableStr, is_milestone: z.boolean().optional(), old_start: nullableStr, old_end: nullableStr, new_start: nullableStr, new_end: nullableStr, shift_days: num, critical_after: z.boolean().optional(), critical_before: z.boolean().optional(), supplier: nullableStr, kind: nullableStr });
export type DelayChange = z.infer<typeof DelayChangeSchema>;
export const DelayOptionSchema = z.object({ option: z.string(), text: z.string(), params: z.record(z.string(), z.unknown()).nullable().optional() });
export const DelayEventSchema = z.object({
  name: z.string(), task: z.string(), task_subject: nullableStr, delay_days: num, category: nullableStr, reason: nullableStr, status: z.string(), forecast_end_before: nullableStr, forecast_end_after: nullableStr,
  forecast_shift_days: num, tasks_shifted: num, milestones_impacted: nullableStr, newly_critical: nullableStr,
  impact: z.object({ changes: z.array(DelayChangeSchema).default([]), after_critical_path: z.array(z.string()).default([]) }).nullable().optional(), options: z.array(DelayOptionSchema).nullable().optional(),
  recorded_by: nullableStr, recorded_on: nullableStr, approved_by: nullableStr, approved_on: nullableStr, applied_on: nullableStr, notified: nullableStr, ai_insight: nullableStr, decision_note: nullableStr,
  brief: z.object({ summary: nullableStr, source: nullableStr, status: nullableStr, model: nullableStr, ai_interaction_id: nullableStr, language: nullableStr }).nullable().optional(),
});
export type DelayEvent = z.infer<typeof DelayEventSchema>;
export const DelaySimulationSchema = z.object({
  task: z.string(), delay_days: num, changes: z.array(DelayChangeSchema), milestones_impacted: z.array(DelayChangeSchema), forecast_end_before: nullableStr, forecast_end_after: nullableStr, forecast_shift_days: num,
  newly_critical: z.array(z.string()), newly_near_critical: z.array(z.string()), options: z.array(DelayOptionSchema), after: z.object({ critical_path: z.array(z.string()) }).passthrough(),
});
export type DelaySimulation = z.infer<typeof DelaySimulationSchema>;
export const ReadinessSchema = z.object({ ready: z.boolean(), checks: z.array(z.object({ key: z.string(), ok: z.boolean(), detail: z.string() })), failed: z.array(z.string()), retention_amount: num, payable_amount: num, recommendation: z.string() });
export const CommercialMilestoneSchema = z.object({
  name: z.string(), supplier: z.string(), purchase_order: nullableStr, title: z.string(), task: nullableStr, task_subject: nullableStr, task_progress: nnum, amount: num, pct_of_contract: nnum, due_date: nullableStr,
  completion_required_pct: num, evidence_required: num, retention_pct: num, status: z.string(), purchase_invoice: nullableStr, claim_amount: nnum, approved_by: nullableStr, approved_on: nullableStr, note: nullableStr,
  invoice: z.object({ name: z.string(), posting_date: nullableStr, grand_total: num, net_total: num, outstanding_amount: num, status: nullableStr }).passthrough().nullable(), readiness: ReadinessSchema.nullable(), evidence_count: num,
});
export type CommercialMilestone = z.infer<typeof CommercialMilestoneSchema>;
export const DocVersionSchema = z.object({ version: z.string(), status: z.string(), approved_by: nullableStr, approved_on: nullableStr, effective_date: nullableStr, supersedes: nullableStr, change_note: nullableStr, file: nullableStr, idx: num });
export const ControlledDocumentSchema = z.object({
  name: z.string(), document_number: z.string(), title: z.string(), discipline: nullableStr, doc_type: nullableStr, current_version: nullableStr, status: z.string(), latest_approved_version: nullableStr, latest_approved_on: nullableStr,
  site_visibility: num, owner_user: nullableStr, supplier: nullableStr, versions: z.array(DocVersionSchema),
  compliance: z.object({ compliant: z.boolean(), issue: nullableStr, latest_approved_version: nullableStr, pending_versions: z.array(z.string()) }).passthrough().nullable().optional(),
});
export type ControlledDocument = z.infer<typeof ControlledDocumentSchema>;
export const EvidenceSchema = z.object({ name: z.string(), title: z.string(), evidence_type: nullableStr, task: nullableStr, commercial_milestone: nullableStr, vendor_deliverable: nullableStr, file: nullableStr, captured_by: nullableStr, captured_on: nullableStr, location: nullableStr, note: nullableStr, verified_by: nullableStr, verified_on: nullableStr });
export type Evidence = z.infer<typeof EvidenceSchema>;
export const ProjectDecisionSchema = z.object({
  name: z.string(), title: z.string(), category: z.string(), status: z.string(), task: nullableStr, supplier: nullableStr, raised_by: nullableStr, raised_on: nullableStr, owner_user: nullableStr, due_date: nullableStr,
  question: nullableStr, decision: nullableStr, decided_by: nullableStr, decided_on: nullableStr, amount: nnum, schedule_impact_days: nnum, evidence: nullableStr, closure_note: nullableStr,
});
export type ProjectDecision = z.infer<typeof ProjectDecisionSchema>;
export const DeliverableSchema = z.object({ name: z.string(), supplier: z.string(), title: z.string(), task: nullableStr, task_subject: nullableStr, purchase_order: nullableStr, contract_ref: nullableStr, due_date: nullableStr, status: z.string(), received_on: nullableStr, accepted_by: nullableStr, accepted_on: nullableStr, evidence: nullableStr, note: nullableStr, days_late: num });
export type Deliverable = z.infer<typeof DeliverableSchema>;
export const CostViewSchema = z.object({ labour: num.optional().default(0), spent: num.optional().default(0),
  budget: num, estimated: num, approved_changes: num, committed: num, invoiced: num, paid: num, eac: num, cost_variance: num, cost_variance_pct: nnum, committed_pct: nnum, invoiced_pct: nnum });
export const ProjectPermissionsSchema = z.object({ can_record_delay: z.boolean(), can_approve_delay: z.boolean(), can_approve_milestone: z.boolean(), can_approve_document: z.boolean(), can_decide: z.boolean(), can_add_evidence: z.boolean() });
export const ProjectDetailSchema = z.object({
  project: z.object({ name: z.string(), project_name: z.string(), company: nullableStr, status: nullableStr, priority: nullableStr, percent_complete: num, expected_start_date: nullableStr, expected_end_date: nullableStr, actual_start_date: nullableStr, estimated_costing: num, total_purchase_cost: num, notes: nullableStr, cost_center: nullableStr }),
  as_of: z.string(), permissions: ProjectPermissionsSchema,
  health: z.object({ expected: nnum, gap: nnum, status: StatusSchema, days_left: nnum }),
  schedule: ScheduleSchema, delays: z.array(DelayEventSchema), deliverables: z.array(DeliverableSchema),
  performance: z.array(z.object({ supplier: z.string(), deliverables: num, on_time: num, late: num, open: num, on_time_pct: nnum, avg_delay_days: num })),
  milestones: z.array(CommercialMilestoneSchema),
  documents: z.object({ documents: z.array(ControlledDocumentSchema), summary: z.object({ total: num, compliant: num, compliance_pct: num, non_compliant: z.array(z.object({ document_number: z.string(), title: nullableStr, issue: nullableStr }).passthrough()) }) }),
  evidence: z.array(EvidenceSchema), decisions: z.array(ProjectDecisionSchema),
  procurement: z.object({
    purchase_orders: z.array(z.object({ name: z.string(), supplier: z.string(), transaction_date: nullableStr, schedule_date: nullableStr, grand_total: num, net_total: num, status: nullableStr, per_received: num, per_billed: num })),
    purchase_invoices: z.array(z.object({ name: z.string(), supplier: z.string(), posting_date: nullableStr, grand_total: num, net_total: num, outstanding_amount: num, status: nullableStr, bill_no: nullableStr, purchase_order: nullableStr })),
    delayed: z.array(z.object({ name: z.string(), supplier: z.string(), schedule_date: nullableStr, days_late: num, per_received: num }).passthrough()), committed: num, invoiced: num, paid: num,
  }),
  cost: CostViewSchema, members: z.array(z.string()),
  summary: z.object({ critical: num, near_critical: num, open_delays: num, late_deliverables: num, open_rfis: num, pending_changes: num, rework: num, claims_not_ready: num, documents_non_compliant: num, procurement_delayed: num }),
});
export type ProjectDetail = z.infer<typeof ProjectDetailSchema>;
export const PortfolioRowSchema = ProjectSchema.omit({ modified: true, total_costing_amount: true, actual_start_date: true }).extend({
  schedule: z.object({ baseline_end: nullableStr, forecast_end: nullableStr, variance_days: num, status: z.string() }), critical_count: num, tasks: num, cost: CostViewSchema, procurement_delayed: num,
  open_delays: num, open_decisions: num, late_deliverables: num, claimed_milestones: num, documents: num,
});
export type PortfolioRow = z.infer<typeof PortfolioRowSchema>;
export const PortfolioSchema = z.object({ projects: z.array(PortfolioRowSchema), count: num, as_of: z.string(), permissions: ProjectPermissionsSchema });

export function usePortfolio(company?: string) {
  return useQuery({ queryKey: ["portfolio", company ?? ""], staleTime: 60_000, queryFn: async () => PortfolioSchema.parse(await frappe.call("onesphere.api.projects.portfolio", company ? { company } : {})) });
}
export function useProjectDetail(name: string | undefined) {
  return useQuery({ queryKey: ["project_detail", name ?? ""], enabled: Boolean(name), queryFn: async () => ProjectDetailSchema.parse(await frappe.call("onesphere.api.projects.project", { name })) });
}
/** One mutation hook for every project decision (delay, milestone, document, RFI, evidence, deliverable): re-reads the project afterwards. */
export function useProjectMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: string; params: Record<string, unknown> }) => frappe.post<Record<string, unknown>>(`onesphere.api.projects.${input.method}`, input.params),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["project_detail"] }); void qc.invalidateQueries({ queryKey: ["portfolio"] }); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
}
export async function simulateDelay(project: string, task: string, delayDays: number) {
  return DelaySimulationSchema.parse(await frappe.call("onesphere.api.projects.simulate_delay", { project, task, delay_days: delayDays }));
}
export const DelayBriefResponseSchema = z.object({ interaction_id: z.string(), source: z.enum(["ai", "rule"]), model: nullableStr, text: z.string(), insight: nullableStr, latency_ms: z.number() });
export type DelayBriefResponse = z.infer<typeof DelayBriefResponseSchema>;
export async function requestDelayBrief(delayEvent: string, language: string) {
  const res = await fetch("/ai/v1/projects/delay_brief", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ delay_event: delayEvent, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return DelayBriefResponseSchema.parse(await res.json());
}


// ---------------------------------------------------------------- executive control (CEO-06, CEO-07, CEO-10, CEO-11, CEO-12, CEO-15)

export const DriverSchema = z.object({
  driver: z.string(), current: nnum, previous: nnum, unit: nullableStr, bad_when: nullableStr, weight: nnum, evidence: nullableStr, change: nnum, change_pct: nnum, adverse: z.boolean(), contribution_pct: num,
}).passthrough();
export type Driver = z.infer<typeof DriverSchema>;
export const ExceptionDetailSchema = z.object({
  exception: z.object({ name: z.string(), kpi: z.string(), kpi_name: nullableStr, unit: nullableStr, domain: nullableStr, company: nullableStr, plant: nullableStr, period: z.string(), severity: z.string(), deviation: nnum, materiality_score: nnum, status: z.string(), owner_user: nullableStr, due_date: nullableStr, insight_source: nullableStr }),
  snapshot: z.object({ value: nnum, target: nnum, budget: nnum, forecast: nnum, prior_period: nnum, prior_year: nnum }).nullable(),
  scope: z.string(), drivers: z.object({ drivers: z.array(DriverSchema), primary: nullableStr, adverse_count: num, favourable: z.array(z.string()), explained: z.boolean() }),
  rule_root_cause: z.object({ fr: z.string(), en: z.string() }), links: z.array(z.object({ label: z.string(), to: z.string() })),
  insight: z.object({ name: z.string(), summary: nullableStr, source: nullableStr, status: nullableStr, model: nullableStr, ai_interaction_id: nullableStr, language: nullableStr, reviewed_by: nullableStr }).nullable(),
  comments: z.array(z.object({ name: z.string(), author: nullableStr, commented_on: nullableStr, visibility: nullableStr, comment: nullableStr })),
  actions: z.array(z.object({ name: z.string(), problem: nullableStr, status: nullableStr, owner_user: nullableStr, due_date: nullableStr, priority: nullableStr, escalation_level: nnum, escalate_to: nullableStr })),
  permissions: z.object({ can_comment: z.boolean(), can_simulate: z.boolean(), can_export: z.boolean(), can_update_actions: z.boolean() }), previous_period: z.string(),
});
export type ExceptionDetail = z.infer<typeof ExceptionDetailSchema>;
export const RisksSchema = z.object({
  as_of: z.string(), permissions: z.object({ can_comment: z.boolean(), can_simulate: z.boolean(), can_export: z.boolean(), can_update_actions: z.boolean() }),
  actions: z.array(z.object({ name: z.string(), problem: nullableStr, status: nullableStr, owner_user: nullableStr, due_date: nullableStr, priority: nullableStr, escalation_level: nnum, escalate_to: nullableStr, source_doctype: nullableStr, source_name: nullableStr, company: nullableStr, plant: nullableStr, overdue_days: num })),
  delays_pending: z.array(z.object({ name: z.string(), project: nullableStr, task_subject: nullableStr, delay_days: num, forecast_shift_days: num, recorded_on: nullableStr })),
  project_risks: z.array(z.object({ name: z.string(), project: nullableStr, title: nullableStr, category: nullableStr, status: nullableStr, due_date: nullableStr, amount: nnum, schedule_impact_days: nnum, overdue_days: num })),
  reconciliation_open: num, close_overdue: z.array(z.object({ name: z.string(), task: nullableStr, period: nullableStr, due_date: nullableStr, owner_user: nullableStr })), label_mismatches_open: num,
  plant_alarms: z.array(z.object({ name: z.string(), plant: nullableStr, parameter_name: nullableStr, alert_kind: nullableStr, recommendation: nullableStr })),
  counts: z.record(z.string(), z.number()),
});
export type Risks = z.infer<typeof RisksSchema>;
export const TrendsSchema = z.object({
  kpi: z.string(), definition: z.object({ kpi_name: nullableStr, unit: nullableStr, direction: nullableStr, domain: nullableStr }).partial(), periods: z.array(z.string()),
  rows: z.array(z.object({ company: z.string(), plant: nullableStr, series: z.array(z.number().nullable()), change: nnum, change_pct: nnum, avg3: nnum, direction: z.string(), vs_avg3_pct: nnum,
    latest: z.object({ value: nnum, target: nnum, budget: nnum, forecast: nnum, prior_period: nnum, prior_year: nnum, status: nullableStr }).nullable() })),
});
export type Trends = z.infer<typeof TrendsSchema>;
export const ScenarioMetricSchema = z.object({ metric: z.string(), baseline: z.number(), simulation: z.number(), delta: z.number(), delta_pct: nnum });
export const ScenarioResultSchema = z.object({
  is_simulation: z.boolean(), variables: z.record(z.string(), z.number()), factors: z.record(z.string(), z.number()), lost_sales_pct: num, capacity_bound: z.boolean(), metrics: z.array(ScenarioMetricSchema),
  lines: z.array(z.object({ name: z.string(), revenue: num, revenue_sim: num, margin: num, margin_sim: num, margin_pct: nnum, margin_pct_sim: nnum })), notes: z.array(z.string()),
});
export type ScenarioResult = z.infer<typeof ScenarioResultSchema>;
export const ScenarioRunSchema = z.object({ name: z.string(), title: z.string(), base_period: z.string(), company: nullableStr, status: nullableStr, run_by: nullableStr, run_on: nullableStr, variables: nullableStr, revenue_delta_pct: nnum, ebitda_delta_pct: nnum, gross_margin_pct_sim: nnum, ebitda_sim: nnum, assumptions: nullableStr });
export type ScenarioRun = z.infer<typeof ScenarioRunSchema>;
export const ScenarioBaselineSchema = z.object({
  period: z.string(), company: nullableStr,
  baseline: z.object({ revenue: num, cogs: num, personnel: num, logistics: num, other_opex: num, depreciation: num, receivables: num, payables: num, inventory: num, import_share: num, packaging_share: num, scheduled_hours: num, downtime_hours: num,
    lines: z.array(z.object({ name: z.string(), revenue: num, cogs: num })), notes: z.array(z.string()).optional() }).passthrough(),
  variables: z.array(z.object({ key: z.string(), label: z.string() })), runs: z.array(ScenarioRunSchema),
});
export type ScenarioBaseline = z.infer<typeof ScenarioBaselineSchema>;
export const SimulateResponseSchema = z.object({ period: z.string(), company: nullableStr, result: ScenarioResultSchema, run: nullableStr, runs: z.array(ScenarioRunSchema).nullable().optional(), model: nullableStr });
export const ExportSchema = z.object({ file_name: z.string(), mime: z.string(), size: num, content_base64: z.string(), period: nullableStr, counts: z.record(z.string(), z.number()) });

export function useExceptionDetail(name: string | null) {
  return useQuery({ queryKey: ["exception_detail", name ?? ""], enabled: Boolean(name), queryFn: async () => ExceptionDetailSchema.parse(await frappe.call("onesphere.api.executive.exception_detail", { name })) });
}
export function useRisks(company?: string) {
  return useQuery({ queryKey: ["exec_risks", company ?? ""], staleTime: 60_000, queryFn: async () => RisksSchema.parse(await frappe.call("onesphere.api.executive.risks", company ? { company } : {})) });
}
export function useTrends(kpi: string | null, company?: string) {
  return useQuery({ queryKey: ["exec_trends", kpi ?? "", company ?? ""], enabled: Boolean(kpi), queryFn: async () => TrendsSchema.parse(await frappe.call("onesphere.api.executive.trends", { kpi, periods: 6, ...(company ? { company } : {}) })) });
}
export function useScenarioBaseline(period: string | undefined, company: string | undefined, enabled: boolean) {
  return useQuery({ queryKey: ["scenario_baseline", period ?? "", company ?? ""], enabled, queryFn: async () => ScenarioBaselineSchema.parse(await frappe.call("onesphere.api.executive.scenario_baseline", { ...(period ? { period } : {}), ...(company ? { company } : {}) })) });
}
/** One mutation hook for the executive decisions (comment, action, root-cause review): re-reads the affected queries afterwards. */
export function useExecMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { method: string; params: Record<string, unknown> }) => frappe.post<Record<string, unknown>>(`onesphere.api.executive.${input.method}`, input.params),
    onSuccess: () => { for (const k of ["exception_detail", "exec_risks", "exceptions", "scenario_baseline"]) void qc.invalidateQueries({ queryKey: [k] }); },
  });
}
export async function runSimulation(period: string | undefined, company: string | undefined, variables: Record<string, number>, save: boolean, title?: string, assumptions?: string, model?: string) {
  return SimulateResponseSchema.parse(await frappe.post("onesphere.api.executive.simulate", { period: period ?? null, company: company ?? null, variables: JSON.stringify(variables), save: save ? 1 : 0, model: model ?? null, title: title ?? null, assumptions: assumptions ?? null }));
}
export async function exportPack(period: string | undefined, company: string | undefined, fmt: "xlsx" | "pdf" | "pptx") {
  return ExportSchema.parse(await frappe.call("onesphere.api.executive.export_pack", { ...(period ? { period } : {}), ...(company ? { company } : {}), fmt }));
}
export const RootCauseResponseSchema = z.object({ interaction_id: z.string(), source: z.enum(["ai", "rule"]), model: nullableStr, text: z.string(), insight: nullableStr, latency_ms: z.number() });
export type RootCauseResponse = z.infer<typeof RootCauseResponseSchema>;
export async function requestRootCause(exception: string, language: string) {
  const res = await fetch("/ai/v1/executive/root_cause", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ exception, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return RootCauseResponseSchema.parse(await res.json());
}

// ---------------------------------------------------------------- demo metrics (requirement 9.10)
export const DemoMetricsSchema = z.object({
  as_of: z.string(), since: nullableStr,
  time_saved: z.object({ total_hours: z.number(), total_steps: z.number(), rows: z.array(z.object({ step: z.string(), track: z.string(), count: z.number(), minutes: z.number(), hours: z.number() })) }),
  exceptions: z.object({ total: z.number(), open: z.number(), rows: z.array(z.object({ kind: z.string(), track: z.string(), total: z.number(), open: z.number() })) }),
  decision_cycle: z.object({ n: z.number(), median_hours: nullableNum, baseline_hours: z.number(), reduction_pct: nullableNum }),
  assumptions: z.object({ step_minutes: z.record(z.string(), z.number()), manual_decision_hours: z.number() }),
});
export type DemoMetrics = z.infer<typeof DemoMetricsSchema>;
export function useDemoMetrics() {
  return useQuery({ queryKey: ["demo-metrics"], queryFn: async () => DemoMetricsSchema.parse(await frappe.call("onesphere.api.demo.metrics")), staleTime: 60_000 });
}

export const AiMetricsSchema = z.object({
  total: z.number(), rated: z.number(), by_feedback: z.record(z.string(), z.number()),
  by_kind: z.array(z.object({ kind: z.string(), total: z.number(), rated: z.number(), useful: z.number(), not_useful: z.number(), correct: z.number(), needs_review: z.number() })),
  persisted: z.boolean(), scope: z.string(),
});
export type AiMetrics = z.infer<typeof AiMetricsSchema>;
export function useAiMetrics() {
  return useQuery({
    queryKey: ["ai-metrics"], staleTime: 60_000,
    queryFn: async () => {
      const res = await fetch("/ai/v1/assistant/metrics", { headers: await aiHeaders() });
      if (!res.ok) throw new Error(`AI API ${res.status}`);
      return AiMetricsSchema.parse(await res.json());
    },
  });
}

// ---------------------------------------------------------------- reusable scenario models (CEO-11, OS-0204)
export const ScenarioModelSchema = z.object({
  name: z.string(), title: z.string(), description: nullableStr, company: nullableStr, variables: z.record(z.string(), z.number()).default({}), assumptions: nullableStr, tags: nullableStr,
  owner_user: nullableStr, runs_count: nullableNum, last_run: nullableStr,
});
export type ScenarioModel = z.infer<typeof ScenarioModelSchema>;
export function useScenarioModels(company: string | undefined, enabled: boolean) {
  return useQuery({ queryKey: ["scenario_models", company ?? ""], enabled, queryFn: async () => z.object({ models: z.array(ScenarioModelSchema) }).parse(await frappe.call("onesphere.api.executive.scenario_models", company ? { company } : {})) });
}
export async function saveScenarioModel(input: { title: string; variables: Record<string, number>; description?: string; company?: string; assumptions?: string; tags?: string; name?: string }) {
  return z.object({ name: z.string(), title: z.string(), models: z.array(ScenarioModelSchema) }).parse(await frappe.post("onesphere.api.executive.save_scenario_model", {
    title: input.title, variables: JSON.stringify(input.variables), description: input.description ?? null, company: input.company ?? null, assumptions: input.assumptions ?? null, tags: input.tags ?? null, name: input.name ?? null,
  }));
}

// ---------------------------------------------------------------- reports in plain language (Client AI-4)
export const ReportSpecSchema = z.object({
  title: nullableStr, doctype: z.string(), fields: z.array(z.string()).default([]), filters: z.array(z.tuple([z.string(), z.string(), z.unknown()])).default([]),
  group_by: nullableStr, bucket: nullableStr, date_field: nullableStr, measure: z.object({ agg: z.enum(["count", "sum", "avg", "min", "max"]), field: nullableStr }).nullable().optional(),
  order_by: z.object({ field: z.string(), dir: z.string() }).nullable().optional(), limit: z.number().default(20), chart: z.enum(["table", "bar", "line"]).default("table"), period: nullableStr,
});
export type ReportSpec = z.infer<typeof ReportSpecSchema>;
const ReportDraftBaseSchema = z.object({ spec: ReportSpecSchema.nullable().optional(), explanation: nullableStr, errors: z.array(z.string()).default([]), notes: z.array(z.string()).default([]), error: nullableStr, candidates: z.array(z.string()).optional() });
export type ReportDraft = { interaction_id: string | null; source: "rule" | "ai" | "human"; model: string | null; spec: ReportSpec | null; explanation: string | null; errors: string[]; notes: string[]; error: string | null; candidates?: string[] };
export async function draftReportRule(instruction: string, language: string): Promise<ReportDraft> {
  const d = ReportDraftBaseSchema.extend({ ok: z.boolean(), source: z.string() }).parse(await frappe.call("onesphere.api.reports.draft", { instruction, language: language.slice(0, 2) }));
  return { interaction_id: null, source: "rule", model: null, spec: d.spec ?? null, explanation: d.explanation || null, errors: d.errors, notes: d.notes, error: d.error || null, candidates: d.candidates };
}
export async function draftReportAi(instruction: string, language: string): Promise<ReportDraft> {
  const res = await fetch("/ai/v1/reports/draft", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ instruction, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  const d = ReportDraftBaseSchema.extend({ interaction_id: z.string(), source: z.enum(["ai", "rule"]), model: nullableStr, latency_ms: z.number() }).parse(await res.json());
  return { interaction_id: d.interaction_id, source: d.source, model: d.model || null, spec: d.spec ?? null, explanation: d.explanation || null, errors: d.errors, notes: d.notes, error: d.error || null };
}
export const ReportRunSchema = z.object({
  spec: ReportSpecSchema, explanation: nullableStr, errors: z.array(z.string()).default([]), labels: z.record(z.string(), z.string()).default({}), as_of: z.string(), name: z.string().optional(),
  result: z.object({ columns: z.array(z.string()), rows: z.array(z.record(z.string(), z.unknown())), count: z.number(), total: nullableNum, mode: z.enum(["list", "aggregate"]), groups: z.number().optional(), truncated: z.boolean().optional() }),
});
export type ReportRun = z.infer<typeof ReportRunSchema>;
export async function runReport(spec: ReportSpec, language: string) {
  return ReportRunSchema.parse(await frappe.post("onesphere.api.reports.run", { spec: JSON.stringify(spec), language: language.slice(0, 2) }));
}
export async function saveReport(input: { title: string; spec: ReportSpec; instruction?: string; source: "rule" | "ai" | "human"; language?: string; is_shared?: boolean; model?: string; interaction_id?: string; name?: string }) {
  return z.object({ name: z.string(), title: z.string() }).parse(await frappe.post("onesphere.api.reports.save", {
    title: input.title, spec: JSON.stringify(input.spec), instruction: input.instruction ?? null, source: input.source, language: input.language ?? null, is_shared: input.is_shared ? 1 : 0,
    model: input.model ?? null, interaction_id: input.interaction_id ?? null, name: input.name ?? null,
  }));
}
export const SavedReportSchema = z.object({
  name: z.string(), title: z.string(), doctype_name: z.string(), instruction: nullableStr, spec: ReportSpecSchema, source: z.string(), language: nullableStr, owner_user: nullableStr,
  is_shared: z.number(), pinned: z.number(), run_count: nullableNum, last_run: nullableStr, modified: z.string(), mine: z.boolean(),
});
export type SavedReport = z.infer<typeof SavedReportSchema>;
export function useSavedReports() {
  return useQuery({ queryKey: ["saved_reports"], queryFn: async () => z.object({ count: z.number(), items: z.array(SavedReportSchema) }).parse(await frappe.call("onesphere.api.reports.saved")) });
}
export async function runSavedReport(name: string, language: string) {
  return ReportRunSchema.parse(await frappe.post("onesphere.api.reports.run_saved", { name, language: language.slice(0, 2) }));
}
export async function updateSavedReport(name: string, patch: { pinned?: boolean; is_shared?: boolean; delete?: boolean }) {
  return frappe.post("onesphere.api.reports.update_saved", { name, pinned: patch.pinned == null ? null : patch.pinned ? 1 : 0, is_shared: patch.is_shared == null ? null : patch.is_shared ? 1 : 0, delete: patch.delete ? 1 : 0 });
}

// ---------------------------------------------------------------- process optimisation (Client AI-5)
export const ProcessStageSchema = z.object({ process: z.string(), stage: z.string(), n: z.number(), median: nullableNum, p90: nullableNum, avg: nullableNum, max: nullableNum, target: z.number(), ratio: nullableNum, severity: z.enum(["Red", "Amber", "Green", "Grey"]), open: z.number(), unit: z.string() });
export const ProcessRecommendationSchema = z.object({ kind: z.string(), severity: z.string(), text: z.string(), evidence: z.record(z.string(), z.unknown()).default({}), action: z.object({ problem: z.string(), priority: z.string(), due_in_days: z.number() }).nullable().optional() });
export const ProcessOverviewSchema = z.object({
  as_of: z.string(), company: nullableStr, stages: z.array(ProcessStageSchema),
  automation: z.array(z.object({ key: z.string(), manual: z.number(), automated: z.number(), share_manual_pct: z.number(), minutes: z.number(), hours_potential: z.number(), text_en: z.string(), text_fr: z.string() })),
  rework: z.array(z.object({ key: z.string(), total: z.number(), rework: z.number(), rate_pct: z.number(), text_en: z.string(), text_fr: z.string() })),
  activity: z.object({ changes_by_doctype: z.array(z.object({ ref_doctype: nullableStr, c: z.number() })), changes_by_user: z.array(z.object({ user: z.string(), c: z.number() })), comments_by_doctype: z.array(z.object({ reference_doctype: nullableStr, c: z.number() })), window_days: z.number() }),
  recommendations: z.object({ fr: z.array(ProcessRecommendationSchema), en: z.array(ProcessRecommendationSchema) }), rule_summary: z.object({ fr: z.string(), en: z.string() }),
  counts: z.object({ stages: z.number(), red: z.number(), amber: z.number(), hours_potential: z.number(), rework_items: z.number() }),
});
export type ProcessOverview = z.infer<typeof ProcessOverviewSchema>;
export function useProcessOverview(company: string | undefined) {
  return useQuery({ queryKey: ["process_overview", company ?? ""], staleTime: 60_000, queryFn: async () => ProcessOverviewSchema.parse(await frappe.call("onesphere.api.process.overview", company ? { company } : {})) });
}
export const ProcessAiSchema = z.object({ interaction_id: z.string(), source: z.enum(["ai", "rule"]), model: nullableStr, text: z.string(), insight: nullableStr, latency_ms: z.number() });
export type ProcessAiResponse = z.infer<typeof ProcessAiSchema>;
/** Explain one record in plain language (WP-12.6). Sends the fields the screen already fetched to the Narrator —
 * no new server round trip to gather them — and saves the answer as a Draft Insight attached to that record. */
export const NarrateSchema = z.object({
  interaction_id: z.string(), source: z.string(), model: nullableStr, text: z.string(),
  evidence: z.array(z.object({ kind: z.string(), ref: z.string(), detail: nullableStr })).default([]),
  insight: nullableStr, latency_ms: z.number(),
});
export type NarrateResponse = z.infer<typeof NarrateSchema>;
export async function explainRecord(doctype: string, name: string, facts: Record<string, unknown>, language: string) {
  const res = await fetch("/ai/v1/narrate", {
    method: "POST", headers: await aiHeaders(),
    body: JSON.stringify({
      question: `Explain this ${doctype} record for a manager: what it is, what state it is in, and anything that needs attention.`,
      facts, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr",
      reference_doctype: doctype, reference_name: name, save: true,
    }),
  });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return NarrateSchema.parse(await res.json());
}

export async function requestProcessRecommendation(company: string | undefined, language: string) {
  const res = await fetch("/ai/v1/process/recommend", { method: "POST", headers: await aiHeaders(), body: JSON.stringify({ company: company ?? null, language: language.startsWith("en") ? "en" : language === "ln" ? "ln" : "fr" }) });
  if (!res.ok) throw new Error(`AI API ${res.status}: ${await res.text()}`);
  return ProcessAiSchema.parse(await res.json());
}

// ---------------------------------------------------------------- point of sale pilot (WP-15, OS-0321)
export const PosProfileSchema = z.object({
  name: z.string(), company: z.string(), warehouse: nullableStr, currency: nullableStr, selling_price_list: nullableStr, customer: nullableStr, taxes_and_charges: nullableStr,
  payment_modes: z.array(z.object({ mode: z.string(), default: z.number() })), session: z.object({ name: z.string(), user: nullableStr, period_start_date: nullableStr }).nullable(), tickets_today: z.number(),
});
export const PosContextSchema = z.object({ profiles: z.array(PosProfileSchema), today: z.string(), can_sell: z.boolean() });
export function usePosContext() {
  return useQuery({ queryKey: ["pos_context"], queryFn: async () => PosContextSchema.parse(await frappe.call("onesphere.api.pos.context")) });
}
export const PosItemSchema = z.object({ item_code: z.string(), item_name: z.string(), item_group: nullableStr, brand: nullableStr, stock_uom: nullableStr, image: nullableStr, rate: num, stock: num, barcode: nullableStr });
export function usePosItems(profile: string | undefined, search: string) {
  return useQuery({ queryKey: ["pos_items", profile ?? "", search], enabled: !!profile, placeholderData: keepPreviousData,
    queryFn: async () => z.object({ items: z.array(PosItemSchema), currency: nullableStr }).parse(await frappe.call("onesphere.api.pos.items", { profile, search })) });
}
export const PosQuoteSchema = z.object({ net_total: num, total_taxes: num, grand_total: num, rounded_total: num, currency: nullableStr,
  items: z.array(z.object({ item_code: z.string(), item_name: nullableStr, qty: num, rate: num, amount: num })) });
export type PosQuote = z.infer<typeof PosQuoteSchema>;
export async function quotePos(profile: string, lines: { item_code: string; qty: number }[]) {
  return PosQuoteSchema.parse(await frappe.post("onesphere.api.pos.quote", { profile, lines: JSON.stringify(lines) }));
}
export async function openPosSession(profile: string) {
  return frappe.post("onesphere.api.pos.open_session", { profile });
}
export const PosCheckoutSchema = PosQuoteSchema.extend({
  invoice: z.string(), posting_date: z.string(), posting_time: z.string(), customer: nullableStr, mode_of_payment: z.string(), offline: z.boolean().optional().default(false),
  rollup: z.object({ period: z.string(), company: z.string(), revenue_before: nullableNum, revenue_after: nullableNum, revenue_target: nullableNum, revenue_status: nullableStr, snapshot: nullableStr,
    gl_income_lines: z.number(), gl_income_amount: num, mart_today: z.object({ invoices: z.number(), revenue: num, net_revenue: num }).nullable(), kpis_recomputed: z.array(z.string()), lake: z.string() }).nullable(),
});
export type PosCheckout = z.infer<typeof PosCheckoutSchema>;
/** `offlineRef`/`offlineQueuedAt` mark a ticket being replayed from the browser's offline queue (WP-15.2): checkout()
 * is idempotent on `offlineRef`, so calling this again for the same queued ticket (e.g. an 'online' event racing a
 * manual "Sync now") never posts a second invoice. */
export async function checkoutPos(profile: string, lines: { item_code: string; qty: number }[], mode_of_payment: string, offlineRef?: string, offlineQueuedAt?: string) {
  return PosCheckoutSchema.parse(await frappe.post("onesphere.api.pos.checkout", {
    profile, lines: JSON.stringify(lines), mode_of_payment, ...(offlineRef ? { offline_ref: offlineRef, offline_queued_at: offlineQueuedAt ?? "" } : {}),
  }));
}
export const PosRecentSchema = z.object({ tickets: z.array(z.object({ name: z.string(), posting_time: z.string(), customer: nullableStr, grand_total: num, rounded_total: num, total_qty: num, currency: nullableStr, offline: z.boolean().optional().default(false), modes: z.array(z.string()) })),
  total_today: num, session: z.object({ name: z.string() }).nullable() });
export function usePosRecent(profile: string | undefined) {
  return useQuery({ queryKey: ["pos_recent", profile ?? ""], enabled: !!profile, queryFn: async () => PosRecentSchema.parse(await frappe.call("onesphere.api.pos.recent", { profile })) });
}

// ---------------------------------------------------------------- employee and contractor editors (WP-15 write screens)
//
// The endpoints re-run every rule in `engines/employment.py` and refuse the save if one blocks, so the payloads
// below are what the form *proposes*; `lib/employment.ts` mirrors the rules only to tell the person which field is
// wrong before they submit.

export type EmploymentProblem = { field: string; code: string; severity: "error" | "warning"; params?: Record<string, unknown> };

const NamedRow = z.object({ name: z.string() }).passthrough();
export const EmployeeFormOptionsSchema = z.object({
  companies: z.array(NamedRow),
  departments: z.array(z.object({ name: z.string(), company: nullableStr })),
  designations: z.array(z.string()),
  grades: z.array(z.string()),
  branches: z.array(z.string()),
  holiday_lists: z.array(z.string()),
  shift_types: z.array(z.string()),
  genders: z.array(z.string()),
  salutations: z.array(z.string()),
  employment_types: z.array(z.object({ name: z.string(), group: z.string() })),
  statuses: z.array(z.string()),
  contractor_types: z.array(z.string()),
  contractor_statuses: z.array(z.string()),
  known_groups: z.array(z.string()),
});
export type EmployeeFormOptions = z.infer<typeof EmployeeFormOptionsSchema>;

export function useEmployeeFormOptions(company?: string, enabled = true) {
  return useQuery({
    queryKey: ["employee_form_options", company ?? ""],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: async () => EmployeeFormOptionsSchema.parse(await frappe.call("onesphere.api.employee_admin.form_options", { company })),
  });
}

const ProblemSchema = z.object({ field: z.string(), code: z.string(), severity: z.enum(["error", "warning"]), params: z.record(z.string(), z.unknown()).optional() });
export const EditableEmployeeSchema = z.object({
  name: z.string(), employee_name: nullableStr, user_id: nullableStr,
  values: z.record(z.string(), z.unknown()),
  group: z.string(), manager_name: nullableStr,
  problems: z.array(ProblemSchema),
});
export type EditableEmployee = z.infer<typeof EditableEmployeeSchema>;

export function useEditableEmployee(name: string | undefined) {
  return useQuery({
    queryKey: ["editable_employee", name ?? ""],
    enabled: !!name,
    queryFn: async () => EditableEmployeeSchema.parse(await frappe.call("onesphere.api.employee_admin.employee", { name })),
  });
}

/** Everything the People screens read about a person, so a save shows up wherever they appear. */
const PEOPLE_KEYS = [...HR_SCREEN_KEYS, "people_overview", "employee_file", "assignments_overview", "person_timeline",
  "editable_employee", "employee_form_options"] as const;

function invalidatePeople(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["list", "Employee"] });
  void qc.invalidateQueries({ queryKey: ["doc", "Employee"] });
  for (const key of PEOPLE_KEYS) void qc.invalidateQueries({ queryKey: [key] });
}

export function useSaveEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name?: string; values: Record<string, unknown> }) =>
      frappe.post<{ name: string; employee_name: string | null; created: boolean; group: string; warnings: EmploymentProblem[] }>(
        "onesphere.api.employee_admin.save_employee", { name: input.name, values: JSON.stringify(input.values) },
      ),
    onSuccess: () => invalidatePeople(qc),
  });
}

export function useEndEmployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; relieving_date: string; reason_for_leaving: string; status?: string; new_workplace?: string }) =>
      frappe.post<{ name: string; status: string; relieving_date: string; warnings: EmploymentProblem[]; orphaned_reports: string[] }>(
        "onesphere.api.employee_admin.end_employment", { ...input },
      ),
    onSuccess: () => invalidatePeople(qc),
  });
}

export const EditableContractorSchema = z.object({
  name: z.string(), values: z.record(z.string(), z.unknown()), problems: z.array(ProblemSchema),
});
export function useEditableContractor(name: string | undefined) {
  return useQuery({
    queryKey: ["editable_contractor", name ?? ""],
    enabled: !!name,
    queryFn: async () => EditableContractorSchema.parse(await frappe.call("onesphere.api.employee_admin.contractor", { name })),
  });
}

export function useSaveContractor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name?: string; values: Record<string, unknown> }) =>
      frappe.post<{ name: string; contractor_name: string; created: boolean; warnings: EmploymentProblem[] }>(
        "onesphere.api.employee_admin.save_contractor", { name: input.name, values: JSON.stringify(input.values) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["editable_contractor"] });
      for (const key of ["contractor_overview", "contractor", "assignments_overview", "person_timeline"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}


// ---------------------------------------------------------------------------------------------------------------
// The lifecycle steps closed on 2026-09-14 (docs/OneSphere-Employee-Lifecycle.md Part A): a resourcing request and
// its approval; interviews with a panel and each interviewer's feedback; the offer, its answer and the hire; a
// document filed from the app; a shift assigned from a date; a person's own weekly hours; the appraisal entered;
// a promotion recorded. One generic mutation for the writes; typed queries for the reads.

export function useLifecycleMutation<TInput extends Record<string, unknown>>(method: string, invalidate: string[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => frappe.post<Record<string, unknown>>(method, input),
    onSuccess: () => { for (const k of invalidate) void qc.invalidateQueries({ queryKey: [k] }); },
  });
}

// --- resourcing requests
export const RequisitionApprovalSchema = z.object({ level: z.string(), decision: z.string(), decided_by: nullableStr, decided_on: nullableStr, note: nullableStr });
export const RequisitionSchema = z.object({
  name: z.string(), designation: nullableStr, department: nullableStr, company: nullableStr, no_of_positions: z.number(),
  expected_compensation: z.number(), monthly_cost: z.number(), status: z.string(), requested_by: nullableStr, requested_by_name: nullableStr,
  posting_date: nullableStr, expected_by: nullableStr, reason: nullableStr, levels: z.array(z.string()), next_level: z.string().nullable(),
  approvals: z.array(RequisitionApprovalSchema), job_opening: nullableStr,
});
export type Requisition = z.infer<typeof RequisitionSchema>;
export const RequisitionOptionsSchema = z.object({
  can_request: z.boolean(), me: z.object({ name: z.string(), employee_name: nullableStr, company: nullableStr, department: nullableStr, designation: nullableStr }).nullable(),
  designations: z.array(z.string()), departments: z.array(z.object({ name: z.string(), department_name: nullableStr, company: nullableStr })),
  companies: z.array(z.string()), grades: z.array(z.object({ name: z.string(), default_base_pay: z.number().nullable().optional() })),
  rule: z.object({ exec_positions: z.number(), exec_monthly_cdf: z.number() }),
});
export const RequisitionQueueSchema = z.object({ as_of: z.string(), is_hr: z.boolean(), to_decide: z.array(RequisitionSchema), all_pending: z.array(RequisitionSchema), mine: z.array(RequisitionSchema) });
export const RequisitionListSchema = z.object({ as_of: z.string(), rows: z.array(RequisitionSchema), counts: z.record(z.string(), z.number()), approved_open: z.array(RequisitionSchema) });
export function useRequisitionOptions(company?: string) {
  return useQuery({ queryKey: ["req_options", company ?? ""], queryFn: async () => RequisitionOptionsSchema.parse(await frappe.call("onesphere.api.requisition.options", company ? { company } : {})) });
}
export function useRequisitionQueue(company?: string) {
  return useQuery({ queryKey: ["req_queue", company ?? ""], queryFn: async () => RequisitionQueueSchema.parse(await frappe.call("onesphere.api.requisition.queue", company ? { company } : {})) });
}
export function useRequisitions(company?: string) {
  return useQuery({ queryKey: ["req_list", company ?? ""], queryFn: async () => RequisitionListSchema.parse(await frappe.call("onesphere.api.requisition.requisitions", company ? { company } : {})) });
}
export async function previewRequisitionPath(positions: number, compensation: number) {
  return z.object({ levels: z.array(z.string()) }).parse(await frappe.call("onesphere.api.requisition.preview", { no_of_positions: positions, expected_compensation: compensation }));
}
export const REQUISITION_KEYS = ["req_queue", "req_list", "req_options", "hr_hiring", "hr_lifecycle"];

// --- interviews
export const InterviewFeedbackRowSchema = z.object({ name: z.string(), interviewer: z.string(), interviewer_name: nullableStr, result: nullableStr, average_rating: z.number().nullable(), feedback: nullableStr, skills: z.array(z.object({ skill: z.string(), rating: z.number() })) });
export const InterviewReadingSchema = z.object({
  panel: z.array(z.string()), answered: z.array(z.string()), waiting: z.array(z.string()), complete: z.boolean(), average_rating: z.number().nullable(),
  cleared: z.number(), rejected: z.number(), leaning: z.string(), panel_names: z.record(z.string(), nullableStr), feedback: z.array(InterviewFeedbackRowSchema),
});
export const InterviewSchema = z.object({
  name: z.string(), job_applicant: z.string(), applicant_name: nullableStr, job_opening: nullableStr, designation: nullableStr, status: z.string(),
  scheduled_on: z.string(), from_time: z.string(), to_time: z.string(), skills: z.array(z.string()), reading: InterviewReadingSchema, my_turn: z.boolean().optional(),
});
export type Interview = z.infer<typeof InterviewSchema>;
export const InterviewOptionsSchema = z.object({ interviewers: z.array(z.object({ user: z.string(), name: nullableStr, designation: nullableStr, company: nullableStr })), me: z.string(), is_hr: z.boolean() });
export const CandidateInterviewsSchema = z.object({ interviews: z.array(InterviewSchema), me: z.string(), is_hr: z.boolean() });
export const UpcomingInterviewsSchema = z.object({ as_of: z.string(), rows: z.array(z.object({
  name: z.string(), job_opening: nullableStr, applicant_name: nullableStr, opening_title: nullableStr, company: nullableStr, designation: nullableStr, scheduled_on: z.string(),
  from_time: z.string(), to_time: z.string(), status: z.string(), panel: z.array(nullableStr), answered: z.number(), of: z.number(), mine: z.boolean(), my_turn: z.boolean(), evaluation: nullableStr,
})) });
export function useInterviewOptions() { return useQuery({ queryKey: ["iv_options"], queryFn: async () => InterviewOptionsSchema.parse(await frappe.call("onesphere.api.interview.options")) }); }
export function useCandidateInterviews(evaluation?: string) {
  return useQuery({ queryKey: ["iv_candidate", evaluation ?? ""], enabled: !!evaluation, queryFn: async () => CandidateInterviewsSchema.parse(await frappe.call("onesphere.api.interview.for_candidate", { evaluation })) });
}
export function useUpcomingInterviews(company?: string) {
  return useQuery({ queryKey: ["iv_upcoming", company ?? ""], queryFn: async () => UpcomingInterviewsSchema.parse(await frappe.call("onesphere.api.interview.upcoming", company ? { company } : {})) });
}
export const INTERVIEW_KEYS = ["iv_candidate", "iv_upcoming", "rec_candidate", "rec_shortlist", "rec_openings", "hr_hiring"];

// --- the offer and the hire
export const OfferStateSchema = z.object({
  offer: z.string().nullable(), status: nullableStr, offer_date: nullableStr, designation: nullableStr, company: nullableStr,
  terms: z.array(z.object({ offer_term: z.string(), value: nullableStr })), employee: z.string().nullable(), onboarding: z.string().nullable(),
});
export function useOfferState(job_applicant?: string) {
  return useQuery({ queryKey: ["offer_state", job_applicant ?? ""], enabled: !!job_applicant, queryFn: async () => OfferStateSchema.parse(await frappe.call("onesphere.api.hiring.offer_state", { job_applicant })) });
}
export const OFFER_KEYS = ["offer_state", "rec_candidate", "rec_shortlist", "rec_openings", "hr_hiring", "hr_lifecycle", "employee_list", "req_list"];

// --- documents
export const DocumentTypesSchema = z.object({ types: z.array(z.string()), mandatory: z.array(z.string()), joining: z.array(z.string()) });
export function useDocumentTypes() { return useQuery({ queryKey: ["cmp_types"], queryFn: async () => DocumentTypesSchema.parse(await frappe.call("onesphere.api.compliance.document_types")) }); }
export const COMPLIANCE_KEYS = ["cmp_types", "compliance_overview", "compliance_documents", "hr_lifecycle"];

// --- shifts
export const ShiftsSchema = z.object({
  rows: z.array(z.object({ name: z.string(), shift_type: z.string(), start_date: z.string(), end_date: nullableStr, status: z.string(), shift_location: nullableStr })),
  current: nullableStr, types: z.array(z.object({ name: z.string(), start_time: nullableStr, end_time: nullableStr })),
});
export function useShifts(employee?: string) {
  return useQuery({ queryKey: ["emp_shifts", employee ?? ""], enabled: !!employee, queryFn: async () => ShiftsSchema.parse(await frappe.call("onesphere.api.employee_admin.shifts", { employee })) });
}
export const SHIFT_KEYS = ["emp_shifts", "editable_employee", "attendance_gate", "employee_list"];
export const PROMOTION_KEYS = ["editable_employee", "employee_list", "hr_movements", "person_timeline", "hr_lifecycle"];

// --- my week
export const MyWeekSchema = z.object({
  employee: z.string(), employee_name: nullableStr, company: nullableStr, approver: nullableStr, approver_name: nullableStr,
  week: z.string(), days: z.array(z.string()), timesheet: z.string().nullable(), docstatus: z.number().nullable(), editable: z.boolean(),
  decision: z.object({ decision: nullableStr, note: nullableStr, decided_by: nullableStr }).nullable(),
  rows: z.array(z.object({ project: z.string(), project_name: nullableStr, hours: z.array(z.number()) })),
  projects: z.array(z.object({ name: z.string(), project_name: nullableStr })),
  rule: z.object({ day_max: z.number(), week_max: z.number(), step: z.number() }),
});
export type MyWeek = z.infer<typeof MyWeekSchema>;
export function useMyWeek(start?: string) {
  return useQuery({ queryKey: ["my_week", start ?? ""], queryFn: async () => MyWeekSchema.parse(await frappe.call("onesphere.api.my.my_week", start ? { start } : {})) });
}
export const WEEK_KEYS = ["my_week", "labour_queue", "labour_board", "my_dashboard"];

// --- the appraisal entered
export const AppraisalEntrySchema = z.object({
  appraisal: z.object({ name: z.string(), employee: z.string(), employee_name: nullableStr, cycle: nullableStr, docstatus: z.number(), rate_goals_manually: z.number(),
    total_score: z.number(), self_score: z.number(), avg_feedback_score: z.number(), final_score: z.number(), remarks: nullableStr, reflections: nullableStr }),
  kras: z.array(z.object({ idx: z.number(), kra: z.string(), per_weightage: z.number(), goal_completion: z.number(), goal_score: z.number() })),
  goals: z.array(z.object({ idx: z.number(), kra: z.string(), per_weightage: z.number(), score: z.number(), score_earned: z.number() })),
  self_ratings: z.array(z.object({ idx: z.number(), criteria: z.string(), per_weightage: z.number(), rating: z.number() })),
  feedback: z.array(z.object({ name: z.string(), reviewer: z.string(), reviewer_name: nullableStr, total_score: z.number().nullable(), feedback: nullableStr, added_on: nullableStr, docstatus: z.number() })),
  voice: z.object({ self: z.boolean(), manager: z.boolean(), colleague: z.boolean(), hr: z.boolean(), open: z.boolean() }),
  platform_score: z.number(),
  corrected: z.object({ score: z.number(), used: z.record(z.string(), z.number()), missing: z.array(z.string()), reweighted: z.boolean() }),
  criteria: z.array(z.string()),
});
export type AppraisalEntry = z.infer<typeof AppraisalEntrySchema>;
export function useAppraisalEntry(appraisal?: string) {
  return useQuery({ queryKey: ["apr_entry", appraisal ?? ""], enabled: !!appraisal, queryFn: async () => AppraisalEntrySchema.parse(await frappe.call("onesphere.api.appraisal.entry", { appraisal })) });
}
export const APPRAISAL_KEYS = ["apr_entry", "appraisal_person", "appraisal_cycle", "appraisal_board", "appraisals_reviews"];


// --- master data: created here, read by the add-ons (D-142)
export const MASTER_KEYS = ["master_options", "erp_list", "erp_doc"];

/** Every picker on one master-data form, in one request. Cached: the lists change far more slowly than the form. */
export function useMasterOptions(kind: string, company?: string) {
  return useQuery({
    queryKey: ["master_options", kind, company ?? ""],
    staleTime: 10 * 60_000,
    queryFn: async () => MasterOptionsSchema.parse(await frappe.call("onesphere.api.master.form_options", { kind, company })),
  });
}

/** The option lists are whatever the kind needs; each is a list of names or of `{name, …}` rows. */
export const MasterOptionsSchema = z.record(z.string(), z.unknown());
export const MasterMadeSchema = z.object({
  name: z.string(), label: nullableStr, created: z.boolean(), vehicle: z.string().optional(),
  // a customer's credit limit: set when Finance created it, else a proposal waiting for Finance (OS-0671)
  credit: z.object({ company: z.string(), amount: z.number(), decided: z.boolean() }).nullable().optional(),
  warnings: z.array(z.object({ field: z.string(), code: z.string(), severity: z.string() }).loose()).default([]),
});
export type MasterMade = z.infer<typeof MasterMadeSchema>;

/** Create the record. The endpoint re-runs every rule and refuses the write, so a save that returns has been kept. */
export function useCreateMaster(kind: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { values: Record<string, unknown>; vehicle?: Record<string, unknown> | null }) =>
      MasterMadeSchema.parse(
        await frappe.post(`onesphere.api.master.create_${kind}`, {
          values: JSON.stringify(input.values),
          ...(input.vehicle ? { vehicle: JSON.stringify(input.vehicle) } : {}),
        }),
      ),
    onSuccess: () => { for (const k of MASTER_KEYS) void qc.invalidateQueries({ queryKey: [k] }); },
  });
}
